import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { AttachmentInfo } from '../components/MessageList';
import { clearAuthStorage, getAuthItem, setAuthItem } from '../utils/authStorage';
// Function-level use only (fileHelpers imports this module back; safe at call time).
import { decodeTextBytes } from '../utils/fileHelpers';
import { PAIR_LETTERS, PairLetter } from '../utils/accountPairs';
import { isVideoFile, VIDEO_UPLOAD_TIMEOUT_MS } from '../constants/video';
import type {
  BranchEntryKind,
  BranchSummaryOmittedReason,
  ExecutionState,
  SupplementCapability,
  SupervisionActionKind,
  SupervisionActionState,
  SupervisionAllowedAction,
  SupervisionGrantLevel,
  SupervisionStatus,
  SupervisionWakePolicy,
} from '../constants/supervision';
import type {
  Phase1ReviewMode as ConversationPhase1ReviewMode,
  Phase1ReviewModeSource,
} from './projects';

/** Optional hooks for attachment uploads (P-COND-F4B29EA85EDC ST3). */
export interface UploadOptions {
  /** Upload progress 0-100 (bytes sent / total); only called when the total is known. */
  onUploadProgress?: (percent: number) => void;
  /** Aborting it cancels the upload (axios rejects with ERR_CANCELED). */
  signal?: AbortSignal;
}

/** One selectable review model (P-COND-8CE6F1D21E1D); `key` = "provider/model". */
export interface ReviewModelOption {
  provider: string;
  model: string;
  key: string;
  label: string;
}

/** `GET|PATCH /api/conversations/{id}/review-setting` (P-COND-8CE6F1D21E1D
 *  Contract Pack). `phase1_review_mode` null = 跟随项目; `phase1_review_model`
 *  null = 自动选择 (always null unless mode is B). */
export interface ConversationReviewSetting {
  phase1_review_mode: ConversationPhase1ReviewMode | null;
  phase1_review_model: string | null;
  effective_phase1_review_mode: ConversationPhase1ReviewMode | null;
  effective_phase1_review_mode_source: Phase1ReviewModeSource | null;
  project_phase1_review_mode_effective: ConversationPhase1ReviewMode | null;
  review_setting_viewable: boolean;
  review_setting_editable: boolean;
  review_setting_readonly_reason: 'not_owner' | 'tier' | 'collab' | 'disabled' | 'running' | null;
  review_model_options: ReviewModelOption[];
  phase1_review_model_available: boolean;
  review_model_unavailable_reason: string | null;
}

/** PATCH body: an absent key = unchanged; an explicit null clears it. */
export interface ConversationReviewSettingUpdate {
  phase1_review_mode?: ConversationPhase1ReviewMode | null;
  phase1_review_model?: string | null;
}

const DEFAULT_UPLOAD_TIMEOUT_MS = 120000;

/** Axios config shared by the single and batch upload calls. */
function uploadRequestConfig(files: File[], opts?: UploadOptions) {
  const hasVideo = files.some((f) => isVideoFile(f.name, f.type));
  const onProgress = opts?.onUploadProgress;
  return {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: hasVideo ? VIDEO_UPLOAD_TIMEOUT_MS : DEFAULT_UPLOAD_TIMEOUT_MS,
    signal: opts?.signal,
    onUploadProgress: onProgress
      ? (e: { loaded: number; total?: number }) => {
        if (e.total) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      }
      : undefined,
  };
}

export const SUPPLEMENT_POLL_INTERVAL_MS = 3000;

export const ALLOWED_AGENT_MODELS = [
  "claude-opus-5-5",
  "claude-opus-5",
  "claude-sonnet-5-5",
  "claude-opus-4-8",
  "claude-sonnet-4-6",
  "claude-haiku-5-5",
  "claude-haiku-4-5-20251001",
] as const;
export type AgentModel = typeof ALLOWED_AGENT_MODELS[number];

export interface SupplementDeliveryDTO {
  id: string;
  run_id: string;
  client_request_id: string;
  content: string;
  status: 'queued' | 'sending' | 'accepted' | 'rejected' | 'unknown';
  at: string;
  accepted_at?: string;
  message_id?: string | null;
  reason?: string | null;
  resolved_action?: 'end' | 'continue' | null;
}

export interface SupplementStateDTO {
  protocol?: 'claude_stream';
  pending: { id: string; content: string; at: string } | null;
  accepted: { id: string; at: string } | null;
  state_available?: boolean;
  can_send?: boolean;
  run_id?: string | null;
  exit_confirmed?: boolean;
  can_resolve?: boolean;
  deliveries?: SupplementDeliveryDTO[];
}

/**
 * Authentication credentials.
 */
interface LoginCredentials {
  username: string;
  password: string;
}

/**
 * Authentication response from backend LoginResponse.
 *
 * Phase 0 dual-key transition (P0-T5A): backend emits BOTH `is_platform_admin`
 * (new canonical) and `is_admin` (legacy alias) for one sprint. Frontend reads
 * via `is_platform_admin ?? is_admin` fallback. After backend drops the alias,
 * the legacy field can be removed in the same PR as the fallback.
 */
export interface AuthResponse {
  access_token: string;
  token_type: string;
  user_id: string;
  is_platform_admin: boolean;
  // TODO P2: drop is_admin fallback once backend stops dual-emit
  is_admin?: boolean;
}

/**
 * Public representation of an Organization (Phase 1 T5-A response shape).
 * Mirrors backend `app.api.orgs.OrgResponse`.
 */
/**
 * Self-serve subscription (P-COND-LRT ST4). Mirrors
 * `backend/app/api/v1/routers/self_serve_subscription.py` schemas. The
 * "payment" is a STUB/sandbox — no real charge occurs.
 */
export interface SubscribeIntentRequest {
  /** Purchasable tier: 'trial' | 'standard' | 'enterprise' (NOT 'internal'). */
  tier: string;
  /** 'monthly' | 'quarterly' | 'semiannual' | 'annual'. */
  period: string;
}

export interface SubscribeIntentResponse {
  order_id: string;
  org_id: string;
  tier: string;
  period: string;
  /** Computed STUB charge (normalized-USD Decimal string). */
  amount_usd: string;
  gateway_provider: string;
  session_id: string;
  /** 'pending' at intent time. */
  status: string;
  first_purchase: boolean;
}

export interface SandboxCallbackRequest {
  /** 'success' | 'failed'. */
  status: string;
}

export interface SandboxCallbackResponse {
  order_id: string;
  org_id: string;
  /** Terminal order status: 'paid' (success) | 'failed'. */
  order_status: string;
  /** True only when this callback applied the subscription math. */
  applied: boolean;
  plan_tier?: string | null;
  subscription_period?: string | null;
  /** ISO-8601 +08:00 (TZ:AWARE-UTC). */
  subscription_expires_at?: string | null;
  price_usd?: string | null;
  prorated_differential_usd?: string | null;
  first_purchase?: boolean | null;
}

export interface OrgResponse {
  id: string;
  slug: string;
  display_name: string;
  settings?: Record<string, unknown> | null;
  /** ISO-8601 with +08:00 offset (per timezone-standard rule TZ:AWARE-UTC). */
  created_at?: string | null;
  // Phase B (P-COND-D38665CEC4B4) — token-budget tiers (USD) carried by the
  // admin org detail/PATCH responses. Optional + null = unlimited; older
  // responses omit them entirely.
  monthly_token_budget_usd?: number | string | null;
  daily_token_budget_usd?: number | string | null;
  weekly_token_budget_usd?: number | string | null;
  // Org caps (P-COND-A08B0B61BE73) surfaced to members so OrgProjects can gate
  // the "新建项目" button before the backend 409s. null = unlimited.
  max_projects?: number | null;
  max_members?: number | null;
  // Subscription state (P-COND-LRT) surfaced on the org detail response and
  // consumed by the org 订阅管理 current-plan card. All optional; older
  // responses omit them. `plan_tier` = internal/trial/standard/enterprise;
  // `subscription_period` = monthly/quarterly/semiannual/annual;
  // `subscription_expires_at` = ISO-8601 +08:00 (TZ:AWARE-UTC) or null.
  plan_tier?: string | null;
  subscription_period?: string | null;
  subscription_expires_at?: string | null;
}

/**
 * Blast-radius preview for department removal (P-COND-3F91C4A8D2E5 ST1).
 * Mirrors backend `app.api.orgs.OrgDepartmentImpactResponse`.
 * Read-only — emits no audit row and changes nothing.
 */
export interface OrgDepartmentImpact {
  department: string;
  /** Active org members currently assigned to this department. */
  member_count: number;
  /** Unredeemed invitations carrying this department. */
  invitation_count: number;
  /** Display names of the collab projects that will be tombstoned. */
  project_names: string[];
}

/**
 * Result of removing a department (P-COND-3F91C4A8D2E5 ST1).
 * Mirrors backend `app.api.orgs.OrgDepartmentRemoveResponse`.
 *
 * `departments` is the FRESH sorted union after removal — callers should
 * adopt it directly instead of refetching. The DELETE is idempotent, so a
 * double-click returns 200 with zeroed counters rather than a 404.
 */
export interface OrgDepartmentRemoveResult {
  department: string;
  members_cleared: number;
  invitations_cleared: number;
  projects_updated: number;
  departments: string[];
}

/**
 * One department plus its visibility-pause flag (P-COND-05EF0D660DAA).
 * Mirrors backend `app.api.orgs.OrgDepartmentState`.
 *
 * `paused` mirrors membership in `org.settings["paused_departments"]`.
 * Pausing is a VISIBILITY suspension only: members of a paused department
 * stop resolving access to the org's projects, but nothing is deleted and
 * no `access.yaml` is rewritten, so resuming restores the original paths.
 * An explicit per-project `ProjectMember` grant always prevails.
 */
export interface OrgDepartmentState {
  name: string;
  paused: boolean;
}

/**
 * Full department-registry payload (P-COND-05EF0D660DAA).
 * Mirrors backend `app.api.orgs.OrgDepartmentsResponse`.
 *
 * `department_states` carries the SAME names in the SAME order as
 * `departments` (index/zip safe). A paused department is STILL listed —
 * hiding it would leave an admin no row to click 「恢复」 on.
 */
export interface OrgDepartmentsResult {
  departments: string[];
  department_states: OrgDepartmentState[];
}

/**
 * Coerce an `OrgDepartmentsResult` into a shape the UI can render blindly
 * (P-COND-05EF0D660DAA).
 *
 * The shipped backend always emits `department_states`, so on the happy path
 * this is a pass-through. It exists for the rolling-deploy window in which a
 * new bundle can briefly talk to an old backend: without it, `undefined`
 * states would render as "no departments" and hide every row — including the
 * paused ones an admin needs in order to resume them. Synthesized entries are
 * always `paused: false`; the client NEVER infers pause state itself.
 */
function normalizeOrgDepartmentsResult(
  data: OrgDepartmentsResult
): OrgDepartmentsResult {
  const departments = Array.isArray(data?.departments) ? data.departments : [];
  const states = Array.isArray(data?.department_states)
    ? data.department_states
    : [];
  return {
    departments,
    department_states:
      states.length === departments.length
        ? states
        : departments.map((name) => ({ name, paused: false })),
  };
}

/**
 * Public representation of an OrgMember (Phase 1 T5-A response shape).
 * Mirrors backend `app.api.orgs.OrgMemberResponse`.
 *
 * `role` is one of `"admin"` | `"member"` (backend validates).
 *
 * Phase 2.7 T4-B (P-COND-P2_7-T4B): `department` / `title` /
 * `manager_user_id` added as OPTIONAL fields per Plan Pack Contract
 * Pack §"Cross-Layer Contracts". Today the backend `_serialize_member`
 * helper (`backend/app/api/orgs.py::_serialize_member`) only emits
 * `{org_id, user_id, role, joined_at}` so these fields will be absent
 * from current responses — but the underlying `OrgMember` model
 * already stores them and the PATCH `/metadata` route accepts them.
 * Declaring them optional keeps the TS type forward-compatible for
 * when the backend serializer is extended (no FE churn at that point),
 * and lets the metadata-edit modal track locally-known values.
 */
export interface OrgMemberResponse {
  org_id: string;
  user_id: string;
  role: string;
  joined_at?: string | null;
  /** Optional — backend `OrgMember.department` (P2.6 T4-A field). */
  department?: string | null;
  /** Optional — backend `OrgMember.title` (P2.6 T4-A field). */
  title?: string | null;
  /**
   * Optional — backend `OrgMember.manager_user_id`. UUID4 of another
   * ACTIVE org_member of the SAME org, or `null` to clear. Backend
   * 422s on a non-null manager that is not an active member of this
   * org (see `api/orgs.py::update_org_member_metadata`).
   */
  manager_user_id?: string | null;
  /**
   * Optional — JOIN'd from `users.username` by Phase 3 UI Completion
   * T6c-B lane (a) M1 (`backend/app/api/orgs.py::_serialize_member_full`).
   * Present in responses from GET `/api/v1/orgs/{org_id}/members` (which
   * uses `_serialize_member_full`); ABSENT in legacy callsites that
   * still use `_serialize_member` (POST /members, PATCH /role, PATCH
   * /metadata, transfer-confirm — see M1 changelog 2026-05-28). UI
   * code MUST fall back to `user_id` when `username` is undefined.
   */
  username?: string | null;
  /**
   * Optional — JOIN'd from `users.display_name` by the user-org-identity-ux
   * extension (`backend/app/api/orgs.py::_serialize_member_full`). The
   * member's real name; present only in `_serialize_member_full` responses
   * (GET /members). UI prefers `display_name ?? username ?? user_id`.
   */
  display_name?: string | null;
  /**
   * Optional — JOIN'd from `users.email` by lane (a) M1. Same
   * present/absent semantics as `username` above. Used as a
   * disambiguation hint in the OrgMembers row tooltip.
   */
  email?: string | null;
  /**
   * Optional — JOIN'd from `users.username` via the LEFT OUTER JOIN on
   * `OrgMember.manager_user_id` (lane (a) M1
   * `_serialize_member_full`). Resolves the manager's display name for
   * the "直属上级" column. Falls back to `manager_user_id` (UUID) if
   * the join missed (deactivated manager, etc.).
   */
  manager_username?: string | null;
}

/**
 * Narrow public representation of a user addable to an org — the
 * payload returned by `GET /api/v1/orgs/{org_id}/addable-users` (lane
 * (a) M1; backend `app.api.orgs.AddableUserResponse`).
 *
 * Deliberately narrow: only `user_id` / `username` / `email`. Does NOT
 * leak which other orgs the user belongs to (cross-org information
 * isolation per plan §6 M1 risk register "addable-users 端点跨 org 信息泄露").
 */
export interface AddableUser {
  user_id: string;
  username: string;
  email: string | null;
}

/**
 * Public representation of an `OrgInvitation` row (Phase 3 v1.1 T3a
 * — extension §17.3 / E10). Mirrors backend
 * `app.api.v1.schemas.invitations.InvitationResponse`.
 *
 * `invite_code` is the URL-safe random token the recipient redeems
 * via `POST /api/v1/invitations/{code}/redeem`. `remaining_uses` is
 * the computed `max_uses - used_count` (clamped to 0).
 */
export interface OrgInvitationResponse {
  id: string;
  org_id: string;
  invite_code: string;
  email?: string | null;
  phone?: string | null;
  wecom_userid?: string | null;
  org_role: string;
  max_uses?: number | null;
  used_count: number;
  remaining_uses: number;
  /** ISO-8601 with +08:00 offset; null only if backend mis-serialized. */
  expires_at?: string | null;
  invited_by: string;
  created_at?: string | null;
  /**
   * ST2 (P-COND-LRT, Req 6): subscription months gifted to the org on
   * redemption. null = no grant (self-serve / code-free path).
   */
  grant_subscription_months?: number | null;
}

/**
 * Payload for `PATCH /api/v1/orgs/{org_id}/members/{user_id}/metadata`
 * (Phase 2.6 T4-A G13, surfaced to UI by Phase 2.7 T4-B).
 *
 * Partial-PATCH semantics (backend honors `model_fields_set`):
 *
 *   - Key ABSENT  → leave that field unchanged on the server.
 *   - Key PRESENT with non-null value → set the field to that value.
 *   - Key PRESENT with `null` → CLEAR the field on the server.
 *
 * Callers should `delete patch.field` to express "absent" rather than
 * setting it to `undefined` — `undefined` keys get serialized away by
 * axios so the result is the same, but `delete` is unambiguous.
 */
export interface OrgMemberMetadataPatch {
  department?: string | null;
  title?: string | null;
  manager_user_id?: string | null;
}

/**
 * Response body for `DELETE /api/v1/orgs/{org_id}` (Phase 2.7 T4-A).
 *
 * Contract note (Plan Pack delta): the Plan Pack §"Sub-Task T4-A" sketches
 * a `{org_id, members_deactivated, projects_deactivated, audit_log_id}`
 * shape, but the actual backend route (`backend/app/api/orgs.py
 * ::deactivate_org_endpoint`) returns the `OrgResponse` of the deactivated
 * org via `response_model=OrgResponse` — the cascade row counts live in
 * the `audit_log.details` row emitted by
 * `services/org_lifecycle.deactivate_org` and are NOT in the HTTP body.
 *
 * To stay forward-compatible, this type declares the optional row-count
 * fields as the union of "what the backend returns today" + "what the
 * Plan Pack proposes". Callers MUST read row counts as optional. The
 * frontend toast falls back to a generic success message when counts
 * are absent.
 */
export interface DeactivateOrgResponse extends OrgResponse {
  /** Optional, only present if backend evolves to include row counts. */
  members_deactivated?: number;
  /** Optional, only present if backend evolves to include row counts. */
  projects_deactivated?: number;
  /** Optional, only present if backend evolves to surface audit id. */
  audit_log_id?: string | null;
}

/**
 * Request body for `POST /api/v1/orgs/{org_id}/transfer` (Phase 2.5 T4-A;
 * surfaced to UI by Phase 3 T6c-A `TransferOrgModal`).
 *
 * Mirrors backend `OrgTransferInitiateRequest` in
 * `backend/app/api/orgs.py`: `new_owner_user_id` is required (must
 * resolve to an ACTIVE admin org_member of the same org); `reason`
 * is optional metadata captured in the `org_transfer_initiated`
 * audit row.
 */
export interface OrgTransferInitiateRequest {
  new_owner_user_id: string;
  reason?: string;
}

/**
 * Response body for `POST /api/v1/orgs/{org_id}/transfer`. Mirrors
 * backend `OrgTransferInitiateResponse`: `transfer_token` is a UUID4
 * persisted to Redis under `p2_5:org_transfer_token:{token}` with a
 * 12h TTL; `expires_at` is the absolute expiry (ISO 8601 with +08:00
 * offset per `to_cst_iso`). The new owner must call
 * `POST /api/v1/orgs/{org_id}/transfer/confirm` with this token
 * within 12 hours to complete the transfer.
 */
export interface OrgTransferInitiateResponse {
  transfer_token: string;
  expires_at: string;
}

/**
 * Conversation creation request matching backend ConversationCreate.
 */
interface CreateConversationRequest {
  project_id: string;
  initial_message?: string;
  is_wecom?: boolean;
  is_group?: boolean;
  member_ids?: string[];
  base_branch?: string;
  /** Method A multi-model provider profile name (e.g. "anthropic",
   *  "deepseek"). Omit/empty -> backend resolves the authorized project default. */
  provider_profile?: string;
  /**
   * Per-conversation multimodel routing override (Per-Project Backend Plan
   * Sub-Task 3). Omit a field to inherit from the project default; pass an
   * explicit value to override. Enum constraints mirror the backend:
   *   - architecture_mode ∈ {'claude_cli','conductor_native','codex_cli'}
   *   - backend_kind      ∈ {'claude','native_loop','aux_only'}
   *
   * `backend_model` may carry the scoped server recommendation at creation.
   * The backend revalidates it; later per-message selection remains available.
   */
  architecture_mode?: 'claude_cli' | 'conductor_native' | 'codex_cli';
  backend_kind?: 'claude' | 'native_loop' | 'aux_only';
  backend_model?: string;
  /**
   * Workspace mode binding (SP-U2, P-COND-CXACT-T3-U2). Optional. Only set
   * for projects whose `projects.mode_paths` is configured. Backend
   * (ConversationCreate, P-COND-CXACT-T1-A2) validates the value and 422s on
   * anything outside {'dev','collab'}. Omit to leave the
   * conversation mode-unbound (legacy NULL behaviour).
   */
  mode?: 'dev' | 'collab';
  /**
   * Fork lineage (方案3 clone reuse, P-COND-3AA40FA7D564). When set, the new
   * conversation REUSES the referenced source conversation's per-conversation
   * git clone (HEAD transfers) instead of cloning fresh from the base branch,
   * and the source conversation becomes read-only (irreversible). git-enabled
   * projects only; backend 422s an invalid/foreign/already-readonly source.
   */
  from_conversation_id?: string;
  /**
   * Discussion branch (P-COND-490FE79403B8 ST5, ⋯ → 从这里分支新会话). Send
   * BOTH to cut a new conversation from `branch_from_message_id` of
   * `branch_from_conversation_id`. The server derives base branch / mode /
   * sub-modes / provider from the source and ignores those client fields on
   * this path — callers must not send `base_branch`. 422 when combined with
   * `from_conversation_id`; 403 without access to the source.
   */
  branch_from_conversation_id?: string;
  branch_from_message_id?: string;
  /**
   * Server-side branch context (CS-T9, P-COND-82309B809347 §3.4). Only read
   * with `branch_from_conversation_id`. Omitted = the server derives it
   * (selected text -> 'ask', message -> 'message', else 'sidebar').
   */
  branch_entry_kind?: BranchEntryKind;
  observation?: boolean;
  /** The 划词「发问」 fragment (entry kind 'ask'). */
  branch_selected_text?: string;
  /**
   * Dev sub-mode (P-COND-AB7B8345E64D). Optional. Only meaningful for
   * git-enabled dev-mode projects. 'operation' selects the coordination
   * ("统筹") persona that organizes scattered branches, cleans up, and merges
   * content — it does NOT do feature development. Omit / NULL / 'branch_dev'
   * = today's feature-development behavior. Backend 422s an illegal value,
   * operation+collab, or operation on a non-git project.
   */
  dev_submode?: 'branch_dev' | 'operation';
  /**
   * Collab sub-mode (P-COND-COLLABSUB). Optional. Only meaningful for collab
   * conversations. Selects the orientation block injected into the system prompt,
   * pointing the agent at the most relevant skill cluster for the session.
   * 'planning' = content creation / research / social media;
   * 'design' = visual design / brand / image generation;
   * 'production' = document file generation (PPT/Word/Excel).
   * Omit / null = general mode (default behavior, no orientation block).
   * Backend 422s on non-collab conversations.
   */
  collab_submode?: 'planning' | 'design' | 'production';
}

/**
 * Provider profile entry returned by GET /api/admin/providers.
 * Token values are NEVER returned — only env var name + boolean is_set + source.
 *
 * `source` reflects token resolution order (DB > OS env > missing):
 *   - "db"      → token is stored encrypted in the provider_tokens table
 *   - "env"     → token is set as an OS env var on the orchestrator host
 *   - "missing" → token_env_var is declared but no value found in either source
 *
 * `updated_at` / `updated_by` are populated only when source === "db".
 */
/**
 * Per-tier thinking mode descriptor (Method A multi-provider thinking control).
 * Mirrors backend `ThinkingMode` pydantic model in `backend/app/core/provider_profile.py`.
 *
 * - `name`            internal identifier (e.g. "off", "low", "medium", "high", "reasoner")
 * - `display_name`    UI label (e.g. "关闭", "低 (1024)", "DeepSeek Reasoner")
 * - `budget_tokens`   value for `MAX_THINKING_TOKENS` env var. `0` disables;
 *                     `>0` enables with budget; `null` means do NOT set the env var
 *                     (preserves CLI 4.6+ adaptive default)
 * - `model_override`  if set, overrides `ANTHROPIC_MODEL` env var for the dispatch
 *                     (used by DeepSeek reasoner tier to switch models)
 */
export interface ProviderThinkingMode {
  name: string;
  display_name: string;
  budget_tokens: number | null;
  model_override: string | null;
}

export interface ProviderProfileEntry {
  name: string;
  display_name: string;
  base_url: string;
  default_model: string;
  /** Provider's supported model list (from YAML `models`). Empty list => fallback applies. */
  models?: string[];
  bypass_rotation: boolean;
  env_var_name: string;
  is_set: boolean;
  source: 'env' | 'db' | 'missing';
  updated_at?: string | null;
  updated_by?: string | null;
  /**
   * Available thinking-mode tiers for this profile (from YAML `thinking_modes`).
   * Empty array => thinking is not configurable for this profile (e.g. Kimi).
   */
  thinking_modes: ProviderThinkingMode[];
  /**
   * YAML-declared default tier name (used when no DB override exists).
   * `null` => no env var injection on cold start (CLI adaptive default preserved).
   */
  default_thinking_mode: string | null;
  /**
   * Current effective tier (DB row > YAML default > none).
   * Same string value as one of `thinking_modes[].name`, or null.
   */
  active_thinking_mode: string | null;
  /**
   * Provenance of `active_thinking_mode`, for the UI badge:
   *   - "db"   admin override is set in `provider_thinking_settings`
   *   - "yaml" no DB row; YAML default in effect
   *   - "none" no override and no YAML default — env var NOT injected
   */
  active_thinking_mode_source: 'db' | 'yaml' | 'none';
  /**
   * P-COND-5062A6F6431A Sub-Task 3 — per-`env_var_name` account set.
   *
   * OPTIONAL by contract: a backend that predates the multi-account change
   * omits this field entirely, and the UI must degrade to the historical
   * single-row rendering driven by `source` / `is_set` / `updated_at`.
   * Never treat absence as "zero accounts".
   *
   * Keyed by `env_var_name`, NOT by profile name — `kimi` and
   * `Kimi (Claude CLI)` share `KIMI_AUTH_TOKEN` and therefore share one
   * account set (see planning/provider-multi-account-management-plan.md
   * > Contract Pack v1).
   */
  accounts?: ProviderAccount[];
}

/**
 * 4-state account health (P-COND-D9B909CA2914 Contract Pack).
 *
 * `unknown` is the DEGRADATION state, not an error: it covers both "Redis was
 * unreadable" and "the backend predates the health fields". A consumer must
 * render it, never throw on it, and never let it collapse into `healthy`.
 */
export type ProviderAccountHealthState =
  | 'healthy'
  | 'cooling'
  | 'invalid'
  | 'unknown';

/** Which TTL tier a cooldown came from. There is no third tier. */
export type ProviderAccountCooldownClass = 'rolling' | 'usage_exhausted';

/**
 * One API-key account registered under a provider's `env_var_name`.
 *
 * Key MATERIAL is never present — the backend returns labels + provenance
 * only (`list_provider_accounts` does not decrypt). `updated_at` is a
 * `+08:00` ISO string (TZ:AWARE-UTC contract) and is null for `env` /
 * `missing` rows, which have no DB audit trail.
 *
 * The 7 health fields (P-COND-D9B909CA2914 Sub-Task 2) are folded in by
 * `account_pair_manager.py::get_provider_account_health`. They are ALL
 * optional on purpose: the backend OMITS the whole set when Redis is absent
 * or a read raises (fail-open + fail-quiet — the admin page must not fail to
 * load because a cache blipped), and an older backend never emitted them at
 * all. Absence is the wire encoding of `unknown`.
 *
 * CRITICAL: a missing `health_state` MUST be read as `unknown`, never as
 * `healthy`. Letting a default masquerade as a real verdict would tell an
 * operator "this key is fine" on the strength of no evidence whatsoever
 * (.claude/rules/sentinel-overloading.md, bug-patterns #311). Use
 * `providerAccountHealthState()` below rather than re-spelling the fallback.
 *
 * Note also that `source` (db/env/missing) is credential PROVENANCE — a
 * different axis from health. The UI renders two dots; do not merge them.
 */
export interface ProviderAccount {
  label: string;
  source: 'db' | 'env' | 'missing';
  updated_at: string | null;
  health_state?: ProviderAccountHealthState;
  /** Human-readable basis, e.g. `kimi · HTTP 403 · usage limit`. */
  health_reason?: string | null;
  cooldown_class?: ProviderAccountCooldownClass | null;
  /** ISO 8601 `+08:00`. Derived from the Redis TTL at READ time, not stored. */
  recovers_at?: string | null;
  last_probe_at?: string | null;
  last_probe_ok?: boolean | null;
  last_probe_model?: string | null;
}

/**
 * The single authoritative "unset vs default" resolver for account health.
 *
 * Exists so no call site writes `acct.health_state || 'healthy'`: the whole
 * point of the 4th state is that ABSENCE of evidence is its own verdict.
 */
export function providerAccountHealthState(
  account: Pick<ProviderAccount, 'health_state'>,
): ProviderAccountHealthState {
  const state = account.health_state;
  if (state === 'healthy' || state === 'cooling' || state === 'invalid') {
    return state;
  }
  return 'unknown';
}

/** One account's outcome inside a probe run. */
export interface ProviderAccountProbeResult {
  account_label: string;
  probed: boolean;
  ok: boolean;
  /** `revived` | `failed` | `no_credential` — server-authored, display-only. */
  outcome?: string;
  error?: string;
  cost_usd?: number;
}

/** Response of POST /api/admin/providers/tokens/{env}/probe. */
export interface ProviderAccountProbeResponse {
  env_var_name: string;
  provider: string;
  /** The model actually used (the whitelist-validated pick). */
  model: string;
  max_accounts_per_run: number;
  probed_count: number;
  skipped_by_cap: string[];
  /** Labels whose latch/cooldown the probe cleared. */
  revived: string[];
  total_cost_usd: number;
  results: ProviderAccountProbeResult[];
}

/**
 * Reserved account label. Contract Pack v1: a literal, NOT a NULL sentinel
 * (.claude/rules/sentinel-overloading.md). The `default` row cannot be
 * created by hand nor deleted — the server refuses the delete with a 4xx.
 */
export const DEFAULT_PROVIDER_ACCOUNT_LABEL = 'default';

/** Contract Pack v1 constraint on `provider_tokens.account_label`. */
export const PROVIDER_ACCOUNT_LABEL_PATTERN = /^[a-z0-9_-]{1,32}$/;

/**
 * Validate a NEW account label against the frozen contract.
 * Returns null when valid, else a display-ready Chinese error string.
 *
 * `existingLabels` is optional; when supplied, a duplicate inside the same
 * `env_var_name` group is rejected client-side (the server upsert would
 * otherwise silently REPLACE that account's key).
 */
export function validateProviderAccountLabel(
  label: string,
  existingLabels: string[] = [],
): string | null {
  if (!label) return '请输入账号标签';
  if (label === DEFAULT_PROVIDER_ACCOUNT_LABEL) {
    return '“default” 为保留标签，不能新建（它是每个 provider 的主账号）';
  }
  if (!PROVIDER_ACCOUNT_LABEL_PATTERN.test(label)) {
    return '标签只能包含小写字母、数字、下划线或连字符，长度 1–32';
  }
  if (existingLabels.includes(label)) return '该标签已存在，请换一个';
  return null;
}

export interface ProviderProfilesResponse {
  profiles: ProviderProfileEntry[];
}

/**
 * Sanitized provider profile returned by the non-admin endpoint
 * `GET /api/conversations/provider-profiles`, consumed by the
 * new-conversation dialog and by ChatPage (composer pill health).
 * Token values are NEVER returned;
 * `token_required` indicates the profile declares a token env var and
 * `is_set` whether a live token is currently resolvable.
 *
 * `health_state` (P-COND-1F526B8A2BA5 ST3) is the AUTHORITATIVE field: the
 * four-state roll-up over whichever credential ring holds this provider's
 * secret (env-var API keys, the account-pair OAuth ring, or the Codex DB),
 * folded by `account_pair_manager.py::get_profile_health`. It reuses the SAME
 * `ProviderAccountHealthState` union the admin page speaks — one vocabulary,
 * declared once, never a second copy.
 *
 * `healthy` (P-COND-5062A6F6431A ST4) is the LEGACY boolean, kept one release
 * as a compat shim so a browser on a stale cached bundle still understands the
 * payload. The backend derives it as `health_state !== 'invalid'`, so it
 * CANNOT disagree — but it is lossy (it cannot distinguish "throttled, back in
 * 30 minutes" from "fine"). Read `health_state`; treat `healthy` as legacy.
 *
 * BOTH are optional on purpose — an older backend omits them — and a missing
 * `health_state` means `unknown`, never `healthy`. The whole chain is
 * fail-open: signal lost (field absent, fetch failed, Redis down) → render
 * normally, send stays enabled. NEVER grey a pill, and never block sending,
 * because a signal went missing.
 */
export interface SelectableProviderProfile {
  name: string;
  display_name: string;
  token_required: boolean;
  is_set: boolean;
  /** @deprecated Legacy shim; derived from `health_state`. Read that instead. */
  healthy?: boolean;
  health_state?: ProviderAccountHealthState;
  /** Scoped execution eligibility; legacy health alone is not a permit. */
  selectable?: boolean | null;
  unavailable_reason?: string | null;
  recommended_model?: string | null;
  fallback_provider?: string | null;
  fallback_model?: string | null;
  fallback_reason?: string | null;
  model_availability?: Record<string, {
    selectable: boolean;
    state: ProviderAccountHealthState;
    reason?: string | null;
    /** Stable machine code for the unavailability (e.g. {@link CODEX_CONVERSATION_UNSETTLED}). */
    code?: string | null;
  }>;
}

/**
 * `model_availability[*].code` emitted when this conversation still holds an
 * unsettled Codex run lock. The composer's refresh button then asks the backend
 * to re-check (`recheckConversationCodexRuns`) before re-reading availability.
 * Single spelling — import it, never re-type the literal.
 */
export const CODEX_CONVERSATION_UNSETTLED = 'codex_conversation_unsettled';

/** POST /api/conversations/{id}/codex-recheck response. `message` is user-safe Chinese text. */
export interface CodexConversationRecheckResult {
  released: number;
  remaining: number;
  pending: boolean;
  needs_admin: boolean;
  message: string;
}

/** POST /api/admin/codex/runs/{run_id}/reconcile response (`status` = resulting run status). */
export interface CodexRunReconcileResult {
  run_id: string;
  status: string | null;
}

/**
 * Admin reconcile is a synchronous in-process recovery that can outlive the
 * client-wide 30 s API_TIMEOUT; a client abort would read as "failed" while the
 * server keeps going. Per-request override only.
 */
export const CODEX_RECONCILE_TIMEOUT_MS = 120000;

export interface BackendSelectorResponse {
  current: string;
  options: string[];
}

/** One selectable third-party reviewer model (多模型复核). */
export interface ReviewTarget {
  provider: string;
  model: string;
  display_name: string;
  available: boolean;
  unavailable_reason?: string | null;
  /** 'aux' (one tool-less model call) | 'agent' (read-only review child, ST10). */
  execution?: 'aux' | 'agent';
  /** Vendor label shown above the model id in the picker (e.g. "DeepSeek"). */
  provider_label?: string | null;
}

/** Reviewer list + the server-authoritative caps that bound a review. */
export interface ReviewTargetsResponse {
  targets: ReviewTarget[];
  max_models_per_review: number;
  remaining_today: number;
  /** Server-side USD spend cap per reviewer model (footer copy; never hardcoded). */
  max_cost_usd_per_model?: number;
  /**
   * Why `targets` is empty (P-COND-368144D76579). Enum only — never model
   * names. null when targets is non-empty; absent on an older backend
   * (the modal then falls back to `multiReview.no_targets`).
   */
  unavailable_reason?: ReviewUnavailableReason | null;
}

/** Canonical `ReviewTargetsResponse.unavailable_reason` values. */
export type ReviewUnavailableReason = 'disabled' | 'no_project' | 'no_authorized_model' | 'error';

/** Per-model result of a 多模型复核 run. */
export interface ReviewOutcome {
  provider: string;
  model: string;
  ok: boolean;
  /** 'approved' | 'issues' | 'unknown' — never optimistically upgraded. */
  verdict: string;
  error?: string | null;
  latency_ms: number;
  cost_usd: number;
  execution?: 'aux' | 'agent';
  /** Review child conversation (agent targets) — link to its investigation. */
  child_conversation_id?: string | null;
}

export interface MultiModelReviewResponse {
  /** Id of the injected role="system" message; null while `pending`. */
  message_id: string | null;
  outcomes: ReviewOutcome[];
  remaining_today: number;
  /** True when agent targets run in the background (result arrives over WS). */
  pending?: boolean;
  /** Set when agent targets were started; pass to cancelMultiModelReview. */
  review_id?: string | null;
}

/** Live status of one review child — `agent_delegations.status` (contract enum). */
export type ActiveReviewModelStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancel_requested'
  | 'cancel_unconfirmed'
  | 'cancelled';

/** One model of a running review (GET .../multi-model-review/active). */
export interface ActiveReviewModel {
  label: string;
  status: ActiveReviewModelStatus;
  started_at: string | null;
  child_conversation_id: string | null;
}

/** Panel phase of an open review (`review_agent.py::open_reviews`, P-COND-4A04BE14C922). */
export type OpenReviewPhase = 'running' | 'awaiting_proposal' | 'awaiting_approval' | 'finished';

/** `auto` = Phase 1 post-hoc 独立审查; `manual` = 人工多模型复核. */
export type OpenReviewSource = 'auto' | 'manual';

/**
 * One review the SERVER lists as open for this conversation. The four
 * optional fields are absent on an older backend — normalise them through
 * ReviewResultCard.tsx::normalizeOpenReview, never read them raw.
 */
export interface ActiveReview {
  review_id: string;
  started_at: string;
  models: ActiveReviewModel[];
  source?: OpenReviewSource;
  running?: boolean;
  phase?: OpenReviewPhase;
  owner_display_id?: string | null;
}

export interface ActiveReviewsResponse {
  reviews: ActiveReview[];
  /** Running reviews that hold a plain approve (same count as the server's
   *  approval gate, hidden reviews included). Absent on an older backend. */
  approval_hold_count?: number;
}

/**
 * Message send request matching backend MessageCreate.
 */
interface SendMessageRequest {
  content: string;
  client_timestamp?: string;
  language?: string;
}

/**
 * Backend ConversationSummary schema.
 */
export interface ConversationSummaryResponse {
  id: string;
  project_id: string;
  project_name: string;
  is_active: boolean;
  is_wecom: boolean;
  compact_summary: string | null;
  last_message_preview?: string;
  theme?: string;
  is_group?: boolean;
  member_count?: number;
  member_usernames?: string[];
  created_at: string;
  updated_at: string;
  has_unread?: boolean;
  is_favorite?: boolean;
  /** Fork lineage (方案3, P-COND-3AA40FA7D564): true = HEAD transferred to a
   *  fork; the conversation is read-only (no /push, no dispatch). */
  is_readonly?: boolean;
  execution_read_only?: boolean;
  /** Source conversation this one forked from (reuses its clone); null = owns
   *  its own clone. */
  forked_from_conversation_id?: string | null;
  /** Discussion lineage (ST5): conversation this one was branched from; null = not a branch. */
  branched_from_conversation_id?: string | null;
  /** Discussion lineage (ST5): source message at the branch point. */
  branched_from_message_id?: string | null;
  /** Personal relay (个人中转站): owner-only, never dispatched, 24h TTL per message. */
  is_personal_relay?: boolean;
  /** Server-owned: skill_refine = private draft; observation = read-only execution; null = ordinary. */
  purpose?: string | null;
}

/**
 * Backend ConversationDetail schema.
 */
export interface ConversationDetailResponse {
  id: string;
  project_id: string;
  project_name: string;
  is_active: boolean;
  is_wecom: boolean;
  compact_summary: string | null;
  theme?: string;
  is_group?: boolean;
  member_count?: number;
  messages: MessageResponse[];
  pending_action: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  /** Whether more historical messages are available (pagination) */
  has_more?: boolean;
  /** ID of the oldest message in the current page (pagination cursor) */
  oldest_message_id?: string | null;
  /** Method A provider profile name (null/omitted => backend defaults to "anthropic"). */
  provider_profile?: string | null;
  /** Models available for this conversation's provider; empty => no selector. */
  allowed_models?: string[];
  /**
   * Per-conversation multimodel routing fields (Per-Project Backend Plan
   * Sub-Task 1). Null on any field means "inherit from project default",
   * which in turn falls back to system-level defaults. Same wire enums as
   * `ProjectResponse.architecture_mode` / `backend_kind`.
   */
  architecture_mode?: 'claude_cli' | 'conductor_native' | 'codex_cli' | null;
  backend_kind?: 'claude' | 'aux_only' | null;
  backend_model?: string | null;
  /** Fork lineage (方案3, P-COND-3AA40FA7D564): true = read-only (HEAD moved to
   *  a fork; no /push, no dispatch). */
  is_readonly?: boolean;
  execution_read_only?: boolean;
  /** Source conversation this one forked from; null = owns its own clone. */
  forked_from_conversation_id?: string | null;
  /** Discussion lineage (ST5): conversation this one was branched from; null = not a branch. */
  branched_from_conversation_id?: string | null;
  /** Discussion lineage (ST5): source message at the branch point. */
  branched_from_message_id?: string | null;
  /** Workspace mode + dev/collab sub-mode for the chat-header mode badge
   *  (P-COND-403CF74F433B / P-COND-COLLABSUB). null = legacy conversation. */
  mode?: 'dev' | 'collab' | null;
  dev_submode?: 'branch_dev' | 'operation' | null;
  collab_submode?: 'planning' | 'design' | 'production' | null;
  /** Personal relay (个人中转站): owner-only, never dispatched. */
  is_personal_relay?: boolean;
  /** Per-message lifetime in seconds for a relay conversation; null otherwise. */
  relay_ttl_seconds?: number | null;
  /** Server-owned: skill_refine = private draft; observation = read-only execution; null = ordinary. */
  purpose?: string | null;
  /** Delegation / review-child lineage (P-COND-DEF54F26332F ST3), read-only.
   *  Parent conversation id; null = top-level conversation. */
  parent_conversation_id?: string | null;
  /** 'standard' | 'delegation_child' | 'review_child' | 'image_job'. */
  conversation_kind?: string | null;
}

export interface PlatformSkillEntry {
  slug: string;
  is_default: boolean;
  category: string;
  description: string;
  description_zh?: string;
  /** "active" | "inactive" | "mandatory" | "override" */
  status: string;
  is_starred?: boolean;
  modes: string[];
}

export interface PlatformSkillCatalogResponse {
  items: PlatformSkillEntry[];
}

export interface UserSkillPrefs {
  pinned_skills: string[];
  pinned_commands: string[];
}

/** Partial update type — pinned_commands is optional so callers that only
 *  change pinned_skills don't have to carry the full prefs object. */
export interface UserSkillPrefsUpdate {
  pinned_skills: string[];
  pinned_commands?: string[];
}

export interface MandatorySkillEntry {
  slug: string;
  description: string;
}

export interface MandatorySkillsResponse {
  items: MandatorySkillEntry[];
}

/**
 * Per-conversation composer UI memory (model / supervisor / long). Backed by an
 * ephemeral Redis cache, not the DB — restores the last-used composer state
 * when switching back to a conversation.
 */
export interface ComposerPrefs {
  model: string | null;
  supervisor: boolean;
  long: boolean;
  /**
   * Per-conversation thinking effort tier (P-COND-A7564248F0E7 ST3).
   * Legal set: low | medium | high | extra | max. `null` means "not yet
   * chosen" — the composer treats it as the default `high`.
   */
  thinking_effort: string | null;
  /**
   * Per-conversation Thinking on/off toggle (separate from the effort tier).
   * `null` means "not yet chosen" — the composer treats it as the default ON
   * (true). When OFF the dispatch injects MAX_THINKING_TOKENS=0 regardless of
   * the effort tier's budget.
   */
  thinking_enabled: boolean | null;
  /**
   * Per-conversation output-length tier (P-COND-MULTIPROVIDER-THINKLEN).
   * One of the active provider's `length_modes[].name` (e.g.
   * standard | long | max). `null` means "not yet chosen" — the composer
   * treats it as the default `standard` (no output-length override).
   */
  thinking_length: string | null;
  /**
   * Per-conversation 规划/开发 composer toggle. `null` means "this
   * conversation never chose" — the composer resolves it to `coding`. This is
   * the ONLY resolution point for that default; the backend never derives it.
   */
  dispatch_mode: 'planning' | 'coding' | null;
}

/**
 * Backend MessageResponse schema.
 */
export interface MessageResponse {
  id: string;
  conversation_id: string;
  role: string;
  source: string;
  content: string;
  content_rich: Record<string, unknown> | null;
  created_at: string;
  attachments?: AttachmentInfo[];
  sender_name?: string;
  metadata?: Record<string, unknown> | null;
}

/**
 * Backend ProjectResponse schema (admin, full details).
 */
export interface ProjectResponse {
  id: string;
  name: string;
  display_name?: string | null;
  description: string | null;
  path: string;
  access_method: string;
  status: string;
  default_permission_tier: string | null;
  default_model: string | null;
  /**
   * Project-level allowed-model whitelist (P-COND-1027 Phase 2 follow-up).
   * `null` means "no whitelist — any catalog model accepted". An empty
   * array `[]` is the admin escape hatch for "lock all users out of model
   * selection" (UI confirms before sending). Backend mirrors this contract
   * in `ProjectResponse` (admin.py).
   */
  allowed_models: string[] | null;
  /**
   * Per-project multimodel routing override (P-COND Per-Project Backend
   * Plan, Sub-Task 1). When null, dispatch falls back to the system-level
   * defaults (`SystemSettingsResponse.default_architecture_mode` etc.).
   * Wire enums mirror `update_system_settings`:
   *   - architecture_mode ∈ {'claude_cli','conductor_native','codex_cli'}
   *   - backend_kind      ∈ {'claude','aux_only'}
   *   - backend_model     free-form string (validated by catalog at runtime)
   */
  architecture_mode: 'claude_cli' | 'conductor_native' | 'codex_cli' | null;
  backend_kind: 'claude' | 'aux_only' | null;
  backend_model: string | null;
  ssh_host: string | null;
  ssh_port: number | null;
  require_ip_whitelist: boolean;
  need_approval: boolean;
  planning_folder: boolean;
  allowed_scripts: string[] | null;
  skill_tier_overrides: Record<string, string> | null;
  extra_mounts: Array<{source_project?: string; host_path?: string; container_path?: string; mode?: string}> | null;
  /**
   * Workspace-mode mount map (P-COND-CAAB7244BAE1 Sub-Task 2). The backend
   * admin ProjectResponse (admin.py) returns it; collab-only projects have
   * exactly one live `collab` key. Discriminate via
   * `constants/permissionTiers.ts::isCollabFromModePaths` — never inline.
   */
  mode_paths?: { dev?: string | null; collab?: string | null } | null;
  planning_progress?: {
    stage_label: string;
    exchange_count: number;
    collected_fields: string[];
    missing_fields: string[];
    stage_started_at: string | null;
    last_exchange_at: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

/**
 * Response body for POST /api/v1/projects/{id}/transfer (Phase 2 P-COND-P2-T2B).
 *
 * Returned on successful project ownership transfer. Denials raise
 * HTTP errors (403 / 400 / 404) with a `detail` string and no body
 * fields of this shape.
 */
export interface TransferProjectResponse {
  project_id: string;
  owner_user_id: string;
  previous_owner_user_id: string | null;
}

/**
 * Project summary for non-admin users (safe fields only).
 */
export interface TestEnvResponse {
  project_id: string;
  status: string;
  test_env?: {
    server_host?: string;
    slot?: number;
    ports?: Record<string, number>;
    mysql_db?: string;
    mysql_user?: string;
    redis_db?: number;
    systemd_unit?: string;
  } | null;
  free_mb?: number | null;
  message?: string | null;
}

/**
 * Per-project entry in the shared test-server inventory (admin view).
 */
export interface TestServerApp {
  project_name: string;
  project_id: string;
  status: string;
  slot: number | null;
  http_port: number | null;
  systemd_unit: string | null;
  ram_mb: number | null;
}

/**
 * Aggregated response for the Test Server admin section.
 */
export interface TestServerAppsResponse {
  host: string;
  reachable: boolean;
  free_mb: number | null;
  apps: TestServerApp[];
}

/**
 * One Conductor own-host test slot (registry: scripts/test-env/slots.conf).
 * Distinct from the shared remote TestServerApp above. Backend returns a
 * bare array of these from GET /api/admin/conductor-slots.
 */
export interface ConductorSlot {
  slot_id: string;
  service: string;
  port: string | null;
  worktree: string | null;
  is_legacy: boolean;
  created: string | null;
  status: string;
  /** 8-char worktree HEAD (matches /health/ready "version"); null on probe failure. */
  head?: string | null;
  /** True when local master has commits the worktree HEAD lacks; null = unknown. */
  behind_master?: boolean | null;
  /** Legacy only: true while takeover mode is ON (state file present); null for other slots. */
  takeover?: boolean | null;
}

export type ConductorSlotsResponse = ConductorSlot[];

/** Response of the background-op slot endpoints (deploy-master / teardown). */
export interface ConductorSlotOpStartResponse {
  status: string;
  op_name: string;
  slot_id?: string;
  refresh_db?: boolean;
  enable?: boolean;
}

/**
 * Response shape for a conductor-slot control action.
 */
export interface ConductorSlotActionResponse {
  slot_id: string;
  action: string;
  status: string;
  slot_status: string;
}

// ---------------------------------------------------------------------------
// Deployment Slots — blue-green port pool (P-COND-35C384AB4499)
// ---------------------------------------------------------------------------

export interface DeploymentSlot {
  port: number;
  /**
   * Closed set: idle | staging | active | draining.
   * Hard-compared in six places across the shell scripts and admin.py, so a new
   * literal makes a slot permanently unselectable — never widen it here to
   * describe a new state, add a sibling field instead (bug-patterns #607).
   */
  status: string;
  active_tasks: number;
  pid: string | null;
  version: string;
  started_at: string | null;
  /** Only present when status === 'staging' */
  preview_url?: string;
  /**
   * True when slot data was inferred by bootstrap probe (no Redis record).
   * Set by admin.py::get_deployment_slots bootstrap path; absent in normal flow.
   */
  bootstrap_slot?: boolean;
}

/**
 * Result snapshot of one background deploy-script run.
 * Written by admin.py::_log_deploy_script_output, read by
 * admin.py::get_deployment_slots. Field spelling is a backend contract —
 * see planning/graceful-deploy-visibility-and-blockers-plan.md > Contract Pack.
 */
export interface DeployLastResult {
  /** 'graceful_stage' | 'graceful_promote' */
  script: string;
  /** 'running' | 'finished' */
  status: string;
  /** Process exit code; null while status === 'running' */
  rc: number | null;
  /** Last ~2000 chars of the script's combined stdout */
  tail: string;
  /** ISO 8601 with +08:00 offset */
  started_at: string;
  /** ISO 8601 with +08:00 offset; null while status === 'running' */
  finished_at: string | null;
}

/**
 * What parked a slot for admin confirmation. Closed set, validated server-side
 * by app/services/deployment_state.py::validate_trigger.
 */
export type PendingPromotionTrigger =
  | 'nightly_heap'
  | 'nightly_rebuild'
  | 'operation_safe'
  | 'operation_force'
  | 'callback';

/**
 * A slot that is staged and waiting for a platform admin to cut traffic to it
 * (P-COND-9327D08D05D7). Written by app/services/deployment_state.py, surfaced
 * by admin.py::get_deployment_slots, consumed by DeploymentSlotsSection.
 *
 * The parked slot's own `status` stays 'staging' — this record is a SEPARATE
 * Redis key precisely so the closed status enum did not have to grow a literal.
 * Confirming calls the ordinary promote endpoint; there is no dedicated
 * confirm endpoint.
 */
export interface PendingPromotion {
  /** Port of the staged slot awaiting confirmation */
  port: number;
  /** git SHA (8 chars) of the staged process */
  staged_version: string;
  /** git SHA (8 chars) of the currently active slot; null when unknown */
  active_version: string | null;
  /** ISO 8601 UTC — when the slot was parked */
  staged_at: string;
  trigger: PendingPromotionTrigger | string;
  /** ISO 8601 when a platform admin was notified; null = not yet notified */
  notified_at: string | null;
}

export interface DeploymentSlotsResponse {
  slots: DeploymentSlot[];
  active_port: number;
  staging_port: number | null;
  port_pool: number[];
  /** Absent/null when no deploy ran in the last 24h or the snapshot is unreadable */
  last_result?: DeployLastResult | null;
  /**
   * Absent/null when nothing is parked, the 7-day record TTL lapsed, or the
   * backing service module is unavailable.
   */
  pending_promotion?: PendingPromotion | null;
}

export interface DeploymentSlotActionResponse {
  status: string;
  port?: number;
  pid?: number;
}

export interface ProjectSummaryResponse {
  id: string;
  name: string;
  /** Chinese/human-readable display name (AD-76). Null = use name as fallback. */
  display_name?: string | null;
  /**
   * Owning organisation id. `null` / omitted = legacy project with no org.
   * Mirrors backend `ProjectSummary.org_id` (P-COND-F9806CD74F33 ST1).
   */
  org_id?: string | null;
  /**
   * Owning organisation label — the backend emits `Organization.display_name`,
   * i.e. the SAME field `OrgSwitcher.tsx` renders in its dropdown options and
   * its single-org static label, so the sidebar L1 `.proj-org` chip and the org
   * filter above it always read identically. `null` / omitted = no org (legacy
   * project) and the chip is simply not rendered.
   */
  org_name?: string | null;
  description: string | null;
  need_approval: boolean;
  user_tier: string | null;
  can_select_model: boolean;
  /**
   * Owner-of-record (``projects.owner_user_id``). `null` = ownerless legacy
   * project. Surfaced so the chat model picker locks ON for the project
   * owner regardless of ``can_select_model`` (P-COND-33A24E491829 Issue 2).
   */
  owner_user_id?: string | null;
  /**
   * Owner-console ST3 — true iff the caller may manage this project in the
   * org console (admin, or a project owner holding full_autonomy). Mirrors
   * ``projects.py::ProjectSummary.can_manage``. Missing = false.
   */
  can_manage?: boolean;
  default_model: string | null;
  /**
   * Project-scoped allowed-model whitelist (P-COND-1027 M2).
   * `null` = no whitelist (any catalog model accepted).
   * Empty `[]` = admin escape hatch (locks model selection).
   * Non-empty array = the only models the session picker may show.
   */
  allowed_models: string[] | null;
  /**
   * Org ∩ project effective provider whitelist (2026-06-10).
   * `null`/absent = unrestricted (legacy project without org_id).
   * Non-null array = NewConversationDialog only shows provider profiles
   * whose name is in this list (the "Anthropic default" option always
   * remains as fallback). Layer 1 of the 3-layer provider authorization
   * chain (picker filter / create-time 422 / dispatch gate).
   */
  effective_providers?: string[] | null;
  status: string;    // "active", "planning_requirements", etc.
  git_enabled: boolean;
  /**
   * Dispatch backend selector ('local' | 'ssh_docker' | 'ssh_agent' | 'docker'
   * | 'api'). Surfaced so the NewConversationDialog fork ("基于某个会话") selector
   * only appears for ssh_docker projects — the per-conversation clone reuse
   * feature is ssh_docker-only; local+git uses worktree routing and never
   * clones (P-COND-3AA40FA7D564 gate correction).
   */
  access_method?: string;
  /**
   * Whether the project has a configured git remote (``projects.git_url``).
   * Surfaced so the NewConversationDialog "＋ 创建新分支" affordance appears only
   * for git self-managed projects (``git_enabled && !has_git_url``); Codeup /
   * 云仓管 projects (``has_git_url=true``) auto-create feat_* branches per
   * dispatch and do not need the manual create-branch flow. Optional/absent =
   * legacy list responses that don't emit the field (treated as falsy).
   */
  has_git_url?: boolean;
  /**
   * Project-level default base branch (``projects.git_branch_default``). Used
   * by PreflightBar to pre-select the correct branch and auto-reset branch_base
   * when the user switches to operation mode (P-COND-E607493E4CCE).
   */
  git_branch_default?: string | null;
  /**
   * Git management mode, derived ONCE server-side
   * (``projects.py::_project_git_mode``, P-COND-CA40B4848921 ST3): never
   * re-derive it from access_method / git_url on the client.
   */
  git_mode?: 'local_worktree' | 'self_managed' | 'cloud' | 'none';
  /**
   * Integration line for two-level promotion (feature -> integration ->
   * master). null = no integration line (/merge goes straight to master).
   */
  git_integration_branch?: string | null;
  /**
   * The provider a model-locked user (``can_select_model=false``) is pinned to,
   * resolved SERVER-SIDE (P-COND-ACAD85BAB62D). The chat / new-conversation UI
   * uses this to label the locked provider instead of re-deriving it (the old
   * ``|| 'anthropic'`` hardcode was wrong for deepseek/GLM/Kimi-default
   * projects). `null` / omitted = fall back to the effective-provider default.
   */
  locked_default_provider?: string | null;
  /**
   * Onboarding complexity tier ('light' | 'standard' | 'full'); null /
   * omitted = legacy project created before tiered onboarding. Drives the
   * StageProgressBar S0-S4 truncation (P-COND-45DA25450F43 Sub-Task 3).
   */
  complexity_tier?: string | null;
  planning_progress?: {
    stage_label: string;
    exchange_count: number;
    collected_fields: string[];
    missing_fields: string[];
    stage_started_at: string | null;
    last_exchange_at: string | null;
  } | null;
  /**
   * Workspace mode roots (SP-U2, P-COND-CXACT-T3-U2). `{"dev": "<abs>",
   * "collab": "<abs>"}` when the project has workspace modes enabled; `null`
   * / omitted = modes not configured (today's default). Optional here because
   * the non-admin project LIST endpoint (backend `ProjectSummary`) does not
   * yet surface this column — it currently only appears on the project DETAIL
   * responses. Declaring it optional makes the conversation-mode selector
   * appear automatically if/when the list endpoint starts emitting it,
   * without any further frontend change (extra JSON fields are ignored today).
   */
  mode_paths?: { dev?: string | null; collab?: string | null } | null;
}

/**
 * A conversation whose per-conversation clone can be reused as a fork source
 * (方案3 clone reuse). Returned by GET /api/conversations/forkable.
 */
export interface ForkableConversation {
  id: string;
  branch_name: string;
  /** pending (待提交) | committed (已提交) | clean (未修改) */
  status: 'pending' | 'committed' | 'clean' | string;
  updated_at: string;
}

/**
 * Response of POST /api/projects/{id}/feat-branch (create a feat_* base branch).
 */
export interface CreateFeatBranchResponse {
  branch: string;
  base_branch: string;
  access_method: string;
}

/**
 * Project creation request matching backend ProjectCreate schema.
 */
export interface ProjectCreateRequest {
  name: string;
  description?: string;
  path: string;
  access_method: string;
  ssh_host?: string;
  ssh_port?: number;
  ssh_user?: string;
  api_host?: string;
  api_port?: number;
}

/**
 * Backend SystemStatus schema.
 */
export interface SystemStatusResponse {
  database: string;
  redis: string;
  active_conversations: number;
  pending_confirmations: number;
  uptime_seconds: number;
  current_month_cost_usd?: number;
}

/**
 * Agent self-discovery issue report (P-COND-01699F0E524C).
 *
 * A dispatched project agent that hits an authorization/tool mismatch or a
 * blocking error POSTs a read-only issue report to the callback API; conductor
 * runs a rule-based preliminary analysis and surfaces this admin queue.
 * Mirrors `backend/app/api/admin.py::AgentIssueResponse`.
 */
export interface AgentIssueReport {
  id: string;
  display_id?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  org_id?: string | null;
  conversation_id?: string | null;
  session_id?: string | null;
  category: string;
  severity: string;
  /**
   * Capture source of the report (P-COND-3BC2D7C95FC7 · ST-C).
   * `agent` = T2 self-report intake, `orchestrator` = T3 passive orchestrator
   * backfill, `runtime` = T1 native_loop deterministic gate hit. NULL = legacy
   * rows written before the column existed.
   */
  capture_source?: string | null;
  title: string;
  description: string;
  context?: Record<string, unknown> | null;
  analysis_summary?: string | null;
  analysis_meta?: Record<string, unknown> | null;
  status: string;
  occurrence_count: number;
  resolution_note?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at: string;
  /** S2 triage dimensions (nullable — pre-S2 rows carry null) */
  issue_kind?: string | null;
  risk_tier?: number | null;
  importance?: string | null;
  info_sufficiency?: string | null;
  fix_dispatched_by?: string | null;
  /**
   * Derived triage state, resolved server-side by
   * `admin.py::classification_state`. `issue_kind === null` means exactly
   * "never classified"; a row an admin requeued for re-judgement also has a
   * null issue_kind but reports `requeued` here. Never re-derive this from
   * issue_kind on the client (.claude/rules/sentinel-overloading.md).
   */
  classification_state?: 'classified' | 'requeued' | 'never_classified';
  /**
   * Auto-fix routing target (backend `shared_constants.FIX_TARGET_VALUES`).
   * Never null server-side: `undetermined` = not yet judged. Optional here only
   * for rows served by an older backend.
   */
  fix_target?: string;
}

/**
 * Advisory triage risk suggestion — `analysis_meta.risk_suggestion`, written by
 * `backend/app/aux/issue_triage.py::_resolve_risk_suggestion`. The pipeline
 * NEVER reads it; only an admin confirm (`confirmAgentIssueRiskTier`) sets the
 * formal `risk_tier`.
 */
export interface RiskSuggestion {
  tier: number;
  dimensions: string[];
  likely_paths: string[];
  rationale: string;
  floor_applied: boolean;
  prompt_version: string;
  suggested_at: string | null;
}

/** Risk tiers, 1 = safest (mirrors `shared_constants.RISK_TIER_VALUES`). */
export const RISK_TIER_VALUES: readonly number[] = [1, 2, 3, 4];

function isRiskTier(v: unknown): v is number {
  return typeof v === 'number' && RISK_TIER_VALUES.includes(v);
}

/** Parse `analysis_meta.risk_suggestion`; anything malformed -> null. */
export function readRiskSuggestion(meta: Record<string, unknown> | null | undefined): RiskSuggestion | null {
  const raw = meta && typeof meta === 'object' ? (meta as Record<string, unknown>).risk_suggestion : null;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isRiskTier(r.tier)) return null;
  const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  return {
    tier: r.tier,
    dimensions: strList(r.dimensions),
    likely_paths: strList(r.likely_paths),
    rationale: typeof r.rationale === 'string' ? r.rationale : '',
    floor_applied: r.floor_applied === true,
    prompt_version: typeof r.prompt_version === 'string' ? r.prompt_version : '',
    suggested_at: typeof r.suggested_at === 'string' ? r.suggested_at : null,
  };
}

/**
 * Nightly read-only strong-model review — `analysis_meta.risk_review`, written by
 * the risk-review callback (planning/nightly-strong-model-grading-plan.md
 * Contract Pack). Display-only here; since AD-225 the backend may machine-confirm
 * its tier (`risk_confirmed_kind = machine`) — the frontend never submits it.
 */
export interface RiskReview {
  /** Tier AFTER floors (1-4). */
  tier: number;
  model_tier: number | null;
  verdict: string;
  value_score: number | null;
  risk_dimensions: string[];
  verified_paths: string[];
  duplicate_of: string | null;
  rationale: string;
  floor_applied: boolean;
  model: string;
  /** "YYYY-MM-DDTHH:MM:SSZ" (UTC). */
  reviewed_at: string | null;
  reviewed_head: string;
}

/** Parse `analysis_meta.risk_review`; anything malformed -> null. */
export function readRiskReview(meta: Record<string, unknown> | null | undefined): RiskReview | null {
  const raw = meta && typeof meta === 'object' ? (meta as Record<string, unknown>).risk_review : null;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isRiskTier(r.tier) || typeof r.verdict !== 'string') return null;
  const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    tier: r.tier,
    model_tier: isRiskTier(r.model_tier) ? r.model_tier : null,
    verdict: r.verdict,
    value_score: typeof r.value_score === 'number' ? r.value_score : null,
    risk_dimensions: strList(r.risk_dimensions),
    verified_paths: strList(r.verified_paths),
    duplicate_of: typeof r.duplicate_of === 'string' ? r.duplicate_of : null,
    rationale: str(r.rationale),
    floor_applied: r.floor_applied === true,
    model: str(r.model),
    reviewed_at: typeof r.reviewed_at === 'string' ? r.reviewed_at : null,
    reviewed_head: str(r.reviewed_head),
  };
}

/** Who confirmed `risk_tier` (mirrors `shared_constants.RISK_CONFIRM_KINDS`, AD-225). */
export type RiskConfirmKind = 'human' | 'machine' | 'legacy_agent';
export const RISK_CONFIRM_KINDS: readonly RiskConfirmKind[] = ['human', 'machine', 'legacy_agent'];

/** Hard-lock reasons (mirrors `shared_constants.RISK_LOCK_REASONS`). */
export type RiskLockReason = 'core_path' | 'core_dimension' | 'too_many_files';
export const RISK_LOCK_REASONS: readonly RiskLockReason[] = ['core_path', 'core_dimension', 'too_many_files'];

/**
 * `analysis_meta.risk_confirmed_at/by/kind` (risk_confirm.py::confirm_risk_tier);
 * null when never confirmed. A confirmed row WITHOUT a known kind reads as
 * `legacy_agent` — the same reading as backend `autofix.py::_confirmed_kind`
 * (absent kind ≠ human, sentinel-overloading rule).
 */
export function readRiskConfirmation(
  meta: Record<string, unknown> | null | undefined,
): { at: string; by: string | null; kind: RiskConfirmKind } | null {
  const m = meta && typeof meta === 'object' ? (meta as Record<string, unknown>) : {};
  if (typeof m.risk_confirmed_at !== 'string') return null;
  const kind = RISK_CONFIRM_KINDS.find((k) => k === m.risk_confirmed_kind) ?? 'legacy_agent';
  return { at: m.risk_confirmed_at, by: typeof m.risk_confirmed_by === 'string' ? m.risk_confirmed_by : null, kind };
}

/**
 * `analysis_meta.risk_lock` DISPLAY snapshot (the backend recomputes the lock on
 * every write and never trusts it). Null unless `locked === true`.
 */
export function readRiskLock(
  meta: Record<string, unknown> | null | undefined,
): { reasons: string[]; locked_at: string | null } | null {
  const raw = meta && typeof meta === 'object' ? (meta as Record<string, unknown>).risk_lock : null;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.locked !== true) return null;
  return {
    reasons: Array.isArray(r.reasons) ? r.reasons.filter((x): x is string => typeof x === 'string') : [],
    locked_at: typeof r.locked_at === 'string' ? r.locked_at : null,
  };
}

/**
 * 422 `{"detail": "risk_tier_locked", "reasons": [...]}` from confirm / PATCH
 * (admin.py::_risk_tier_locked_response) → its reasons; any other error → null.
 */
export function riskTierLockedReasons(err: unknown): string[] | null {
  const resp = (err as { response?: { status?: number; data?: { detail?: unknown; reasons?: unknown } } })?.response;
  if (resp?.status !== 422 || resp.data?.detail !== 'risk_tier_locked') return null;
  const reasons = resp.data?.reasons;
  return Array.isArray(reasons) ? reasons.filter((x): x is string => typeof x === 'string') : [];
}

/** Bulk confirm result (admin.py::AgentIssueConfirmRiskTierBulkResponse). */
export interface RiskTierBulkConfirmResult {
  updated: number;
  failed: { id: string; reason: 'not_found' | 'error' | string }[];
}

/** One member row of a dedup family (GET /agent-issues/aggregate). */
export interface AgentIssueFamilyMember {
  id: string;
  display_id?: string | null;
  status: string;
  occurrence_count: number;
  created_at: string;
}

/**
 * A dedup family: the representative row's fields plus the group aggregates.
 * Mirrors `backend/app/api/admin.py::aggregate_agent_issues`.
 */
export interface AgentIssueFamily extends AgentIssueReport {
  dedup_key: string;
  /** Number of ROWS sharing the fingerprint. */
  family_size: number;
  /** Occurrences summed across those rows (NOT the representative's count). */
  family_occurrence_total: number;
  family_members: AgentIssueFamilyMember[];
}

// ── Auto-fix (观测中心 · 自动处理, AF-T8) ─────────────────────────────────
// Mirrors backend/app/api/autofix.py verbatim (planning/observability-autofix-plan.md
// §12.2.6): snake_case field names, NO conversion. Timestamps are +08:00 strings
// or null. Enum values come from backend/app/shared_constants.py (§12.2.3).

/** `live` | `not_live` | `unknown` — `unknown` must be treated as live (§3.2). */
export type AutofixLiveValue = 'live' | 'not_live' | 'unknown';

export interface AutofixLiveState {
  frontend: AutofixLiveValue;
  backend: AutofixLiveValue;
  overall: AutofixLiveValue;
  reasons: string[];
}

export interface AutofixReleaseRecord {
  stage: string;
  trigger: string;
  built_sha: string | null;
  replaced: string | null;
  result: string;
  started_at: string | null;
  finished_at: string | null;
}

/** Why a needs_human run was not auto-fixed (nightly_assistant_report.py::handoff_detail). */
export interface AutofixHandoffDetail {
  reason: string | null;
  authz_codes: string[];
}

export interface AutofixRunSummary {
  id: string;
  issue_id: string;
  issue_display_id: string | null;
  fix_target: string;
  stage: string;
  outcome: string;
  handoff_reason: string;
  handoff_detail?: AutofixHandoffDetail | null;
  effective_state: string;
  merge_commit: string | null;
  touches_frontend: boolean;
  touches_migrations: boolean;
  live_state: AutofixLiveState;
}

export interface AutofixBatch {
  id: string;
  batch_date: string;
  state: string;
  integration_branch: string | null;
  merged_sha: string | null;
  release_stages: AutofixReleaseRecord[];
  runs: AutofixRunSummary[];
}

export interface AutofixRevertAction {
  kind: 'revert' | 'restore' | null;
  allowed: boolean;
  reason_code: string | null;
}

export interface AutofixActionRecord {
  id: string;
  kind: string;
  detail: unknown;
  evidence: unknown;
  conversation_id: string | null;
  proposal_id: string | null;
  created_at: string | null;
}

export interface AutofixRevertRecord {
  op_id: string;
  kind: string;
  exec_state: string;
  target_sha: string | null;
  tested_sha: string | null;
  result_sha: string | null;
  error: string | null;
  initiator: string;
  ship: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface AutofixRunDetail extends AutofixRunSummary {
  actions: AutofixActionRecord[];
  reverts: AutofixRevertRecord[];
  revert_action: AutofixRevertAction;
}

export interface AutofixRevertPreview {
  preview_token: string;
  allowed: boolean;
  reason_code: string | null;
  target_sha: string | null;
  touched_files: string[];
  possibly_conflicting_commits: string[];
  touches_migrations: boolean;
  live_state: AutofixLiveState | null;
  /** Stage 2: the paired-test selection estimate (absent on older backends). */
  test_selection?: AutofixTestSelection;
}

/** Revert test selection: `paired` = base/after run of `nodeids` (+ redline), `full` = whole suite. */
export interface AutofixTestSelection {
  mode: 'paired' | 'full' | string;
  nodeids: string[];
  redline_count: number;
}

/** One member of a batch revert preview (merge order newest → oldest). */
export interface AutofixBatchRevertMember {
  run_id: string;
  issue_display_id: string | null;
  merge_commit: string | null;
  effective_state: string | null;
  target_sha?: string | null;
  touched_files?: string[];
  possibly_conflicting_commits?: string[];
  touches_migrations?: boolean;
  live_state?: AutofixLiveState | null;
  allowed?: boolean;
  reason_code?: string | null;
}

/**
 * Batch (all-or-nothing) revert preview.
 * Backend: GET /api/admin/autofix/batches/{id}/revert-batch-preview?run_ids=a,b
 */
export interface AutofixBatchRevertPreview {
  preview_token: string;
  allowed: boolean;
  reason_code: string | null;
  members: AutofixBatchRevertMember[];
  touched_files: string[];
  touches_migrations: boolean;
  possibly_conflicting_commits: string[];
  test_selection: AutofixTestSelection;
  blocked_members: { run_id: string; reason: string }[];
}

export interface AutofixBatchRevertClaim {
  group_id: string;
  leader_op_id: string;
  exec_state: string;
}

export type AutofixShipMode = 'next_nightly' | 'immediate';

export interface AutofixRevertClaim {
  op_id: string;
  exec_state: string;
}

export interface AutofixRevertOp {
  op_id: string;
  kind: string;
  exec_state: string;
  target_sha: string | null;
  tested_sha: string | null;
  result_sha: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  release_stages: AutofixReleaseRecord[];
}

export interface AutofixChangeOrder {
  project: string | null;
  summary: string | null;
  steps: unknown[];
}

export interface AutofixHandoff {
  run_id: string;
  issue_id: string;
  issue_display_id: string | null;
  handoff_reason: string;
  handoff_detail?: AutofixHandoffDetail | null;
  created_at: string | null;
  change_order: AutofixChangeOrder | null;
  /** Newest one-shot requeue of the issue (autofix.py::_latest_requeues); pending until its next run. */
  requeue?: AutofixHandoffRequeue | null;
}

export interface AutofixHandoffRequeue {
  requeued_at: string;
  actor_user_id: string | null;
  note: string | null;
  pending: boolean;
}

export interface AutofixRequeueResult {
  issue_id: string;
  run_id: string;
  requeued_at: string;
}

// ── Auto-fix status + authorization policies (autofix-observe ST3) ──
// Mirrors backend/app/api/autofix.py::get_status / _policy_out / PolicyCreate.

/** Targets a policy may authorize (autofix.py::_POLICY_FIX_TARGETS). */
export const AUTOFIX_POLICY_FIX_TARGETS: readonly string[] = ['conductor_platform', 'project_env'];
/** PolicyCreate bounds (autofix.py::_MAX_PATH_GLOBS / _VALID_DAYS_MIN / _VALID_DAYS_MAX). */
export const AUTOFIX_POLICY_MAX_PATH_GLOBS = 20;
export const AUTOFIX_POLICY_VALID_DAYS_MIN = 1;
export const AUTOFIX_POLICY_VALID_DAYS_MAX = 90;

export interface AutofixPolicy {
  id: string;
  version: number;
  scope: {
    fix_targets: string[];
    max_risk_tier: number | null;
    path_globs: string[];
    /** P-COND-NIGHTLY-P2: nightly assistant may system-approve within this policy; absent = false. */
    allow_system_approval?: boolean;
    /** Stage 3: business-decision autonomy switch; absent = false. */
    allow_decision_autonomy?: boolean;
    /** Stage 3: allowed decision ops (subset of AUTOFIX_DECISION_OPS); absent = []. */
    decision_ops?: string[];
  };
  /** Decimal strings; no reader yet (UI labels them 暂未生效). */
  daily_budget_usd: string;
  per_issue_budget_usd: string;
  valid_from: string | null;
  valid_until: string | null;
  revoked_at: string | null;
  created_by_user_id: string | null;
  created_at: string | null;
  /** Resolved server-side by autofix_policy.py::active_policy — never re-derive. */
  is_active: boolean;
}

/** One pipeline history entry (Redis `auto_fix:pipeline_history`, newest first). */
export interface AutofixHistoryEntry {
  ran_at: string;
  batch_date: string | null;
  batch_id: string | null;
  runs: number;
  /** STOP_* / GATE_* / `exception`; null = the pass completed normally. */
  stopped_reason: string | null;
  skip_counts: Record<string, number>;
  trigger: 'scheduled' | 'catch_up' | string;
}

/**
 * `autofix.py::_risk_tier_cap` (AD-225): the active policy's tier is
 * authoritative, clamped by the system ceiling. `policy_tier` / `effective` are
 * null without an active policy (`policy_tier` also null on an invalid scope).
 */
export interface AutofixRiskTierCap {
  ceiling: number;
  policy_tier: number | null;
  effective: number | null;
}

export interface AutofixStatus {
  enabled: boolean;
  daily_limit: number;
  /** Object since AD-225; a bare number = older backend (the system ceiling). */
  max_risk_tier_cap: AutofixRiskTierCap | number;
  /** Runtime breaker key present (pipeline paused); unreadable Redis -> true. */
  breaker_off: boolean;
  active_policy: AutofixPolicy | null;
  history: AutofixHistoryEntry[];
  /** Nightly strong-model review block; absent on an older backend. */
  risk_review?: AutofixRiskReviewStatus | null;
  /** Nightly assistant counts (P-COND-NIGHTLY-P2); absent on an older backend. */
  nightly_assistant?: NightlyAssistantStatus;
  /** Effective global budget (nightly-handoff-followups ST2); absent on an older backend. */
  budget?: AutofixBudgetStatus;
}

/**
 * `autofix.py::_budget_status` — the caps in force (shared_constants, USD
 * decimal strings) plus the current night's locked ledger limit
 * (`today_limit_usd`, null when no row yet or on a read error).
 */
export interface AutofixBudgetStatus {
  daily_usd: string;
  per_issue_usd: string;
  breaker_spend_usd: string;
  breaker_days: number;
  today_limit_usd: string | null;
}

/**
 * `autofix.py::get_status` `nightly_assistant` block (P-COND-NIGHTLY-P2 contract).
 * Degraded shape carries NO count keys: {enabled, daily_limit, window_days, degraded: true}.
 */
export interface NightlyAssistantStatus {
  /** settings.nightly_assistant_execute_enabled (default off). */
  enabled: boolean;
  daily_limit: number;
  window_days: number;
  /** Distinct runs with a pre_execute auth_check by the assistant in the window. */
  proposals?: number;
  /** Distinct runs with an approved pre_claim auth_check (the re-authorization the system approval is granted on). */
  approved?: number;
  /** Distinct runs with an approved post_diff auth_check AND nightly_assistant_close.is_merged (integrated + effective + merge_commit). */
  merged?: number;
  /** Distinct runs touched by the assistant ending needs_human. */
  needs_human?: number;
  /** needs_human runs bucketed by their latest action reason ("unknown" when none). P-COND-8CA59E83913D. */
  needs_human_by_reason?: Record<string, number>;
  /** True when the count query failed (display-only). */
  degraded?: boolean;
}

/** Redis `risk_review:last_run` summary (issue_risk_review.py). */
export interface RiskReviewLastRun {
  started_at: string | null;
  finished_at: string | null;
  date_cst: string | null;
  selected: number;
  dispatched_batches: number;
  written: number;
  skipped: number;
  by_verdict: Record<string, number> | null;
  cost_usd: number | null;
  cost_unknown_batches: number;
  /** null | review_cost_ceiling | budget_unavailable | disabled | parked | no_rows */
  stopped_reason: string | null;
  model: string | null;
}

/** `{agree, total, rate}`; rate is 0..1, null when total = 0. */
export interface RiskReviewAgreement {
  agree: number;
  total: number;
  rate: number | null;
}

export interface AutofixRiskReviewStatus {
  enabled: boolean;
  require_gate: boolean;
  last_run: RiskReviewLastRun | null;
  window_days: number;
  queue_len: number | null;
  by_verdict: Record<string, number> | null;
  agreement: {
    suggestion_vs_review: RiskReviewAgreement;
    review_vs_confirmed: RiskReviewAgreement;
  } | null;
  /** Rows machine-confirmed in the window (AD-225); absent on an older backend, null on a metrics error. */
  auto_confirmed?: number | null;
  /** Rows currently hard-locked (recomputed server-side). */
  locked?: number | null;
  /** review tier vs formal tier split by confirmation source; only `human` compares two independent judgments. */
  agreement_by_kind?: Record<RiskConfirmKind, RiskReviewAgreement> | null;
}

export interface AutofixPolicyCreateBody {
  fix_targets: string[];
  max_risk_tier: number;
  path_globs: string[];
  valid_days: number;
  /** Default false server-side; the panel always sends it explicitly. */
  allow_system_approval?: boolean;
  /** Stage 3: default false; decision_ops non-empty requires it (server 422). */
  allow_decision_autonomy?: boolean;
  decision_ops?: string[];
}

/** Mirrors shared_constants.py::AUTOFIX_DECISION_OPS (Stage 3 adoptable ops). */
export const AUTOFIX_DECISION_OPS: readonly string[] = ['ui_text'];

// ── Nightly assistant report (P-COND-8CA59E83913D Sub-Task 4/5) ──
// Mirrors backend/app/services/nightly_assistant_report.py::build_nightly_report.

export type NightlyReviewerKind = 'system' | 'human' | 'none';

export interface NightlyTestResult {
  id: string;
  kind: string;
  sha: string | null;
  run_id: string | null;
  collected: number | null;
  executed: number | null;
  failed: number | null;
  errors: number | null;
  passed: number;
  returncode: number | null;
  timed_out: boolean;
  report_ok: boolean;
  failed_nodeids: string[];
  has_log: boolean;
  started_at: string | null;
  finished_at: string | null;
}

export interface NightlyTimelineItem {
  at: string | null;
  kind: string;
  summary: string;
}

export interface NightlyRun {
  run_id: string;
  issue_id: string | null;
  issue_display_id: string | null;
  stage: string;
  outcome: string | null;
  reason: string | null;
  env_skip: boolean;
  merge_commit: string | null;
  conversation_ids: string[];
  timeline: NightlyTimelineItem[];
  tests: { base: NightlyTestResult | null; after: NightlyTestResult | null } | null;
  /** Decimal string, or "未知" when any step cost is unknown (#315 — never 0). */
  cost_usd: string;
  reviewer_kind: NightlyReviewerKind;
  /** Stage 2 (C15): why the run is a handoff ('none' when it is not). */
  handoff_reason?: string | null;
  /** In the current pending candidate → 剔除 is offered. */
  excludable?: boolean;
  /** Effective and no in-flight revert op → selectable for batch revert. */
  revertable?: boolean;
  /** A revert / restore op is in flight for this run. */
  revert_in_flight?: boolean;
  /** Stage 3: adopted business decisions (oldest first) with revocation state. */
  decisions?: NightlyDecision[];
  /** Stage 3: the run adopted at least one business decision (revoked or not). */
  is_business_decision?: boolean;
}

/** Mirrors nightly_assistant_report.py::run_decisions. */
export interface NightlyDecision {
  action_id: string;
  item_index: number | null;
  title: string | null;
  op: string | null;
  adopted_option: string | null;
  author_class: string | null;
  review_class: string | null;
  proposal_id: string | null;
  adopted_at: string | null;
  revoked: boolean;
  revoke_cause: string | null;
  revoked_at: string | null;
}

export interface NightlyBatch {
  id: string;
  batch_date: string;
  state: string;
  candidate_sha: string | null;
  candidate_base_sha: string | null;
  deployed_sha: string | null;
  test_env_status: string | null;
  test_env_url: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  merged_sha: string | null;
  integration_branch: string | null;
}

export interface NightlyBaseline {
  sha: string | null;
  date: string | null;
  failed: number | null;
  passed: number | null;
  source: string;
  ancestor_ok: boolean | null;
}

export interface NightlyReport {
  batch: NightlyBatch | null;
  baseline: NightlyBaseline | null;
  runs: NightlyRun[];
  integration_tests: NightlyTestResult[];
  test_env_error_tail: string | null;
  manual_command: string | null;
  available_dates: string[];
}

export interface NightlyApproveResult {
  status: string;
  merged_sha?: string | null;
}

export interface NightlyDiffFile {
  path: string;
  excluded: boolean;
  patch?: string;
  truncated?: boolean;
}

export interface NightlyRunDiff {
  base: string;
  head: string;
  files: NightlyDiffFile[];
  truncated: boolean;
}

/** Parsed auto-fix API error. `code` is one of AUTOFIX_REASON_CODES for 409s. */
export interface AutofixApiError {
  status: number | null;
  code: string | null;
  message: string;
}

/**
 * Parse an auto-fix API failure. 409 bodies are `{"detail": {"code", "message"}}`;
 * 404 / 422 / 503 bodies are `{"detail": "<text>"}`.
 */
export function parseAutofixError(err: unknown): AutofixApiError {
  const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const status = typeof resp?.status === 'number' ? resp.status : null;
  const detail = resp?.data?.detail;
  if (detail && typeof detail === 'object') {
    const d = detail as { code?: unknown; message?: unknown };
    return {
      status,
      code: typeof d.code === 'string' ? d.code : null,
      message: typeof d.message === 'string' ? d.message : '',
    };
  }
  return { status, code: null, message: typeof detail === 'string' ? detail : '' };
}

export interface AgentIssueListResponse {
  items: AgentIssueReport[];
  stats: {
    total: number;
    new: number;
    high_open: number;
    resolved_week: number;
  };
}

export interface AgentIssueQuery {
  status?: string;
  category?: string;
  severity?: string;
  /** Source filter: agent | orchestrator | runtime (P-COND-3BC2D7C95FC7 · ST-C). */
  capture_source?: string;
  project?: string;
  q?: string;
  limit?: number;
  /** S2 triage dimension filters */
  issue_kind?: string;
  risk_tier?: number;
  importance?: string;
  info_sufficiency?: string;
  /** Fix-target filter: conductor_platform | project_env | project_content | none | undetermined */
  fix_target?: string;
  /** analysis_meta.risk_suggestion.tier (1-4) */
  suggested_risk_tier?: number;
  /** false = rows WITHOUT a risk suggestion. axios keeps boolean false (it drops only null/undefined). */
  has_risk_suggestion?: boolean;
  /** false = unconfirmed, true = confirmed. Must be sent when false — never truthiness-filtered. */
  risk_confirmed?: boolean;
  /** analysis_meta.risk_confirmed_kind: human | machine | legacy_agent | none (= unconfirmed). */
  confirmed_kind?: string;
  /** analysis_meta.risk_lock.locked snapshot. Must be sent when false — never truthiness-filtered. */
  risk_locked?: boolean;
  /** analysis_meta.risk_review.verdict (backend shared_constants.RISK_REVIEW_VERDICTS) or "none" = not reviewed. */
  review_verdict?: string;
  /** S2 pagination */
  page?: number;
  page_size?: number;
  /** S2 sort: issue_kind_rank | importance_rank | occurrence_count | created_at | display_id | title | value_score */
  sort_by?: string;
  /** Sort direction for display_id / title / created_at: "asc" | "desc" */
  sort_dir?: string;
}

/**
 * `requeue` is deliberately NOT a status transition — it clears `issue_kind`
 * and stamps `analysis_meta.requeued_at` so the nightly classifier re-judges
 * the row. Rejected with 409 on closed rows (reopen first).
 */
export type AgentIssueAction =
  | 'triage'
  | 'investigate'
  | 'resolve'
  | 'dismiss'
  | 'reopen'
  | 'requeue';

export interface AgentIssuePatchBody {
  /** S2: optional — when absent, only label fields are updated (no state transition). */
  action?: AgentIssueAction;
  note?: string;
  /**
   * Platform-admin reclassification overrides (deploy-flow P2-F). When set they
   * override the intake auto-classification. `analysis_summary` records the
   * admin's preliminary mechanism description at triage/resolve time.
   */
  category?: string;
  severity?: string;
  analysis_summary?: string;
  /** S2 triage dimension label overrides (admin-editable) */
  issue_kind?: string;
  risk_tier?: number;
  importance?: string;
  info_sufficiency?: string;
}

/**
 * Global system settings (admin only).
 *
 * Multi-model defaults (P-COND-1027 M1-05): when a project does NOT supply
 * its own architecture/backend override, dispatch falls back to these
 * values. ``null`` on any of the three means "no global default — use
 * legacy claude_cli/claude wiring" (M0 behaviour).
 */
export interface SystemSettingsResponse {
  monthly_budget_cap_usd: number | null;
  fallback_model: string | null;
  current_default_model: string;
  daily_review_auto_dispatch: boolean;
  daily_review_max_dispatches: number;
  template_harvest_enabled: boolean;
  template_harvest_day: number;
  template_harvest_max_projects: number;
  default_architecture_mode: 'claude_cli' | 'conductor_native' | 'codex_cli' | null;
  default_backend_kind: 'claude' | 'native_loop' | null;
  default_backend_model: string | null;
  /**
   * P-COND-1027 Phase 2: when false (default) the legacy 3-field admin card
   * (Architecture Mode / Backend Kind / Backend Model) is hidden in favour
   * of the new Model + Thinking-effort picker. Admin can flip this to
   * surface the legacy override card for debugging.
   */
  advanced_overrides_enabled?: boolean;
  /**
   * D2 hemostasis: global kill switch for the self-serve subscription flow.
   * DEFAULT false (OFF) — self-serve subscription is closed until a platform
   * admin enables this. Both create_subscribe_intent and sandbox_callback
   * (the unauthenticated callback) gate on this DB value.
   */
  self_serve_subscription_enabled: boolean;
  /**
   * Conversation-compaction subsystem config (P-COND-7E403C2061B6 —
   * Sub-Task 4). compact_model null carries the "haiku default" semantic;
   * compact_max_chars is the per-segment summary cap and
   * compact_inject_max_chars the read-side total injection cap.
   * context_denoise_enabled is the experimental D/E Phase-2 switch.
   */
  compact_enabled: boolean;
  compact_provider: string | null;
  compact_model: string | null;
  compact_max_chars: number;
  compact_inject_max_chars: number;
  context_denoise_enabled: boolean;
  context_denoise_llm_min_chars: number;
  /**
   * Third independent switch: when true, claude_cli dispatches drop --resume
   * (stateless rebuild). Orthogonal to compaction/denoise switches.
   */
  claude_cli_stateless_rebuild_enabled: boolean;
  /**
   * P-COND-ACAD85BAB62D req#4: platform-level defaults applied when a NEW
   * org / NEW member / NEW project is created. All three are nullable —
   * null means "fall back to the historical hardcoded default".
   *   - new_org_default_allowed_providers: seeds Organization.allowed_providers
   *   - new_org_default_provider_defaults: seeds Project.provider_defaults
   *   - new_member_default_can_select_model: seeds ProjectMember.can_select_model
   */
  new_org_default_allowed_providers?: string[] | null;
  new_org_default_provider_defaults?: Record<string, string> | null;
  new_member_default_can_select_model?: boolean | null;
  /**
   * P-COND-64770C8A4E98: runtime A/B switch of the Phase 1 post-hoc review.
   * phase1_review_mode is the raw admin value (null = follow .env). The
   * effective mode/source are computed server-side by the single resolver
   * (delegation_review_chain.py::resolve_posthoc_review_mode) — never
   * re-derive them here. agent_delegation_enabled is read-only (.env); B
   * falls back to A when it is false.
   */
  phase1_review_mode?: Phase1ReviewMode | null;
  effective_phase1_review_mode?: Phase1ReviewMode;
  effective_phase1_review_mode_source?: 'admin' | 'env';
  agent_delegation_enabled?: boolean;
}

/** Legal values of system_settings.phase1_review_mode (null = follow .env). */
export type Phase1ReviewMode = 'A' | 'B';

/**
 * Aux model catalog entry (P-COND-1027 Phase 2).
 *
 * Returned by `GET /api/aux/catalog`. Each row carries the wire `name`
 * persisted in `dispatch_meta.backend_model`, the `provider` used by
 * `resolve_model_routing` to pick architecture_mode/backend_kind, and the
 * capability flags consumed by the frontend pickers.
 */
export interface AuxCatalogModel {
  name: string;
  provider: string;
  supports_thinking: boolean;
  thinking_default?: 'off' | 'low' | 'medium' | 'high' | 'max' | null;
  supports_vision?: boolean;
  supports_tools?: boolean;
  context_window?: number | null;
}

/**
 * Admin model row returned by GET /api/admin/models (Sub-Task C of
 * P-COND-B0ACC698DDB7). Mirrors the backend `AdminModelEntry` schema and
 * exposes the Sub-Task A SSoT routing fields (`architecture_mode` +
 * `default_backend`) so the new /admin/models page can render the backend
 * badge per row without re-implementing `resolve_model_routing` on the
 * client.
 */
export interface ModelRoutingInfo {
  name: string;
  provider: string;
  architecture_mode: 'claude_cli' | 'conductor_native' | 'codex_cli';
  default_backend: 'claude' | 'native_loop' | 'aux_only';
  /**
   * Catalog ``default_backend`` overlaid by the operator-set per-model
   * override from ``system_settings.model_backend_overrides`` (Option B
   * Minimal — P-COND-7B23A41E8C3F). The Models & Routing table renders
   * this value in the editable Backend column; ``default_backend`` is
   * preserved for display alongside it so the operator can see what
   * the catalog default would have been.
   */
  effective_backend: 'claude' | 'native_loop' | 'aux_only';
  display_name: string;
  supports_thinking: boolean;
  thinking_default: 'off' | 'low' | 'medium' | 'high' | 'max';
  context_window: number | null;
  enabled: boolean;
}

/**
 * Body for PATCH /api/admin/models (Option B Minimal —
 * P-COND-7B23A41E8C3F). Both fields are optional:
 * - ``enabled`` is validated server-side but persistence is still
 *   deferred to the follow-up that adds ``system_settings.enabled_models``.
 * - ``backend_overrides`` is persisted to
 *   ``system_settings.model_backend_overrides`` immediately. ``aux_only``
 *   is rejected at the server (422); legal values are ``claude``,
 *   ``native_loop``. Send ``{}`` to clear all overrides;
 *   omit the field to leave the persisted map untouched.
 */
export interface AdminModelsUpdate {
  enabled?: string[];
  backend_overrides?: Record<string, 'claude' | 'native_loop'>;
}

/**
 * GET /api/admin/models response envelope. `system_default_model` reflects
 * the env-backed `agent_default_model` setting; `providers` enumerates the
 * unique provider names found in the catalog so the UI can build grouped
 * table headers without re-iterating `models`.
 */
export interface AdminModelsResponse {
  models: ModelRoutingInfo[];
  system_default_model: string;
  providers: string[];
}

/**
 * Model Channel row returned by GET/POST/PUT /api/admin/model-channels
 * (Multi-Model Channel Config — P-COND-B37C3A98ECA5, Sub-Task 3).
 *
 * A channel is a DB-authoritative routing record that OVERRIDES the static
 * catalog. One ``official_model_name`` may appear under multiple channels
 * distinguished by ``display_name`` (the user-visible name AND the dispatch
 * key — the user-facing model picker sends ``display_name`` and channel
 * resolution at dispatch is keyed on it).
 *
 * SECRET SAFETY: ``api_key`` is NEVER returned. ``has_api_key`` is a boolean
 * indicator so the table can render a masked •••••• cell when a key is stored.
 */
export interface ModelChannelOut {
  id: number;
  provider: string;
  provider_display_name: string;
  display_name: string;
  official_model_name: string;
  architecture_mode: 'claude_cli' | 'conductor_native' | 'codex_cli';
  backend: 'claude' | 'native_loop';
  api_url: string | null;
  thinking_mode: string | null;
  enabled: boolean;
  has_api_key: boolean;
}

/**
 * Body for POST /api/admin/model-channels.
 *
 * ``api_key`` is plaintext and MUST be sent ONLY when the admin enters a new
 * key; the backend Fernet-encrypts it and never echoes it back. Omit/empty =
 * no key stored (channel falls back to env / provider profile / OAuth).
 */
export interface ModelChannelCreate {
  provider: string;
  provider_display_name: string;
  display_name: string;
  official_model_name: string;
  architecture_mode: 'claude_cli' | 'conductor_native' | 'codex_cli';
  backend: 'claude' | 'native_loop';
  api_url?: string | null;
  api_key?: string;
  thinking_mode?: string | null;
  enabled?: boolean;
}

/**
 * Body for PUT /api/admin/model-channels/{id}. All fields optional.
 * ``api_key``: send ONLY when the admin typed a new key; omit/empty leaves
 * the existing ciphertext unchanged (the backend does NOT clear it on empty).
 */
export type ModelChannelUpdate = Partial<ModelChannelCreate>;

/**
 * GET /api/admin/model-channels response envelope. ``providers`` enumerates
 * the catalog provider keys (anthropic|openai|deepseek|glm|kimi) for the
 * create/edit drawer; ``system_default_model`` mirrors the read-only default
 * card shared with the Models & Routing section.
 */
export interface ModelChannelsResponse {
  channels: ModelChannelOut[];
  providers: string[];
  system_default_model: string;
}

/**
 * Subscription pricing config (P-COND-LRT ST3).
 *
 * `subscription_pricing` mirrors config/provider_pricing.yaml: outer key =
 * plan_tier, inner key = period, value = full-period price in normalized-USD
 * (null = not purchasable, e.g. the internal tier). `periods` is the
 * canonical period column set from the backend.
 */
export type SubscriptionPricingTable = Record<
  string,
  Record<string, number | null>
>;

export interface SubscriptionPricingResponse {
  subscription_pricing: SubscriptionPricingTable;
  periods: string[];
}

/**
 * Per-plan-tier internal usage budget config (P-COND-LRT ST3, this ticket).
 *
 * Mirrors the `plan_tier_budgets` block of config/provider_pricing.yaml:
 * key = plan tier (internal/trial/standard/enterprise), value = the monthly
 * normalized-USD usage ceiling. `null` = 不限量 (unlimited, e.g. the internal
 * tier). A non-null value must be a POSITIVE number (backend PUT 422s on
 * zero / negative). This ceiling is an internal enforcement basis and is NOT
 * shown to end users (they see qualitative tier differences + ¥ price only).
 *
 * Backend: GET/PUT /api/v1/admin/plan-tier-budgets (require_platform_admin).
 */
export type PlanTierBudgets = Record<string, number | null>;

export interface PlanTierBudgetsResponse {
  plan_tier_budgets: PlanTierBudgets;
}

/**
 * Per-provider per-model token pricing + normalization coefficients config
 * (P-COND-LRT ST3, this ticket).
 *
 * Mirrors the `coefficients` + `providers` blocks of
 * config/provider_pricing.yaml. `coefficients` is a flat map of tuning
 * parameters (e.g. `nzd_to_usd`, `coefficient_list_usd_per_nzd`). Each
 * provider carries a `pricing_mode` ('subscription' | 'metered') and a
 * `models` map of `{input, output, cache_write, cache_read}` token unit
 * prices (USD / 1M tokens). All numeric fields must be NON-NEGATIVE (0 is
 * allowed); the PUT replaces the whole coefficients+providers block.
 *
 * Backend: GET/PUT /api/v1/admin/model-pricing (require_platform_admin).
 */
export interface ModelPricingEntry {
  input: number;
  output: number;
  cache_write: number;
  cache_read: number;
}

export interface ModelPricingProvider {
  pricing_mode: 'subscription' | 'metered';
  models: Record<string, ModelPricingEntry>;
}

export interface ModelPricingResponse {
  coefficients: Record<string, number>;
  providers: Record<string, ModelPricingProvider>;
}

/**
 * A self-serve payment order row (P-COND-LRT ST2, this ticket). Mirrors
 * `backend/app/models/payment_order.py::PaymentOrder` as serialized by
 * `GET /api/v1/orgs/{org_id}/payment-orders`. `amount_usd` is a
 * normalized-USD Decimal STRING (display with the ¥ label per the approved
 * RMB-display decision — no conversion rate applied). Timestamps are ISO-8601
 * with +08:00 offset (TZ:AWARE-UTC).
 */
export interface PaymentOrderItem {
  order_id: string;
  tier: string;
  period: string;
  amount_usd: string;
  status: 'pending' | 'paid' | 'failed' | 'cancelled';
  gateway_provider: string;
  created_at: string;
  resolved_at: string | null;
}

export interface PaymentOrderListResponse {
  orders: PaymentOrderItem[];
}

/**
 * Catalog model entry returned by `GET /api/catalog/models`
 * (Multimodel Unified Backend UX Plan -- Sub-Task D).
 *
 * Flat list shape consumed by:
 *   - `frontend/src/components/InputArea.tsx` -- filter by
 *     `conversation.provider_profile` and render the per-option backend
 *     badge ("claude-sonnet-4-5 -- Claude CLI").
 *
 * The `architecture_mode` and `default_backend` fields mirror the Sub-Task
 * A SSoT additions on `backend/app/aux/catalog.py::ModelInfo`. Backend
 * `aux_only` is reserved for internal aux dispatch and never reaches this
 * endpoint, but is kept in the union for forward compatibility.
 */
export interface CatalogModelInfo {
  name: string;
  provider: string;
  architecture_mode: 'claude_cli' | 'conductor_native' | 'codex_cli';
  default_backend: 'claude' | 'native_loop' | 'aux_only';
  display_name: string;
  supports_thinking: boolean;
}

/**
 * Envelope returned by `GET /api/catalog/models`.
 *
 * `allow_per_message_model_override` mirrors the system-level flag added
 * to `system_settings` by Sub-Task D's migration. The InputArea hides
 * the per-message model picker entirely when this is False (provider is
 * still locked from the conversation's `provider_profile`).
 */
/**
 * One selectable tier (a reasoning-strength or output-length档) for a
 * provider, as returned in `provider_configs` (P-COND-MULTIPROVIDER-THINKLEN).
 */
export interface ProviderTierEntry {
  name: string;
  display_name: string;
}

/**
 * Per-provider composer config: each provider has a DIFFERENT set of
 * reasoning-strength (`thinking_modes`) and output-length (`length_modes`)
 * tiers (每个 provider 的档不同), so the composer renders the ladders for
 * the conversation's effective provider from this map.
 */
export interface ProviderConfigEntry {
  thinking_modes: ProviderTierEntry[];
  length_modes: ProviderTierEntry[];
  /** Provider's active default effort tier (admin row > YAML); null/absent = none declared. */
  default_thinking_mode?: string | null;
}

export interface CatalogModelsResponse {
  state?: 'healthy' | 'unknown';
  reason?: string | null;
  models: CatalogModelInfo[];
  allow_per_message_model_override: boolean;
  /**
   * Provider id -> its reasoning-strength + output-length tier tables.
   * Absent/empty for a provider means "no composer tiers to render".
   */
  provider_configs: Record<string, ProviderConfigEntry>;
}

/**
 * Auxiliary OAuth profile summary as returned by GET /api/aux/oauth/profiles
 * (P-COND-1027 M1-05). The encrypted ``api_key`` is never sent to the
 * client — only metadata. ``expires_at`` is optional because non-OAuth
 * providers (e.g. plain API-key keys) have no rotation horizon.
 */
export interface OAuthProfileSummary {
  id: string;
  provider: string;
  created_at: string;
  expires_at?: string | null;
  /**
   * Optional preview of the encrypted api_key (first 4 + last 4 characters
   * of the cleartext, joined by ellipsis e.g. ``sk-a…wxyz``). The full
   * cleartext is NEVER returned by the backend. When the backend omits
   * this field the UI renders "encrypted (****)" as a placeholder. Phase
   * 2 follow-up T3 (P-COND-1027).
   */
  api_key_preview?: string | null;
}

/**
 * Request body for POST /api/aux/oauth/profiles. The ``api_key`` field is
 * encrypted server-side via the M0 master key + per-row nonce; the client
 * MUST send it only once at create time and MUST NOT log or persist it.
 */
export interface OAuthProfileCreatePayload {
  id: string;
  provider: string;
  api_key: string;
  expires_at?: string | null;
}

/**
 * Backend UserResponse schema (admin).
 *
 * Phase 0 dual-key transition (P0-T5A): the admin user endpoints
 * (`GET /api/admin/users`, `POST`, `PUT`, `DELETE`, `/reactivate`) emit ONLY
 * `is_platform_admin`. The optional `is_admin` legacy field is kept on the
 * TypeScript interface so consumers (AdminPage.tsx — T5-B) can use the
 * `is_platform_admin ?? is_admin` fallback during the transition sprint.
 * Phase 0 §0.5 also added 4 nullable identity-profile fields.
 *
 * Phase 2.7 T5-D (P-COND-P2_7-T5D, L14) — dropped the stale `role`,
 * `department`, `manager_username` fields. Their backend `users` columns
 * were dropped in migration `2026_05_22_0007` (P2 — see CLAUDE.md
 * "Permission Model" paragraph): identity is now sourced from
 * `org_members` (per-org scoping via `OrgMember.role` admin/member +
 * `.department` + `.title` + `.manager_user_id`). Downstream consumers
 * in `pages/AdminPage.tsx` still read these via `as any` casts so the
 * compile boundary is unaffected by the drop; they will be cleaned up
 * in a follow-up frontend sprint outside T5-D's scope.
 */
export interface UserResponse {
  id: string;
  username: string;
  email: string | null;
  is_platform_admin: boolean;
  // TODO P2: drop is_admin fallback once backend stops dual-emit
  is_admin?: boolean;
  wecom_userid: string | null;
  created_at: string;
  last_active_at: string | null;
  deactivated_at: string | null;
  display_name?: string | null;
}

/**
 * Token usage statistics for one user in one period.
 */
export interface UserTokenStats {
  user_id: string;
  username: string;
  period: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  cost_usd: number;
}

/**
 * Token usage statistics for one account pair in one period.
 */
export interface AccountTokenStats {
  account_id: string;
  period: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  cost_usd: number;
}

/**
 * Aggregated token usage summary for a period.
 */
export interface TokenUsageSummary {
  period: string;
  stats: UserTokenStats[];
  account_stats: AccountTokenStats[];
  total_cost_usd: number;
  periods_available: string[];
}

/**
 * Per-user token usage row scoped to ONE org (2026-05-30 org 用量 page).
 * Mirrors the FROZEN backend contract for
 * GET /api/v1/orgs/{org_id}/token-usage. Distinct from the platform-wide
 * `UserTokenStats` (which the admin /api/admin/token-usage surface returns).
 */
export interface OrgUserTokenStats {
  user_id: string;
  username: string;
  period: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  cost_usd: number;
}

/**
 * Org-scoped token usage summary for a period. FROZEN backend contract for
 * GET /api/v1/orgs/{org_id}/token-usage (no account_stats — org page lists
 * per-user rows only).
 */
export interface OrgTokenUsageSummary {
  period: string;
  stats: OrgUserTokenStats[];
  total_cost_usd: number;
  periods_available: string[];
  // Phase A (P-COND-D38665CEC4B4) — non-monetary metrics for org users.
  // used_points always present; budget_points / monthly_pct are null when the
  // org has no monthly budget set (unlimited → render "无限 / 未设额度").
  used_points?: number | null;
  budget_points?: number | null;
  monthly_pct?: number | null;
  // Phase B (P-COND-D38665CEC4B4) — daily/weekly tiers for the CURRENT CST day
  // / current CST ISO-week (independent of the `period` query param). Each
  // tier's budget_points / pct are null when that tier's budget is unset
  // (unlimited → render "无限 / 未设额度").
  daily_used_points?: number | null;
  daily_budget_points?: number | null;
  daily_pct?: number | null;
  weekly_used_points?: number | null;
  weekly_budget_points?: number | null;
  weekly_pct?: number | null;
}

/**
 * User creation request.
 *
 * Phase 0 dual-key transition (P0-T5A): backend admin endpoint
 * `POST /api/admin/users` accepts `is_platform_admin` (canonical). The legacy
 * `is_admin` field is kept on the TS interface so AdminPage.tsx (T5-B) can
 * stage its migration; callers should prefer `is_platform_admin`.
 */
interface UserCreateRequest {
  username: string;
  password: string;
  email?: string;
  is_platform_admin?: boolean;
  // TODO P2: drop is_admin once AdminPage.tsx switches to is_platform_admin
  is_admin?: boolean;
  wecom_userid?: string;
}

/**
 * User update request.
 *
 * Phase 0 dual-key transition (P0-T5A): backend admin endpoint
 * `PUT /api/admin/users/{id}` accepts `is_platform_admin` (canonical). See
 * UserCreateRequest for migration notes.
 */
interface UserUpdateRequest {
  username?: string;
  password?: string;
  email?: string;
  is_platform_admin?: boolean;
  // TODO P2: drop is_admin once AdminPage.tsx switches to is_platform_admin
  is_admin?: boolean;
  wecom_userid?: string;
}

/**
 * Backend PermissionResponse schema (admin).
 */
export interface PermissionResponse {
  id: string;
  user_id: string;
  project_id: string;
  permission_tier: string;
  can_select_model: boolean;
  can_access_planning: boolean;
  can_access_documents: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Permission creation request.
 */
interface PermissionCreateRequest {
  user_id: string;
  project_id: string;
  permission_tier: string;
  can_access_planning?: boolean;
  can_access_documents?: boolean;
}

/**
 * Template proposal summary (list view).
 */
export interface TemplateProposalResponse {
  id: string;
  title: string;
  summary: string | null;
  content_type: string;
  file_path: string;
  action: string;
  source_project: string;
  classification: string;
  fast_track: boolean;
  large_change: boolean;
  status: string;
  admin_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * Template proposal detail (includes content fields).
 */
export interface TemplateProposalDetail extends TemplateProposalResponse {
  current_content: string | null;
  proposed_content: string;
  diff_summary: string | null;
  agent_reasoning: string | null;
  challenge_reasoning: string | null;
}

/**
 * Template proposal badge counts.
 */
export interface TemplateProposalStats {
  pending: number;
  approved_today: number;
  rejected_today: number;
  total: number;
}

/**
 * Dispatch proposal summary (list view).
 */
export interface DispatchProposalResponse {
  id: string;
  display_id: string | null;
  project_id: string;
  project_name: string | null;
  conversation_id: string;
  title: string;
  status: string;
  permission_tier: string;
  submitted_by: string;
  submitter_name: string | null;
  reviewer_id: string | null;
  reviewer_name: string | null;
  /** "system" only when an autofix action records an explicit system approver (P-COND-8CA59E83913D); absent on older backends. */
  reviewer_kind?: 'system' | 'human' | 'none' | null;
  reviewer_note: string | null;
  reviewed_at: string | null;
  expires_at: string | null;
  created_at: string;
}

/**
 * Dispatch proposal detail (includes content fields).
 */
export interface DispatchProposalDetail extends DispatchProposalResponse {
  task_text: string;
  proposal_content: string;
  proposal_path: string | null;
  skill: string | null;
  is_long_running: boolean;
  source: string | null;
  can_review?: boolean;
  can_implement?: boolean;
  phase2_result?: string | null;
  rollback_info?: string | null;
  rolled_back_at?: string | null;
  rolled_back_by?: string | null;
}

/**
 * Collab approval summary (list view).
 *
 * Frontend mirror of the backend `CollabApprovalResponse` (Plan Pack
 * Contract Pack — field spelling locked, snake_case). One entity with a
 * `kind` discriminator (`standards_write` today, `cross_dept_process`
 * future) carries collab-mode draft→approval (sensitive deliverables get
 * promoted from `plans/` into the authoritative `standards/` tree only
 * after an org admin/owner approves here). Independent from the dev
 * dispatch approval gate (`DispatchProposalResponse`).
 */
export interface CollabApprovalResponse {
  id: string;
  display_id: string | null;
  org_id: string;
  project_id: string;
  conversation_id: string | null;
  kind: string;
  dept_slug: string | null;
  title: string;
  summary: string | null;
  source_path: string;
  target_path: string;
  status: string;
  submitter_id: string;
  submitter_name: string | null;
  reviewer_id: string | null;
  reviewer_note: string | null;
  reviewed_at: string | null;
  expires_at: string | null;
  created_at: string;
}

/**
 * Collab approval detail (adds the draft body read from `source_path`;
 * null when the workspace file cannot be read).
 */
export interface CollabApprovalDetail extends CollabApprovalResponse {
  draft_content: string | null;
}

/**
 * Full-population status tallies returned by `list_collab_approvals` (Phase 3B).
 * Computed over the caller's whole visible set independent of the status/kind
 * display filter, so the stat cards stay stable across tab switches.
 * `expiring` = pending rows whose `expires_at` is within the next 24h.
 */
export interface CollabApprovalCounts {
  pending: number;
  approved: number;
  rejected: number;
  expiring: number;
}

/**
 * Dispatch proposal badge counts.
 */
export interface DispatchProposalStats {
  pending: number;
  approved: number;
  rejected: number;
  implementing: number;
  completed: number;
  failed: number;
  cancelled: number;
  expired: number;
  superseded: number;
}

/**
 * Dispatch outcome statistics for the observability center Tab1 (Q1)
 * (P-COND-3BC2D7C95FC7 · ST-C).
 *
 * Extends the per-status proposal counts with `failure_causes` — a bucketed
 * breakdown of `status='failed'` dispatches derived server-side by running
 * `phase2_result` through `_classify_error` (ST-B; no DB column added). The
 * keys are error-category codes (e.g. auth / timeout / container-gone / budget
 * / crash / other); the UI renders friendly labels but tolerates any key.
 *
 * Mirrors `backend/app/api/dispatch_proposals.py::get_stats` (ST-B contract).
 */
export interface DispatchStatsResponse {
  pending: number;
  approved: number;
  rejected: number;
  implementing: number;
  completed: number;
  failed: number;
  cancelled: number;
  expired: number;
  superseded: number;
  /** Bucketed failure-cause counts (category code -> count). */
  failure_causes: Record<string, number>;
}

/**
 * Paginated response wrapper for dispatch proposal lists.
 */
export interface PaginatedProposalResponse {
  items: DispatchProposalResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * Group chat member details.
 */
export interface ConversationMember {
  id: string;
  user_id: string;
  username: string;
  role: 'owner' | 'member';
  agent_mode: boolean;
  created_at: string;
}

/**
 * Request body for generating a document attachment.
 */
export interface GenerateAttachmentRequest {
  format: 'pdf' | 'docx' | 'xlsx' | 'pptx';
  title: string;
  content_html?: string;
  rows?: Record<string, string>[];
  slides?: Record<string, string>[];
  headers?: string[];
  template_id?: string;
}

/**
 * Response for a single attachment.
 */
export interface AttachmentResponse {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
}

/**
 * Export template response schema.
 */
export interface ExportTemplateResponse {
  id: string;
  name: string;
  logo_base64?: string | null;
  header_text?: string | null;
  footer_text?: string | null;
  primary_color?: string | null;
  font_name?: string | null;
  is_default: boolean;
  created_by?: string | null;
  created_at: string;
}

/**
 * Export template list response.
 */
export interface ExportTemplateListResponse {
  items: ExportTemplateResponse[];
  total: number;
}

/**
 * Request body for creating an export template.
 */
export interface ExportTemplateCreateRequest {
  name: string;
  logo_base64?: string | null;
  header_text?: string | null;
  footer_text?: string | null;
  primary_color?: string | null;
  font_name?: string | null;
}

/**
 * Request body for updating an export template (partial).
 */
export interface ExportTemplateUpdateRequest {
  name?: string;
  logo_base64?: string | null;
  header_text?: string | null;
  footer_text?: string | null;
  primary_color?: string | null;
  font_name?: string | null;
}

/**
 * Server metrics from health monitoring.
 */
export interface ServerMetrics {
  host: string;
  hostname: string;
  cpu_percent: number | null;
  memory_percent: number | null;
  memory_used_mb: number | null;
  memory_total_mb: number | null;
  disk_percent: number | null;
  disk_used_gb: number | null;
  disk_total_gb: number | null;
  load_average: string | null;
  uptime: string | null;
  status: string;
  error: string | null;
}

/**
 * Docker container info from health monitoring.
 */
export interface DockerContainer {
  host: string;
  name: string;
  image: string;
  status: string;
  running_for: string | null;
  ports: string | null;
}

/**
 * Per-project health details.
 */
export interface ProjectHealthDetail {
  name: string;
  access_method: string;
  connectivity: string;
  claude_cli: string | null;
  last_activity: string | null;
  active_tasks: number;
  status: string;
  error: string | null;
}

/**
 * Communication channel health.
 */
export interface ChannelHealth {
  name: string;
  status: string;
  details: string | null;
  response_ms: number | null;
}

/**
 * Aggregated health dashboard response.
 */
export interface HealthDashboard {
  servers: ServerMetrics[];
  docker: DockerContainer[];
  projects: ProjectHealthDetail[];
  channels: ChannelHealth[];
  collected_at: string;
  cache_age_seconds: number;
}

/**
 * Token host status from admin token-status endpoint.
 */
export interface TokenHostStatus {
  host: string;
  status: 'ok' | 'expiring_soon' | 'expired' | 'error' | 'permanent_failure';
  ttl_hours: number | null;
  has_refresh_token: boolean;
  /**
   * False on REMOTE rows (ST①): Conductor strips claudeAiOauth.refreshToken
   * from the credentials it pushes outward, so "absent" there is BY DESIGN and
   * must render as a neutral N/A, not a red "No". True (default) on local rows,
   * where a missing refreshToken is a real fault.
   */
  refresh_token_expected?: boolean;
  distribution_policy?: 'allowed' | 'not_applicable' | 'conflict';
  message: string | null;
}

/**
 * Response from GET /api/admin/token-status.
 */
export interface TokenStatusResponse {
  hosts: TokenHostStatus[];
}

/**
 * Per-host result from token refresh operation.
 */
export interface TokenRefreshResult {
  host: string;
  status: 'ok' | 'failed' | 'permanent_failure' | 'skipped';
  message: string | null;
}

/**
 * Response from POST /api/admin/token-refresh.
 */
export interface TokenRefreshResponse {
  results: TokenRefreshResult[];
  summary: { success: number; failed: number; total: number };
}

/**
 * Static pre-check / post-verify summary of a credential file.
 *
 * SECRETS CONTRACT: this carries field LENGTHS and the expiry only -- the
 * backend never returns an accessToken/refreshToken value, so nothing here
 * may ever be widened to hold one.
 */
export interface CredentialPrecheck {
  field_lengths: Record<string, number>;
  expires_at: number | null;
  expires_at_iso: string | null;
  ttl_hours: number | null;
  subscription_type: string | null;
  file_mode?: string | null;
}

/**
 * Fleet fan-out outcome for one rotation.
 */
export interface CredentialFleetResult {
  skipped?: 'not_exportable' | null;
  hosts: number;
  pushed: number;
  failed: number;
  /**
   * Windows-API arm. Reported separately from the SSH arm and never summed into
   * it: one OAuth grant has N physical copies, so the operator has to see WHICH
   * arm is out of sync. Optional because a backend older than
   * P-COND-85676906AB92 ST2 omits them -- read via `?? 0`, never assume presence.
   */
  windows_hosts?: number;
  windows_pushed?: number;
  windows_failed?: number;
  failed_labels: string[];
}

/**
 * Response from the two credential-rotation admin endpoints.
 *
 * Neither endpoint contacts the provider's OAuth token endpoint -- validation
 * is static. The canonical refresh path stays POST /api/admin/token-refresh.
 */
export interface CredentialRotationResponse {
  pair_label: string;
  ok: boolean;
  precheck: CredentialPrecheck | null;
  verify: CredentialPrecheck | null;
  fleet: CredentialFleetResult;
  backup_path: string | null;
  installed_path: string | null;
  redis_actions: string[];
  errors: string[];
  note: string;
}

/**
 * Response from GET/PUT /api/admin/pair-config.
 */
export interface PairConfigResponse {
  weight_a: number;
  weight_b: number;
  weight_c: number;
  weight_d: number;
  weight_e: number;
  /** Optional: a backend older than the f..j widening omits the newer letters. */
  weight_f?: number;
  weight_g?: number;
  weight_h?: number;
  weight_i?: number;
  weight_j?: number;
  /**
   * Letters retired from routing (kept in the vocabulary, never selectable).
   * Optional: an older backend omits it -> the UI degrades to an unfiltered view.
   */
  retired_letters?: string[];
  /**
   * TEMPORARY switch: local projects (conductor) prefer the egress pair F.
   * Optional: an older backend omits it -> the UI hides the switch.
   */
  local_prefers_egress?: boolean;
}

/** One org / project scope left with no allowed alternate account. */
export interface PairRetireScope {
  org_id: string | null;
  /** null = the org-level scope. */
  project_id: string | null;
  reason: string;
}

/** GET /api/admin/pair-retire/preview (read-only). */
export interface PairRetirePreviewResponse {
  pair_label: string;
  already_retired: boolean;
  /** The ONLY admission rule: false -> the retire call returns 409. */
  fleet_usable_after: boolean;
  usable_after: string[];
  /** Report-only, never blocks. */
  windows_usable_after: boolean;
  affected_conversations: number;
  unmigratable_scopes: PairRetireScope[];
}

export type PairRetireArchiveStatus = 'complete' | 'partial' | 'skipped';

/** POST /api/admin/pair-retire. */
export interface PairRetireResponse {
  pair_label: string;
  already_retired: boolean;
  retired: boolean;
  retired_letters: string[];
  archive: {
    status: PairRetireArchiveStatus;
    archived_files: string[];
    pending_files: Array<{ file: string; reason: string }>;
    error: string | null;
  };
  affected_conversations: number;
  unmigratable_scopes: PairRetireScope[];
  /** Host segments of token push records — copies this host cannot remove. */
  remote_copy_candidates: string[];
  upstream_logout_required: boolean;
  windows_usable_after: boolean;
}


/**
 * Rate-limit cooldown status for a single account pair.
 */
export interface PairCooldownEntry {
  label: string;
  rate_limited: boolean;
  cooldown_class: 'usage_exhausted' | 'rolling' | null;
  cooldown_remaining_seconds: number | null;
  valid: boolean;
  weight: number;
}

/**
 * Response from GET /api/admin/pair-cooldown.
 */
export interface PairCooldownResponse {
  pair_a: PairCooldownEntry;
  pair_b: PairCooldownEntry;
  pair_c: PairCooldownEntry;
  pair_d: PairCooldownEntry;
  pair_e: PairCooldownEntry;
  /** Optional: absent on a backend older than the f..j widening. */
  pair_f?: PairCooldownEntry;
  pair_g?: PairCooldownEntry;
  pair_h?: PairCooldownEntry;
  pair_i?: PairCooldownEntry;
  pair_j?: PairCooldownEntry;
  /** Optional: absent on an older backend -> no filtering. */
  retired_letters?: string[];
}

/**
 * A single file in a conversation's planning folder.
 */
export interface PlanningFileItem {
  name: string;
  ext: string;
}

/**
 * Response from the planning files list endpoint.
 */
export interface PlanningFileListResponse {
  items: PlanningFileItem[];
  has_planning: boolean;
}

/**
 * A file or directory entry from a project folder listing.
 */
/** One sheet of a table preview (backend `sheet_preview.py::build_sheet_preview`).
 *  `rows[0]` is the header; all cells are strings; totals are pre-truncation. */
export interface SheetPreview {
  name: string;
  rows: string[][];
  total_rows: number;
  total_cols: number;
  truncated: boolean;
}

export interface SheetPreviewResponse {
  sheets: SheetPreview[];
}

export interface FolderItem {
  name: string;
  is_dir: boolean;
  ext: string;
  /** Last-modified time, ISO 8601 +08:00 (backend `to_cst_iso`); null/absent when the host cannot report it. */
  modified_at?: string | null;
}

/**
 * Response from the project-folder list endpoint.
 */
export interface FolderListResponse {
  items: FolderItem[];
  folder: string;
  current_path: string;
  has_folder: boolean;
  has_planning_md: boolean;
  /** 'unreachable' when the project host listing failed (not an empty folder). */
  error?: 'unreachable' | null;
  /** domains root only: viewer's dept dir slug (backend `_dept_dir_slug`); null = no department. Display-only. */
  viewer_dept_slug?: string | null;
  /** domains root only: viewer's raw department label; null = no department. */
  viewer_dept?: string | null;
}

/**
 * One persisted Phase 1 DESIGN_BLOCK file of a conversation, from
 * GET /api/conversations/{id}/design-files (design_preview.py::DesignFileItem).
 * `did` + `slug` + `ext` are the session-preview locators; no host path.
 */
export interface ConversationDesignFile {
  did: string;
  slug: string;
  ext: string;
  kind: string;
  /** ISO 8601 with +08:00 offset (to_cst_iso). */
  modified_at: string;
}

/** Paged attachment listing that keeps the backend `total`. */
export interface ConversationAttachmentsPage {
  items: AttachmentInfo[];
  total: number;
}

/**
 * One Conductor Desktop installer's metadata.
 *
 * Field names are transcribed VERBATIM from
 * `backend/app/api/desktop_downloads.py::DesktopReleaseManifest` — they are a
 * cross-layer contract, not a local naming choice. A snake_case key misspelled
 * here yields `undefined` in TypeScript with zero exception and zero log
 * (bug-patterns #711), so the page would silently render an empty card rather
 * than fail.
 */
export interface DesktopReleaseManifest {
  /** `"windows"` | `"macos"` — the only two values the backend emits. */
  platform: string;
  /** Exactly the on-disk filename; also the `{filename}` path segment. */
  filename: string;
  /** 0 when `available` is false. */
  size_bytes: number;
  /** Lowercase hex, 64 chars; `""` when `available` is false. */
  sha256: string;
  /** Shipped client version (`ws_client.py::DEFAULT_APP_VERSION`). */
  version: string;
  /** Whether the installer is actually on disk right now. */
  available: boolean;
}

/**
 * Envelope of `GET /api/v1/desktop/downloads/manifest`
 * (`desktop_downloads.py::DesktopDownloadsManifest`).
 */
export interface DesktopDownloadsManifest {
  items: DesktopReleaseManifest[];
  version: string;
}

/**
 * API configuration.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_TIMEOUT = 30000;

/**
 * Per-request timeout for feat-branch creation (ms). The backend worst case is
 * the 60 s best-effort backup push plus the 60 s `git worktree add`, so the
 * client-wide {@link API_TIMEOUT} (30 s) would abort a request the server is
 * still completing (bug-patterns #989).
 */
const FEAT_BRANCH_CREATE_TIMEOUT = 150000;

/**
 * Per-request timeout for installer downloads (ms).
 *
 * The client-wide {@link API_TIMEOUT} is 30 s — fine for JSON, fatal for a
 * ~60 MB installer on a slow link: axios would abort a perfectly healthy
 * transfer mid-stream and surface it as a generic network error. 15 minutes is
 * a deliberate ceiling rather than `0` (no timeout) so a genuinely dead socket
 * still terminates instead of hanging the button forever.
 */
const DESKTOP_DOWNLOAD_TIMEOUT = 15 * 60 * 1000;

/**
 * Idle (no-bytes) watchdog for the installer download. The 15-minute ceiling
 * above only catches a dead socket after a quarter of an hour; a stall shows
 * up as "no progress event for this long", so the download page aborts via
 * the AbortSignal it passes to `downloadDesktopRelease` and tells the user the
 * transfer stalled instead of leaving the button spinning.
 */
export const DESKTOP_DOWNLOAD_IDLE_TIMEOUT = 60 * 1000;

/** Progress snapshot for the installer download; `total` is 0 when unknown. */
export interface DesktopDownloadProgress {
  loaded: number;
  total: number;
}

export interface DesktopDownloadOptions {
  onProgress?: (progress: DesktopDownloadProgress) => void;
  signal?: AbortSignal;
  /**
   * Name the browser SAVES the file under (v1.0.6 download-source tag, e.g.
   * `ConductorDesktop-Setup-1.0.6_myconductor.net.exe`). Only `a.download`
   * uses it — the request still names the whitelist `filename` verbatim.
   */
  saveAs?: string;
}

/**
 * Axios-based API client for the Conductor backend.
 * All methods return the backend response data directly (no wrapper).
 */
export interface CodexSlot {
  id: string; label: string; project_id: string | null; status: string; managed: boolean; accepted?: boolean;
  last_model_success_at: string | null; last_operation_result: string | null;
  ready?: boolean; running?: number;
}
export interface CodexAccountStatus {
  id: string; label: string; enabled: boolean; weight: number; state_version: number;
  cooldown_until: string | null; cooling: boolean; free_slots: number; running: number; slots: CodexSlot[];
  available_logins?: number; active_executions?: number;
}
export type CodexPoolScope = 'organization' | 'platform';
export interface CodexSnapshot {
  scope?: CodexPoolScope;
  concurrency_mode?: 'shared_account' | 'exclusive_runner';
  execution_environments?: { local: 'enabled' | 'not_enabled'; remote: 'enabled' | 'not_enabled' | 'unsupported'; runtime_ready: boolean } | null;
  org_id: string | null; organizations: { id: string; name: string }[];
  version: number; max_weight: number; enabled: boolean; read_at: string; accounts: CodexAccountStatus[];
}
export interface CodexOperation { id: string; status: string; result: string | null; }
export type CodexRoute = 'legacy' | 'managed';
export type CodexPurpose = 'research' | 'planning_save' | 'approved_dev';
export interface CodexProjectRecentRun {
  id: string; status: string | null; outcome: string | null; target_kind: 'local' | 'remote' | null;
  resource_phase: 'intended' | 'ready' | null; receipt_status: string | null; settled_at: string | null;
}
export interface CodexProjectOverview {
  project_id: string; name: string; configured: boolean; route_configured: CodexRoute | null;
  route_resolved: CodexRoute | null; route_reason: string | null; defaulted: boolean;
  policy_version: number | null; approved_purposes: CodexPurpose[]; updated_at: string | null;
  admission: { waiting: number; active: number; oldest_waiting_at: string | null };
  recent_runs: CodexProjectRecentRun[];
}
export interface CodexProjectsOverview {
  scope: CodexPoolScope; org_id: string | null; read_at: string;
  purposes: CodexPurpose[]; routes: CodexRoute[]; projects: CodexProjectOverview[];
}
/** CP-G-2 (E-4): GET /api/admin/codex/runs/records — applied task-container limits and
 *  account-switch records per run. Timestamps are +08:00 (TZ:AWARE-UTC); null = not recorded. */
export type CodexRunRecordKind = 'all' | 'resource' | 'switch';
export interface CodexResourceProfile {
  profile_id: string | null; memory: string | null; cpus: string | null; pids: number | null;
  home_tmpfs: string | null; tmp_tmpfs: string | null; codex_tmp_tmpfs: string | null;
  skills_tmpfs: string | null; codex_home_tmpfs: string | null;
}
export interface CodexAccountSwitch {
  from_account_id: string | null; to_account_id: string | null; from_run_id: string | null;
  stage: string | null; reason: string | null; basis: string | null;
  from_account_label: string | null; to_account_label: string | null;
}
export interface CodexRunRecord {
  run_id: string; conversation_id: string; project_id: string; project_name: string;
  account_id: string; account_label: string | null; status: string | null; outcome: string | null;
  created_at: string | null; settled_at: string | null;
  resource_profile: CodexResourceProfile | null; account_switch: CodexAccountSwitch | null;
}
export interface CodexRunRecordsPage {
  scope: CodexPoolScope; org_id: string | null; kind: CodexRunRecordKind;
  total: number; limit: number; offset: number; items: CodexRunRecord[];
}
export interface CodexRunRecordsQuery {
  org_id?: string; scope?: CodexPoolScope; kind?: CodexRunRecordKind;
  conversation_id?: string; account_id?: string; limit?: number; offset?: number;
}
export interface CodexProjectPolicy {
  project_id: string; configured: boolean; route: CodexRoute | null; policy_version: number | null;
  approved_purposes: CodexPurpose[];
}
/** PUT body: an omitted key is UNCHANGED; `route: null` clears to not-configured.
 *  `expected_policy_version` is REQUIRED (CP-A-5 optimistic concurrency): the version the
 *  editor loaded, 0 when the project had no policy row (GET `policy_version: null`);
 *  a mismatch is a 409 with reason `codex_admission.policy_version_conflict`. */
export interface CodexProjectPolicyUpdate {
  route?: CodexRoute | null; approved_purposes?: CodexPurpose[]; expected_policy_version: number;
}
export interface CodexProjectPolicyWriteResult {
  project_id: string; route: CodexRoute | null; policy_version: number | null;
  approved_purposes: CodexPurpose[]; created: boolean; changed: boolean;
}

/** Body source for `renderHtmlSnapshot`: full html and/or its sha256 (hex), plus image format. */
export interface HtmlSnapshotSource {
  html?: string;
  htmlSha256?: string;
  format?: 'png' | 'jpeg';
}

/** Blob -> text; jsdom lacks `Blob.text()`, so fall back to FileReader. */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Download egress policy (P-COND-978F5A84A23F): a 403 on a `responseType:
 * 'blob' | 'text'` request carries its JSON `{detail}` as a Blob / string.
 * Parse it in place so every caller reads `error.response.data.detail` the
 * same way, and make `error.message` the readable server text. Unparsable
 * bodies are left untouched. The frontend never decides policy -- it only
 * relays the server's message.
 */
export async function normalizeForbiddenBody(
  error: { response?: { status?: number; data?: unknown }; message?: string },
): Promise<void> {
  const res = error.response;
  if (!res || res.status !== 403) return;
  let raw: unknown = res.data;
  if (!(typeof Blob !== 'undefined' && raw instanceof Blob) && typeof raw !== 'string') return;
  try {
    if (typeof raw !== 'string') raw = await readBlobText(raw as Blob);
    raw = JSON.parse(raw as string);
  } catch {
    return;
  }
  if (!raw || typeof raw !== 'object') return;
  res.data = raw;
  const detail = (raw as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail) error.message = detail;
}

class ApiClient {
  private client: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to attach auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (this.authToken && config.headers) {
          config.headers['Authorization'] = `Bearer ${this.authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for 401 handling (+ 403 blob/text body -> JSON, egress policy)
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        await normalizeForbiddenBody(error);
        if (error.response?.status === 401) {
          this.clearAuth();
          const basePath = process.env.PUBLIC_URL || '';
          const loginPath = `${basePath}/login`;
          if (typeof window !== 'undefined' && window.location.pathname !== loginPath) {
            window.location.href = loginPath;
          }
        }
        return Promise.reject(error);
      }
    );

    // Load saved token from localStorage
    this.loadSavedToken();
  }

  // ==================== Authentication ====================

  /**
   * Logs in with username and password credentials.
   * Backend returns: {access_token, token_type, user_id}
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/api/auth/login', credentials);
    this.setAuth(response.data.access_token);
    return response.data;
  }

  /**
   * Logs out the current user.
   * Backend returns: {message}
   */
  async logout(): Promise<void> {
    try {
      await this.client.post('/api/auth/logout');
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Fetches the current user's profile from the database.
   * Used to validate isAdmin status on mount (prevents localStorage spoofing).
   *
   * Phase 0 dual-key transition (P0-T5A): backend `/auth/me` emits BOTH
   * `is_platform_admin` and legacy `is_admin`; consumers should read via
   * `is_platform_admin ?? is_admin`.
   * TODO P2: drop is_admin from this return shape once backend stops dual-emit.
   */
  async getMe(): Promise<{ user_id: string; username: string; display_name?: string; email?: string; phone?: string; wecom_userid?: string; is_platform_admin: boolean; is_admin?: boolean }> {
    const response = await this.client.get<{ user_id: string; username: string; display_name?: string; email?: string; phone?: string; wecom_userid?: string; is_platform_admin: boolean; is_admin?: boolean }>('/api/auth/me');
    return response.data;
  }

  /**
   * Updates the authenticated user's own profile fields (partial PATCH).
   * Only the keys present in `payload` are applied server-side.
   * Backend: PATCH /api/auth/me → MeResponse shape.
   */
  async updateMe(payload: { display_name?: string; email?: string; phone?: string }): Promise<{ user_id: string; username: string; display_name?: string; email?: string; phone?: string; wecom_userid?: string; is_platform_admin: boolean; is_admin?: boolean }> {
    const response = await this.client.patch<{ user_id: string; username: string; display_name?: string; email?: string; phone?: string; wecom_userid?: string; is_platform_admin: boolean; is_admin?: boolean }>('/api/auth/me', payload);
    return response.data;
  }

  /**
   * Requests a one-time verification code for a NEW email/phone the user wants
   * to set on their own profile (P-COND-6522244D843C). `channel` is 'email' or
   * 'sms'. Backend: POST /api/auth/me/request-contact-otp → 204 (409 taken,
   * 429 throttled). Delivery degrades to log-only if no provider is configured.
   */
  async requestContactOtp(channel: 'email' | 'sms', newContact: string): Promise<void> {
    await this.client.post('/api/auth/me/request-contact-otp', { channel, new_contact: newContact });
  }

  /**
   * Verifies the OTP and commits the new email/phone to the user's profile.
   * Backend: POST /api/auth/me/verify-contact → MeResponse (400 bad/expired code).
   */
  async verifyContact(channel: 'email' | 'sms', newContact: string, code: string): Promise<{ user_id: string; username: string; display_name?: string; email?: string; phone?: string; wecom_userid?: string; is_platform_admin: boolean; is_admin?: boolean }> {
    const response = await this.client.post<{ user_id: string; username: string; display_name?: string; email?: string; phone?: string; wecom_userid?: string; is_platform_admin: boolean; is_admin?: boolean }>('/api/auth/me/verify-contact', { channel, new_contact: newContact, code });
    return response.data;
  }

  /**
   * Changes the authenticated user's own password (requires current password).
   * On success the backend revokes the user's OTHER sessions but keeps this one.
   * Backend: POST /api/auth/me/password → { message }.
   */
  async changePassword(payload: { current_password: string; new_password: string }): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/api/auth/me/password', payload);
    return response.data;
  }

  /**
   * Forgot-password (email channel): requests a reset link be emailed.
   * PUBLIC, enumeration-safe — the backend always returns a generic message.
   * Backend: POST /api/auth/forgot-password → { message }.
   */
  async forgotPasswordEmail(payload: { email: string }): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/api/auth/forgot-password', payload);
    return response.data;
  }

  /**
   * Reset password using the single-use token from the email link.
   * Backend: POST /api/auth/reset-password → { message }.
   */
  async resetPasswordWithToken(payload: { token: string; new_password: string }): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/api/auth/reset-password', payload);
    return response.data;
  }

  /**
   * Forgot-password (phone channel): sends an SMS OTP for password reset.
   * PUBLIC, enumeration-safe — the backend always returns a generic message.
   * Backend: POST /api/auth/forgot-password-phone → { message }.
   */
  async forgotPasswordPhoneSendOtp(payload: { phone: string }): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/api/auth/forgot-password-phone', payload);
    return response.data;
  }

  /**
   * Reset password using a phone + SMS OTP + new password.
   * Backend: POST /api/auth/reset-password-phone → { message }.
   */
  async resetPasswordWithOtp(payload: { phone: string; code: string; new_password: string }): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/api/auth/reset-password-phone', payload);
    return response.data;
  }

  /**
   * Fetches the WeCom QR code URL for SSO login.
   * Backend returns: {qr_url, state}
   */
  async getWeComQR(): Promise<{ qr_url: string; state: string }> {
    const response = await this.client.get<{ qr_url: string; state: string }>('/api/auth/wecom/qr');
    return response.data;
  }

  // ==================== Conversations ====================

  /**
   * Retrieves conversations for the current user with pagination support.
   * Backend returns: ConversationSummary[]
   * Uses LIMIT+1 pattern: caller requests limit+1, checks length to determine hasMore.
   */
  async getConversations(params?: { limit?: number; offset?: number; favorite?: boolean; org_id?: string; project_id?: string; q?: string; is_active?: boolean; branched_from?: string }): Promise<ConversationSummaryResponse[]> {
    const queryParams = new URLSearchParams();
    queryParams.set('limit', String(params?.limit ?? 51));
    if (params?.offset !== undefined && params.offset > 0) {
      queryParams.set('offset', String(params.offset));
    }
    // Server-side search (会话ID/分支/标题/内容): non-empty q pushes the match
    // down to the DB so results past the pagination window and beyond the
    // 100-char preview surface. Omitted/empty → existing pagination path.
    if (params?.q && params.q.trim()) {
      queryParams.set('q', params.q.trim());
    }
    // Server-side favorite filter: surfaces favorited convs past list pagination.
    if (params?.favorite) {
      queryParams.set('favorite', 'true');
    }
    // Server-side org scope: keeps the paginated list aligned with the selected
    // org. Omitted (undefined) → cross-org view (platform-admin god-mode).
    if (params?.org_id) {
      queryParams.set('org_id', params.org_id);
    }
    // Server-side project scope: keeps the paginated list aligned with the
    // selected project (二级栏目). Omitted → org-scoped (or cross-org) view.
    if (params?.project_id) {
      queryParams.set('project_id', params.project_id);
    }
    // Server-side active/archived filter: the list is paginated over a
    // recency-ordered pool where archived convs dominate, so without this
    // filter a project's active convs get starved past the loaded window
    // (a client-side filter then discards the archived ones, leaving few).
    if (params?.is_active !== undefined) {
      queryParams.set('is_active', String(params.is_active));
    }
    // Branches of one source conversation (划词「发问」entry, P-COND-A0898459F4F0).
    // Callers still filter by `branched_from_conversation_id` client-side.
    if (params?.branched_from) {
      queryParams.set('branched_from', params.branched_from);
    }
    const response = await this.client.get<ConversationSummaryResponse[]>(
      `/api/conversations/?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * Retrieves a specific conversation with its messages.
   * Supports pagination via optional params:
   *   limit: max messages to return (default 0 = all)
   *   before: cursor — return messages older than this ID
   *   after: cursor — return messages newer than this ID
   * Backend returns: ConversationDetail with has_more and oldest_message_id
   *
   * ``options.signal`` is optional and purely additive: a caller that switches
   * conversations rapidly aborts the superseded detail request instead of
   * letting N fetches pile up. Axios rejects an aborted request with a
   * CanceledError — callers must treat it as a cancel, not a failure.
   */
  async getConversation(
    conversationId: string,
    params?: { limit?: number; before?: string; after?: string },
    options?: { signal?: AbortSignal }
  ): Promise<ConversationDetailResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.set('limit', String(params.limit));
    if (params?.before) queryParams.set('before', params.before);
    if (params?.after) queryParams.set('after', params.after);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.client.get<ConversationDetailResponse>(
      `/api/conversations/${conversationId}${query}`,
      options?.signal ? { signal: options.signal } : undefined
    );
    return response.data;
  }

  /**
   * Creates a new conversation.
   * Backend returns: ConversationDetail
   */
  async createConversation(data: CreateConversationRequest): Promise<ConversationDetailResponse> {
    const response = await this.client.post<ConversationDetailResponse>('/api/conversations/', data);
    return response.data;
  }

  /**
   * Server-side branch context of a branch conversation (CS-T9, §3.4).
   * Backend route: GET /api/conversations/{id}/branch-context.
   * 404 = no snapshot (branch created before CS-T1, or snapshot write failed);
   * 403 = no access / not the snapshot's creator. Callers handle both.
   */
  async getBranchContext(conversationId: string): Promise<BranchContext> {
    const response = await this.client.get<BranchContext>(
      `/api/conversations/${conversationId}/branch-context`
    );
    return response.data;
  }

  /**
   * Stamp `consumed_at` after the seed rode on the branch's first send
   * (idempotent server-side). Callers fire-and-forget and ignore errors.
   * Backend route: POST /api/conversations/{id}/branch-context/consume.
   */
  async consumeBranchContext(conversationId: string): Promise<BranchContext> {
    const response = await this.client.post<BranchContext>(
      `/api/conversations/${conversationId}/branch-context/consume`
    );
    return response.data;
  }

  /**
   * Mark a conversation as read (updates last_read_at on backend).
   */
  async markConversationRead(conversationId: string): Promise<void> {
    await this.client.put(`/api/conversations/${conversationId}/read`);
  }

  /**
   * Archives a conversation (sets is_active to false).
   * Backend returns: ConversationDetail
   */
  async archiveConversation(conversationId: string): Promise<ConversationDetailResponse> {
    const response = await this.client.patch<ConversationDetailResponse>(
      `/api/conversations/${conversationId}/archive`
    );
    return response.data;
  }

  /**
   * Unarchives a conversation (sets is_active back to true).
   * Backend returns: {status: "unarchived"}
   */
  async unarchiveConversation(conversationId: string): Promise<{ status: string }> {
    const response = await this.client.patch<{ status: string }>(
      `/api/conversations/${conversationId}/unarchive`
    );
    return response.data;
  }

  /**
   * Clears a poisoned agent session so dispatching can resume (AISS-240 ST2).
   *
   * The recovery exit for the per-conversation same-error dispatch breaker:
   * drops `conversations.claude_session_id` (the CLI --resume token, which is
   * the actual poison) plus the breaker's Redis state. Chat history is NOT
   * touched. Backend returns 409 while an agent is running.
   */
  async resetConversationSession(
    conversationId: string
  ): Promise<{ status: string; cleared_session: boolean; cleared_breaker: boolean }> {
    const response = await this.client.post<{
      status: string;
      cleared_session: boolean;
      cleared_breaker: boolean;
    }>(`/api/conversations/${conversationId}/reset-session`);
    return response.data;
  }

  /**
   * Sets a conversation's favorited (heart) state.
   * Backend returns: {status: "ok", is_favorite: boolean}
   */
  async toggleFavorite(conversationId: string, isFavorite: boolean): Promise<{ status: string; is_favorite: boolean }> {
    const response = await this.client.patch<{ status: string; is_favorite: boolean }>(
      `/api/conversations/${conversationId}/favorite`,
      { is_favorite: isFavorite }
    );
    return response.data;
  }

  /**
   * Reads a conversation's remembered composer settings (model / supervisor /
   * long). Backed by an ephemeral Redis cache (90-day TTL); returns defaults
   * when nothing is stored or Redis is unavailable.
   */
  async getComposerPrefs(conversationId: string): Promise<ComposerPrefs> {
    const response = await this.client.get<ComposerPrefs>(
      `/api/conversations/${conversationId}/composer-prefs`
    );
    return response.data;
  }

  /**
   * Merges a partial update into a conversation's composer settings. Only
   * provided fields change; returns the merged result.
   */
  async updateComposerPrefs(
    conversationId: string,
    data: {
      model?: string | null;
      supervisor?: boolean;
      long?: boolean;
      thinking_effort?: string | null;
      thinking_enabled?: boolean;
      thinking_length?: string | null;
      dispatch_mode?: 'planning' | 'coding' | null;
    }
  ): Promise<ComposerPrefs> {
    const response = await this.client.patch<ComposerPrefs>(
      `/api/conversations/${conversationId}/composer-prefs`,
      data
    );
    return response.data;
  }

  /**
   * Queue a mid-run supplement for the agent currently executing this
   * conversation (native_loop only). The backend parks the text in Redis and
   * the AgentLoop consumes it at its next STEP 7b boundary, so the running
   * task keeps going instead of being stopped just to add a missing detail.
   *
   * `queued: false` means the backend accepted the call but did not park
   * anything (e.g. no live run) — callers should treat it as "not delivered".
   */
  async postSupplement(
    conversationId: string,
    content: string,
    identity?: { expected_run_id: string; client_request_id: string },
  ): Promise<{ status: string; queued: boolean; supplement_id: string; delivery?: SupplementDeliveryDTO }> {
    const response = await this.client.post<{
      status: string;
      queued: boolean;
      supplement_id: string;
      delivery?: SupplementDeliveryDTO;
    }>(
      `/api/conversations/${conversationId}/supplement`,
      { content, ...identity },
    );
    return response.data;
  }

  /**
   * Read the delivery state of the mid-run supplement slot for a conversation.
   *
   * Companion GET to `postSupplement`: the POST only proves the text was PARKED,
   * not that the agent absorbed it. The UI polls this while a supplement is
   * outstanding so the user can see 待受理 flip to 已受理.
   *
   * Both fields are `null` (never `[]`, never omitted) when absent; `at` is an
   * ISO-8601 string carrying the +08:00 offset.
   */
  async getSupplementState(
    conversationId: string,
    runId?: string,
  ): Promise<SupplementStateDTO> {
    const response = await this.client.get<SupplementStateDTO>(
      `/api/conversations/${conversationId}/supplement`, { params: runId ? { run_id: runId } : undefined });
    return response.data;
  }

  async resolveSupplementRecovery(conversationId: string, runId: string, action: 'end' | 'continue') {
    return (await this.client.post(`/api/conversations/${conversationId}/supplement/resolve`,
      { run_id: runId, action })).data;
  }

  /**
   * Permanently deletes a conversation and all its messages.
   * Backend returns: {status: "deleted"}
   */
  async deleteConversation(conversationId: string): Promise<{ status: string }> {
    const response = await this.client.delete<{ status: string }>(
      `/api/conversations/${conversationId}`
    );
    return response.data;
  }

  /**
   * Personal relay: permanently delete ONE message (and its attachments).
   * Backend returns: {deleted_ids: [...]} — idempotent; owner only.
   */
  async deleteRelayMessage(conversationId: string, messageId: string): Promise<{ deleted_ids: string[] }> {
    const response = await this.client.delete<{ deleted_ids: string[] }>(
      `/api/conversations/${conversationId}/relay/messages/${messageId}`
    );
    return response.data;
  }

  /**
   * Personal relay: permanently delete ALL messages (and attachments).
   * Backend returns: {deleted_ids: [...]}; owner only.
   */
  async clearRelay(conversationId: string): Promise<{ deleted_ids: string[] }> {
    const response = await this.client.delete<{ deleted_ids: string[] }>(
      `/api/conversations/${conversationId}/relay/messages`
    );
    return response.data;
  }

  /**
   * Submit user answer to a mid-dispatch question panel (P-COND-7B68C2954CBF).
   * Called by MidDispatchQuestionPanel on confirm click.
   */
  async answerMidDispatchQuestion(
    conversationId: string,
    questionId: string,
    chosenLabel: string,
  ): Promise<{ ok: boolean }> {
    const response = await this.client.post<{ ok: boolean }>(
      `/api/conversations/${conversationId}/answer-question`,
      { question_id: questionId, label: chosenLabel },
    );
    return response.data;
  }

  /**
   * Updates the theme of a conversation.
   * Backend returns: {status, theme}
   */
  async updateConversationTheme(conversationId: string, theme: string): Promise<{ status: string; theme: string }> {
    const response = await this.client.patch<{ status: string; theme: string }>(
      `/api/conversations/${conversationId}/theme`,
      { theme }
    );
    return response.data;
  }

  async updateCollabSubmode(
    conversationId: string,
    submode: 'planning' | 'design' | 'production' | null,
  ): Promise<{ status: string; collab_submode: string | null }> {
    const response = await this.client.patch<{ status: string; collab_submode: string | null }>(
      `/api/conversations/${encodeURIComponent(conversationId)}/collab-submode`,
      { collab_submode: submode },
    );
    return response.data;
  }

  // ==================== Group Chat Members ====================

  /**
   * Retrieves members of a group conversation.
   */
  async getConversationMembers(conversationId: string): Promise<ConversationMember[]> {
    const response = await this.client.get<ConversationMember[]>(
      `/api/conversations/${conversationId}/members`
    );
    return response.data;
  }

  /**
   * Adds a member to a group conversation (owner only).
   */
  async addConversationMember(conversationId: string, userId: string): Promise<ConversationMember> {
    const response = await this.client.post<ConversationMember>(
      `/api/conversations/${conversationId}/members`,
      { user_id: userId }
    );
    return response.data;
  }

  /**
   * Removes a member from a group conversation (owner only).
   */
  async removeConversationMember(conversationId: string, userId: string): Promise<void> {
    await this.client.delete(`/api/conversations/${conversationId}/members/${userId}`);
  }

  /**
   * Converts a solo conversation to a group conversation.
   * After conversion, members can be added via addConversationMember.
   * Backend returns: ConversationDetail
   */
  async convertToGroup(conversationId: string): Promise<ConversationDetailResponse> {
    const response = await this.client.patch<ConversationDetailResponse>(
      `/api/conversations/${conversationId}/convert-to-group`
    );
    return response.data;
  }

  // ==================== Messages ====================

  /**
   * Sends a message to a conversation.
   * Backend returns: MessageResponse
   */
  async sendMessage(conversationId: string, data: SendMessageRequest): Promise<MessageResponse> {
    const response = await this.client.post<MessageResponse>(
      `/api/conversations/${conversationId}/messages`,
      data
    );
    return response.data;
  }

  /**
   * Uploads a file attachment to a conversation.
   * Backend returns: AttachmentInfo
   */
  async uploadAttachment(conversationId: string, file: File, opts?: UploadOptions): Promise<AttachmentInfo> {
    const formData = new FormData();
    formData.append('file', file);
    // Explicitly set multipart/form-data to override the instance default
    // of 'application/json'. Axios 1.x auto-appends the boundary when it
    // detects FormData as the request body.
    // Videos get VIDEO_UPLOAD_TIMEOUT_MS (40 MiB on a slow uplink), everything else 120 s.
    const response = await this.client.post<AttachmentInfo>(
      `/api/conversations/${conversationId}/attachments`,
      formData,
      uploadRequestConfig([file], opts),
    );
    return response.data;
  }

  /**
   * Uploads multiple files in one request so they share a single message bubble.
   * Each file is appended to FormData under the key 'files'; FastAPI collects them
   * into List[UploadFile] and creates exactly one Message with all attachments.
   */
  async uploadAttachmentsBatch(conversationId: string, files: File[], opts?: UploadOptions): Promise<AttachmentInfo[]> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await this.client.post<AttachmentInfo[]>(
      `/api/conversations/${conversationId}/attachments/batch`,
      formData,
      uploadRequestConfig(files, opts),
    );
    return response.data;
  }

  /**
   * Issues a short-lived one-time download token for an attachment.
   * The returned URL can be opened directly in a browser without Bearer auth.
   */
  async fetchAttachmentDownloadToken(attachmentId: string): Promise<string> {
    const response = await this.client.post(
      `/api/attachments/${attachmentId}/download-token`
    );
    return response.data.download_url as string;
  }

  /**
   * Fetches an attachment as a blob URL for authenticated display.
   * The download endpoint requires Bearer token auth. Browser <img> tags
   * don't send auth headers, so we fetch with auth and create a blob URL.
   */
  async fetchAttachmentBlobUrl(attachmentId: string): Promise<string> {
    const response = await this.client.get(
      `/api/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
  }

  /**
   * Server-rendered JPEG thumbnail of an attachment as an authenticated blob URL
   * (sibling of `/download`). Any non-200 rejects -- the caller shows the
   * file-type icon instead. The caller owns `URL.revokeObjectURL`.
   */
  async fetchAttachmentThumbnailUrl(attachmentId: string, signal?: AbortSignal): Promise<string> {
    const response = await this.client.get<Blob>(
      `/api/attachments/${encodeURIComponent(attachmentId)}/thumbnail`,
      { responseType: 'blob', signal }
    );
    return URL.createObjectURL(response.data);
  }

  async fetchDesignPreviewBlobUrl(apiPath: string): Promise<string> {
    const response = await this.client.get(apiPath, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  }

  /**
   * Fetches an attachment's text content for inline preview.
   * Downloads the file as a blob and decodes it via `decodeTextBytes`
   * (BOM / UTF-8 / GB18030 -- not `Blob.text()`, which is UTF-8 only).
   */
  async fetchAttachmentText(attachmentId: string): Promise<string> {
    const response = await this.client.get(
      `/api/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return decodeTextBytes(await response.data.arrayBuffer());
  }

  /**
   * Lists all attachments for a conversation.
   * Returns AttachmentInfo[] sorted by creation date (newest first from backend).
   * Backend returns {items, total, has_more}; we extract items here.
   */
  async findConversationAttachment(conversationId: string, attachmentId: string, signal?: AbortSignal): Promise<AttachmentInfo | null> {
    let offset = 0;
    const limit = 200; // Existing backend maximum page size.
    for (;;) {
      const response = await this.client.get<{ items: AttachmentInfo[]; total: number; has_more: boolean }>(
        `/api/conversations/${encodeURIComponent(conversationId)}/attachments`,
        { params: { limit, offset }, signal }
      );
      const { items, has_more } = response.data;
      const match = items.find(item => item.id === attachmentId);
      if (match) return match;
      if (!items.length || !has_more) return null;
      offset += items.length;
    }
  }

  async fetchAttachmentBlob(attachmentId: string, signal?: AbortSignal): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/api/attachments/${encodeURIComponent(attachmentId)}/download`,
      { responseType: 'blob', signal }
    );
    return response.data;
  }

  /** csv/tsv/xlsx/xlsm attachment as a JSON table (415 other types / 413 too big / 422 unparseable). */
  async fetchAttachmentPreviewTable(attachmentId: string, signal?: AbortSignal): Promise<SheetPreviewResponse> {
    const response = await this.client.get<SheetPreviewResponse>(
      `/api/attachments/${encodeURIComponent(attachmentId)}/preview-table`,
      { signal }
    );
    return response.data;
  }

  /** Project-folder spreadsheet as a JSON table (same codes as above). */
  async fetchProjectFolderPreviewTable(
    conversationId: string, folder: string, path: string, signal?: AbortSignal
  ): Promise<SheetPreviewResponse> {
    const response = await this.client.get<SheetPreviewResponse>(
      `/api/conversations/${encodeURIComponent(conversationId)}/project-folder/preview-table`,
      { params: { folder, path }, signal }
    );
    return response.data;
  }

  /** Office attachment rendered server-side as PDF (501 no soffice / 415 non-Office / 504 timeout). */
  async fetchAttachmentPreviewPdf(attachmentId: string, signal?: AbortSignal): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/api/attachments/${encodeURIComponent(attachmentId)}/preview-pdf`,
      { responseType: 'blob', signal }
    );
    return response.data;
  }

  /**
   * Render files-panel HTML to an ephemeral PNG/JPEG (413 too big, 504 timeout); no attachment is stored.
   * `region` (CSS px) makes the server clip to that band at 2x device scale; omitted -> full page.
   * `source.htmlSha256` alone = hash-first request served from the server's warm page cache;
   * 409 (cache miss) / 422 mean "re-POST with `source.html`". `format` defaults to png server-side.
   */
  async renderHtmlSnapshot(
    conversationId: string, source: HtmlSnapshotSource, width: number, signal?: AbortSignal,
    region?: { top: number; height: number }
  ): Promise<Blob> {
    const response = await this.client.post<Blob>(
      `/api/conversations/${encodeURIComponent(conversationId)}/html-snapshot`,
      {
        html: source.html, html_sha256: source.htmlSha256, width,
        top: region?.top, height: region?.height, format: source.format,
      },
      { responseType: 'blob', signal }
    );
    return response.data;
  }

  /** Office file in a project folder rendered server-side as PDF (same codes as above). */
  async fetchProjectFolderPreviewPdf(
    conversationId: string, folder: string, path: string, signal?: AbortSignal
  ): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/api/conversations/${encodeURIComponent(conversationId)}/project-folder/preview-pdf`,
      { params: { folder, path }, responseType: 'blob', signal }
    );
    return response.data;
  }

  async listConversationAttachments(conversationId: string): Promise<AttachmentInfo[]> {
    const response = await this.client.get<{ items: AttachmentInfo[]; total: number; has_more: boolean }>(
      `/api/conversations/${conversationId}/attachments`
    );
    return response.data.items;
  }

  /**
   * Paged attachment listing that preserves `total` (the plain
   * listConversationAttachments drops it). The header files counter calls
   * this with `{ limit: 1 }` and reads `total`.
   */
  async listConversationAttachmentsPage(
    conversationId: string,
    opts: { limit?: number; offset?: number; signal?: AbortSignal } = {}
  ): Promise<ConversationAttachmentsPage> {
    const params: Record<string, number> = {};
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    const response = await this.client.get<{ items: AttachmentInfo[]; total: number; has_more: boolean }>(
      `/api/conversations/${encodeURIComponent(conversationId)}/attachments`,
      { params, signal: opts.signal }
    );
    return { items: response.data.items, total: response.data.total };
  }

  /** Lists the conversation's persisted design (DESIGN_BLOCK) files, newest first. */
  async listConversationDesignFiles(
    conversationId: string,
    signal?: AbortSignal
  ): Promise<ConversationDesignFile[]> {
    const response = await this.client.get<{ items: ConversationDesignFile[] }>(
      `/api/conversations/${encodeURIComponent(conversationId)}/design-files`,
      { signal }
    );
    return response.data.items;
  }

  /**
   * Fetches a conversation design file's source text via the session-preview
   * route (conversation-access gated). Callers render it through HtmlPreview /
   * SvgPreview -- never as a blob: iframe.
   */
  async fetchDesignSessionFileText(
    projectName: string,
    conversationId: string,
    file: Pick<ConversationDesignFile, 'did' | 'slug' | 'ext'>,
    signal?: AbortSignal
  ): Promise<string> {
    const response = await this.client.get<string>(
      `/api/projects/${encodeURIComponent(projectName)}/design/session-preview`,
      {
        params: { conv: conversationId, did: file.did, slug: file.slug, ext: file.ext, mode: 'inline' },
        responseType: 'text',
        signal,
      }
    );
    return response.data;
  }

  /**
   * Lists planning folder files for a conversation.
   * Returns file names and extensions, plus whether the project has planning enabled.
   */
  async listPlanningFiles(conversationId: string): Promise<PlanningFileListResponse> {
    const response = await this.client.get<PlanningFileListResponse>(
      `/api/conversations/${conversationId}/planning-files`
    );
    return response.data;
  }

  /**
   * Downloads a single planning file as a blob.
   */
  async downloadPlanningFile(conversationId: string, filename: string): Promise<Blob> {
    const response = await this.client.get(
      `/api/conversations/${conversationId}/planning-files/${encodeURIComponent(filename)}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Lists contents of a project folder (planning or document) with type info.
   */
  async listProjectFolder(
    conversationId: string,
    folder: string = 'planning',
    path: string = ''
  ): Promise<FolderListResponse> {
    const params: Record<string, string> = { folder };
    if (path) params.path = path;
    const response = await this.client.get<FolderListResponse>(
      `/api/conversations/${conversationId}/project-folder`,
      { params }
    );
    return response.data;
  }

  /**
   * Downloads a file from a project folder.
   */
  async downloadProjectFolderFile(
    conversationId: string,
    folder: string,
    path: string
  ): Promise<Blob> {
    const response = await this.client.get(
      `/api/conversations/${conversationId}/project-folder/download`,
      { params: { folder, path }, responseType: 'blob' }
    );
    return response.data;
  }

  // ==================== Design File Viewer ====================

  /**
   * Fetches design file content for inline preview.
   * Returns raw HTML content as a string for rendering in a sandboxed iframe.
   */
  async getDesignPreview(projectName: string, path: string): Promise<string> {
    const response = await this.client.get(
      `/api/projects/${encodeURIComponent(projectName)}/design/preview`,
      { params: { path, mode: 'inline' }, responseType: 'text' }
    );
    return response.data;
  }

  /**
   * Downloads a design file (triggers browser download via programmatic link).
   */
  async downloadDesignFile(projectName: string, path: string): Promise<void> {
    const filename = path.split('/').pop() || 'design.html';
    await this.exportDocument(
      `/api/projects/${encodeURIComponent(projectName)}/design/preview?path=${encodeURIComponent(path)}&mode=download`,
      filename
    );
  }

  /**
   * Searches messages across conversations.
   * Backend returns: MessageSearchResult[]
   */
  async searchMessages(query: string): Promise<unknown[]> {
    const response = await this.client.get('/api/messages/search', {
      params: { q: query },
    });
    return response.data;
  }

  // ==================== Export / Download ====================

  /**
   * Generic download helper. Fetches the export URL as a blob via the axios
   * client (which attaches the Authorization header), then triggers a
   * programmatic <a> click to save the file. Works for all formats
   * (md, html, pdf, docx) without popup blockers or cookie-auth issues.
   */
  async exportDocument(url: string, filename: string): Promise<void> {
    const response = await this.client.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 100);
  }

  /**
   * Downloads a dispatch proposal in the given format.
   */
  async exportProposal(id: string, format: 'md' | 'html' | 'pdf' | 'docx' | 'xlsx' | 'pptx'): Promise<void> {
    await this.exportDocument(
      `/api/dispatch-proposals/${id}/export?format=${format}`,
      `proposal-${id.slice(0, 8)}.${format}`
    );
  }

  /**
   * Downloads a conversation in the given format.
   */
  async exportConversation(id: string, format: 'md' | 'html' | 'pdf' | 'docx' | 'xlsx' | 'pptx', roles?: string, last?: number, templateId?: string): Promise<void> {
    let url = `/api/conversations/${id}/export?format=${format}`;
    if (roles) url += `&roles=${encodeURIComponent(roles)}`;
    if (last) url += `&last=${last}`;
    if (templateId) url += `&template_id=${encodeURIComponent(templateId)}`;
    await this.exportDocument(url, `conversation-${id.slice(0, 8)}.${format}`);
  }

  /**
   * Generates a document attachment for a specific assistant message.
   */
  async generateAttachment(
    conversationId: string,
    messageId: string,
    body: GenerateAttachmentRequest
  ): Promise<AttachmentResponse> {
    const response = await this.client.post<AttachmentResponse>(
      `/api/conversations/${conversationId}/messages/${messageId}/generate-attachment`,
      body
    );
    return response.data;
  }

  // ==================== Export Templates ====================

  /**
   * Lists all export templates.
   */
  async listExportTemplates(): Promise<ExportTemplateListResponse> {
    const response = await this.client.get<ExportTemplateListResponse>('/api/export-templates');
    return response.data;
  }

  /**
   * Creates a new export template.
   */
  async createExportTemplate(data: ExportTemplateCreateRequest): Promise<ExportTemplateResponse> {
    const response = await this.client.post<ExportTemplateResponse>('/api/export-templates', data);
    return response.data;
  }

  /**
   * Updates an export template.
   */
  async updateExportTemplate(id: string, data: ExportTemplateUpdateRequest): Promise<ExportTemplateResponse> {
    const response = await this.client.put<ExportTemplateResponse>(`/api/export-templates/${id}`, data);
    return response.data;
  }

  /**
   * Deletes an export template.
   */
  async deleteExportTemplate(id: string): Promise<void> {
    await this.client.delete(`/api/export-templates/${id}`);
  }

  /**
   * Sets an export template as the system default (admin only).
   */
  async setDefaultExportTemplate(id: string): Promise<ExportTemplateResponse> {
    const response = await this.client.put<ExportTemplateResponse>(`/api/export-templates/${id}/default`);
    return response.data;
  }

  // ==================== Projects ====================

  /**
   * Retrieves available projects for the current user (any authenticated user).
   * Returns safe fields only (id, name, description).
   *
   * Phase 1 T6-B: optional `orgId` is passed as `?org_id=...` query parameter.
   * Backend behavior (T5-A):
   *   - When `USE_ORG_SCOPED_PROJECTS=False` (prod default) the param is harmlessly ignored.
   *   - When `USE_ORG_SCOPED_PROJECTS=True` (conductor_p1_dev) the result is
   *     scoped to `Project.org_id == orgId` if the caller is a member of that
   *     org; otherwise an empty list is returned.
   * Passing `null` / `undefined` retains the legacy behavior (all permitted projects).
   */
  async getUserProjects(orgId?: string | null): Promise<ProjectSummaryResponse[]> {
    const params = orgId ? { org_id: orgId } : undefined;
    const response = await this.client.get<ProjectSummaryResponse[]>(
      '/api/projects/',
      params ? { params } : undefined
    );
    return response.data;
  }

  /**
   * Retrieves available base branches for a git-enabled project.
   * Returns master + feat_* branches.
   */
  async getProjectBranches(projectId: string): Promise<string[]> {
    const response = await this.client.get<{ branches: string[] }>(`/api/projects/${projectId}/branches`);
    return response.data.branches;
  }

  /**
   * Lists conversations whose per-conversation clone can be REUSED (方案3 clone
   * reuse fork lineage). Used by the NewConversationDialog "现有会话（待提交）"
   * dropdown. Each item carries a ``status`` ∈ pending|committed|clean that the
   * dialog renders as a 待提交/已提交/未修改 badge.
   *
   * Backend route: GET /api/conversations/forkable?project_id=&branch=
   */
  async listForkableConversations(
    projectId: string,
    branch?: string,
  ): Promise<ForkableConversation[]> {
    const params: Record<string, string> = { project_id: projectId };
    if (branch) params.branch = branch;
    const response = await this.client.get<{ items: ForkableConversation[] }>(
      '/api/conversations/forkable',
      { params },
    );
    return response.data.items;
  }

  /**
   * Fetches each git-enabled conversation's clone git-status for the sidebar
   * status dot. Returns a ``conv_id -> status`` map (status ∈
   * pending|committed|clean). Fail-soft: a non-ssh_docker / non-git project or
   * any backend degradation returns ``{}``.
   *
   * Backend route: GET /api/conversations/git-status?project_id=
   */
  async fetchConversationsGitStatus(
    projectId: string,
  ): Promise<Record<string, string>> {
    const response = await this.client.get<{ statuses: Record<string, string> }>(
      '/api/conversations/git-status',
      { params: { project_id: projectId } },
    );
    return response.data.statuses || {};
  }

  /**
   * Fetches which of the given conversations currently have an agent task
   * RUNNING, for the sidebar's green "执行中" status dot. ``running`` is the
   * subset of ``convIds`` that are running (order/absence carries no meaning);
   * ``awaitingUser`` is the subset of ``running`` that also has an unanswered
   * question panel (sidebar yellow, P-COND-A4F28939DE32). Both default to []
   * when the field is missing (older backend during a rolling deploy).
   *
   * Unlike ``fetchConversationsGitStatus`` (git working-tree state, watermark
   * throttled) this is pure run-state and is polled on a fixed heartbeat by
   * ConversationList. ``ids`` is comma-separated and capped server-side at 200.
   * Errors propagate; the caller swallows them (fail-soft = no green dots).
   *
   * Backend route: GET /api/conversations/active-tasks?project_id=&ids=
   */
  async fetchConversationsActiveTasks(
    projectId: string,
    convIds: string[],
  ): Promise<{ running: string[]; awaitingUser: string[] }> {
    const response = await this.client.get<{
      running?: string[];
      awaiting_user?: string[];
    }>(
      '/api/conversations/active-tasks',
      { params: { project_id: projectId, ids: convIds.join(',') } },
    );
    return {
      running: response.data.running || [],
      awaitingUser: response.data.awaiting_user || [],
    };
  }

  /**
   * Creates a new feat_* base branch on a git self-managed project. The name
   * MUST contain the substring "feat" (backend ``_BRANCH_FEAT_RE``) or the
   * endpoint 422s; the endpoint 502s if the project has no git remote. Used by
   * the NewConversationDialog "创建新分支" modal (Req 5/6). Errors propagate to
   * the caller so the modal can surface the endpoint message.
   *
   * Backend route: POST /api/projects/{id}/feat-branch
   */
  async createFeatBranch(
    projectId: string,
    body: { name: string; base_branch: string },
  ): Promise<CreateFeatBranchResponse> {
    const response = await this.client.post<CreateFeatBranchResponse>(
      `/api/projects/${projectId}/feat-branch`,
      body,
      { timeout: FEAT_BRANCH_CREATE_TIMEOUT },
    );
    return response.data;
  }

  /**
   * Retrieves users with access to a specific project (non-admin).
   * Used by group chat member pickers.
   */
  async getProjectUsers(projectId: string): Promise<{ id: string; username: string; display_name?: string | null }[]> {
    const response = await this.client.get<{ id: string; username: string; display_name?: string | null }[]>(`/api/projects/${projectId}/users`);
    return response.data;
  }

  /**
   * Retrieves all projects with full details (admin only).
   * Backend returns: ProjectResponse[]
   */
  async getProjects(): Promise<ProjectResponse[]> {
    const response = await this.client.get<ProjectResponse[]>('/api/admin/projects');
    return response.data;
  }

  /**
   * Retrieves a single project's full details (admin or scoped).
   * Used by `ProjectSettings` to seed the per-project default_model and
   * allowed_models pickers from the persisted record (P-COND-1027 Phase 2
   * follow-up T2). Mirrors `getProjects()` for fetch wrapper / error
   * handling and reuses the same `ProjectResponse` shape returned by the
   * PATCH route, so the page can pass the response straight back through
   * `updateProjectModelSettings`.
   *
   * Backend route: GET /api/admin/projects/{id}
   */
  async getProject(id: string): Promise<ProjectResponse> {
    const response = await this.client.get<ProjectResponse>(
      `/api/admin/projects/${encodeURIComponent(id)}`,
    );
    return response.data;
  }

  /**
   * Creates a new project (admin only).
   * Backend returns: ProjectResponse
   */
  async createProject(data: Record<string, unknown>): Promise<ProjectResponse> {
    const response = await this.client.post<ProjectResponse>('/api/admin/projects', data);
    return response.data;
  }

  // ==================== Admin Models & Routing (Sub-Task C) ====================

  /**
   * Fetch the unified models + routing payload for the /admin/models page
   * (P-COND-B0ACC698DDB7 Sub-Task C). Returns the entire aux catalog
   * flattened with Sub-Task A's SSoT routing fields (architecture_mode,
   * default_backend) so the new section can render the backend badge per
   * row without calling resolve_model_routing on the client.
   *
   * Backend: GET /api/admin/models. Admin-only; non-admins receive 403.
   */
  async getAdminModelsRouting(): Promise<AdminModelsResponse> {
    const response = await this.client.get<AdminModelsResponse>('/api/admin/models');
    return response.data;
  }

  /**
   * Update the per-model routing state (Option B Minimal —
   * P-COND-7B23A41E8C3F).
   *
   * ``enabled`` is validated server-side but remains a no-op stub until
   * the ``system_settings.enabled_models`` column lands.
   * ``backend_overrides`` IS persisted: server writes the dict to
   * ``system_settings.model_backend_overrides`` and busts the
   * dispatcher's 1 s cache so the new map is observed on the very next
   * dispatch. ``aux_only`` is rejected at the server with 422.
   *
   * Returns the same payload as :meth:`getAdminModelsRouting` so the
   * caller can replace its state with the canonical post-write view
   * (including the updated ``effective_backend`` per row).
   *
   * Backend: PATCH /api/admin/models. Admin-only.
   */
  async updateAdminModels(payload: AdminModelsUpdate): Promise<AdminModelsResponse> {
    const response = await this.client.patch<AdminModelsResponse>(
      '/api/admin/models',
      payload,
    );
    return response.data;
  }

  /**
   * Backwards-compatible alias for :meth:`updateAdminModels` constrained
   * to the ``enabled`` list. New code should prefer
   * :meth:`updateAdminModels` so it can also send ``backend_overrides``.
   * Kept until the enabled-models follow-up retires this shape.
   */
  async updateAdminModelsEnabled(enabled: string[]): Promise<AdminModelsResponse> {
    return this.updateAdminModels({ enabled });
  }

  /**
   * List all model channels plus the catalog provider keys and the system
   * default model (Multi-Model Channel Config — Sub-Task 3).
   *
   * ``api_key`` is never returned; each row carries ``has_api_key`` so the
   * table renders a masked cell.
   *
   * Backend: GET /api/admin/model-channels. Admin-only; non-admins get 403.
   */
  async listModelChannels(): Promise<ModelChannelsResponse> {
    const response = await this.client.get<ModelChannelsResponse>(
      '/api/admin/model-channels',
    );
    return response.data;
  }

  /**
   * Create a new model channel.
   *
   * Caller MUST only include ``api_key`` when the admin entered a plaintext
   * key (the backend encrypts it via Fernet and never echoes it back).
   *
   * Backend: POST /api/admin/model-channels → 201 ModelChannelOut.
   */
  async createModelChannel(body: ModelChannelCreate): Promise<ModelChannelOut> {
    const response = await this.client.post<ModelChannelOut>(
      '/api/admin/model-channels',
      body,
    );
    return response.data;
  }

  /**
   * Update an existing model channel. All body fields optional.
   * ``api_key`` is left unchanged unless a non-empty value is sent.
   *
   * Backend: PUT /api/admin/model-channels/{id} → 200 ModelChannelOut.
   */
  async updateModelChannel(
    id: number,
    body: ModelChannelUpdate,
  ): Promise<ModelChannelOut> {
    const response = await this.client.put<ModelChannelOut>(
      `/api/admin/model-channels/${id}`,
      body,
    );
    return response.data;
  }

  /**
   * Delete a model channel.
   *
   * Backend: DELETE /api/admin/model-channels/{id} → 204.
   */
  async deleteModelChannel(id: number): Promise<void> {
    await this.client.delete(`/api/admin/model-channels/${id}`);
  }

  /**
   * Get the current subscription pricing config (platform admin only).
   *
   * Backend: GET /api/v1/admin/subscription-pricing (P-COND-LRT ST3).
   */
  async getSubscriptionPricing(): Promise<SubscriptionPricingResponse> {
    const response = await this.client.get<SubscriptionPricingResponse>(
      '/api/v1/admin/subscription-pricing',
    );
    return response.data;
  }

  /**
   * Persist edited subscription pricing (platform admin only).
   *
   * Backend: PUT /api/v1/admin/subscription-pricing (P-COND-LRT ST3).
   * 422 on malformed shape; reloads the pricing cache server-side.
   */
  async updateSubscriptionPricing(
    subscriptionPricing: SubscriptionPricingTable,
  ): Promise<SubscriptionPricingResponse> {
    const response = await this.client.put<SubscriptionPricingResponse>(
      '/api/v1/admin/subscription-pricing',
      { subscription_pricing: subscriptionPricing },
    );
    return response.data;
  }

  /**
   * Get the current per-plan-tier internal usage budgets (platform admin).
   *
   * Backend: GET /api/v1/admin/plan-tier-budgets (P-COND-LRT, this ticket).
   */
  async getPlanTierBudgets(): Promise<PlanTierBudgetsResponse> {
    const response = await this.client.get<PlanTierBudgetsResponse>(
      '/api/v1/admin/plan-tier-budgets',
    );
    return response.data;
  }

  /**
   * Persist edited per-plan-tier internal usage budgets (platform admin).
   *
   * Backend: PUT /api/v1/admin/plan-tier-budgets (P-COND-LRT, this ticket).
   * A non-null value must be positive (422 on zero/negative); null =
   * 不限量 (unlimited). Reloads the pricing cache server-side.
   */
  async updatePlanTierBudgets(
    planTierBudgets: PlanTierBudgets,
  ): Promise<PlanTierBudgetsResponse> {
    const response = await this.client.put<PlanTierBudgetsResponse>(
      '/api/v1/admin/plan-tier-budgets',
      { plan_tier_budgets: planTierBudgets },
    );
    return response.data;
  }

  /**
   * Get the current per-provider model pricing + coefficients (platform admin).
   *
   * Backend: GET /api/v1/admin/model-pricing (P-COND-LRT, this ticket).
   */
  async getModelPricing(): Promise<ModelPricingResponse> {
    const response = await this.client.get<ModelPricingResponse>(
      '/api/v1/admin/model-pricing',
    );
    return response.data;
  }

  /**
   * Persist edited model pricing (whole coefficients+providers block replace).
   *
   * Backend: PUT /api/v1/admin/model-pricing (P-COND-LRT, this ticket). All
   * numeric fields must be non-negative (0 allowed); pricing_mode ∈
   * {subscription, metered}. Reloads the pricing cache server-side.
   */
  async updateModelPricing(
    coefficients: Record<string, number>,
    providers: Record<string, ModelPricingProvider>,
  ): Promise<ModelPricingResponse> {
    const response = await this.client.put<ModelPricingResponse>(
      '/api/v1/admin/model-pricing',
      { coefficients, providers },
    );
    return response.data;
  }

  /**
   * List an org's self-serve payment orders, newest first (read-only).
   *
   * Backend: GET /api/v1/orgs/{org_id}/payment-orders (P-COND-LRT ST2).
   * Authz (server-side): org admin of `orgId` OR platform admin.
   */
  async listPaymentOrders(orgId: string): Promise<PaymentOrderListResponse> {
    const response = await this.client.get<PaymentOrderListResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/payment-orders`,
    );
    return response.data;
  }

  /**
   * Get the subscription pricing table for an org (org admin or platform admin).
   *
   * Backend: GET /api/v1/orgs/{orgId}/subscription-pricing (P-COND-LRT).
   * Org-scoped analogue of `getSubscriptionPricing` (which is platform-admin
   * only); reuses the shared `SubscriptionPricingResponse` shape so the org
   * 订阅管理 page can show standard prices + order amounts from the same
   * config the admin edits.
   */
  async getOrgSubscriptionPricing(
    orgId: string,
  ): Promise<SubscriptionPricingResponse> {
    const response = await this.client.get<SubscriptionPricingResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/subscription-pricing`,
    );
    return response.data;
  }

  // ==================== Admin ====================

  /**
   * Retrieves system health and metrics (admin only).
   * Backend returns: SystemStatus
   */
  async getSystemStatus(): Promise<SystemStatusResponse> {
    const response = await this.client.get<SystemStatusResponse>('/api/admin/status');
    return response.data;
  }

  /**
   * List agent self-discovery issue reports with filters + summary stats
   * (admin only). Backend: GET /api/admin/agent-issues.
   */
  async getAgentIssues(params?: AgentIssueQuery): Promise<AgentIssueListResponse> {
    const response = await this.client.get<AgentIssueListResponse>(
      '/api/admin/agent-issues',
      { params },
    );
    return response.data;
  }

  /**
   * List agent issue reports COLLAPSED BY dedup fingerprint (admin only).
   * Backend: GET /api/admin/agent-issues/aggregate.
   *
   * Powers the 家族折叠 toggle in AgentIssuesSection. Called lazily (only when
   * the toggle is switched on) so the default page load stays one request.
   */
  async getAgentIssuesAggregate(): Promise<AgentIssueFamily[]> {
    const response = await this.client.get<AgentIssueFamily[]>(
      '/api/admin/agent-issues/aggregate',
    );
    return response.data;
  }

  /**
   * Fetch a single agent issue report by id (admin only).
   * Backend: GET /api/admin/agent-issues/{id}.
   */
  async getAgentIssue(id: string): Promise<AgentIssueReport> {
    const response = await this.client.get<AgentIssueReport>(
      `/api/admin/agent-issues/${id}`,
    );
    return response.data;
  }

  /**
   * Advance an issue report's state (triage/investigate/resolve/dismiss).
   * Backend: PATCH /api/admin/agent-issues/{id}. Returns 409 if already closed.
   */
  async patchAgentIssue(
    id: string,
    body: AgentIssuePatchBody,
  ): Promise<AgentIssueReport> {
    const response = await this.client.patch<AgentIssueReport>(
      `/api/admin/agent-issues/${id}`,
      body,
    );
    return response.data;
  }

  /**
   * Bulk PATCH agent issue reports (S2).
   * Backend: PATCH /api/admin/agent-issues/bulk
   */
  async bulkPatchAgentIssues(
    issueIds: string[],
    patch: AgentIssuePatchBody,
  ): Promise<{ id: string; ok: boolean; error?: string }[]> {
    const response = await this.client.patch<{ id: string; ok: boolean; error?: string }[]>(
      '/api/admin/agent-issues/bulk',
      { issue_ids: issueIds, patch },
    );
    return response.data;
  }

  // ── Auto-fix (AF-T8) — backend/app/api/autofix.py, platform admin only ──

  /** Batches of one CST night. Backend: GET /api/admin/autofix/batches?date= */
  async getAutofixBatches(date: string): Promise<{ batches: AutofixBatch[] }> {
    const response = await this.client.get<{ batches: AutofixBatch[] }>(
      '/api/admin/autofix/batches', { params: { date } },
    );
    return response.data;
  }

  /** RunDetail incl. live_state + revert_action. Backend: GET /api/admin/autofix/runs/{id} */
  async getAutofixRun(runId: string): Promise<AutofixRunDetail> {
    const response = await this.client.get<AutofixRunDetail>(
      `/api/admin/autofix/runs/${encodeURIComponent(runId)}`,
    );
    return response.data;
  }

  /** needs_human runs with any change order. Backend: GET /api/admin/autofix/handoffs */
  async getAutofixHandoffs(): Promise<{ items: AutofixHandoff[] }> {
    const response = await this.client.get<{ items: AutofixHandoff[] }>('/api/admin/autofix/handoffs');
    return response.data;
  }

  /**
   * One-shot requeue of a handed-off issue for the next nightly run; 409
   * `{"detail": {"code", "message"}}` (already_requeued / issue_not_open /
   * no_handoff / requires_confirm) — parse with `parseAutofixError`.
   * Backend: POST /api/admin/autofix/handoffs/{issue_id}/requeue
   */
  async requeueAutofixHandoff(
    issueId: string, confirm?: boolean, note?: string,
  ): Promise<AutofixRequeueResult> {
    // `confirm=true` is required (409 requires_confirm otherwise) when the
    // handoff was caused by an admin (handoff_reason admin_excluded / reverted).
    const body: { note?: string; confirm?: boolean } = {};
    if (note && note.trim()) body.note = note.trim().slice(0, 500);
    if (confirm) body.confirm = true;
    const response = await this.client.post<AutofixRequeueResult>(
      `/api/admin/autofix/handoffs/${encodeURIComponent(issueId)}/requeue`, body,
    );
    return response.data;
  }

  /** Read-only revert / restore preview. Backend: GET .../runs/{id}/revert-preview?kind= */
  async getAutofixRevertPreview(
    runId: string, kind: 'revert' | 'restore',
  ): Promise<AutofixRevertPreview> {
    const response = await this.client.get<AutofixRevertPreview>(
      `/api/admin/autofix/runs/${encodeURIComponent(runId)}/revert-preview`, { params: { kind } },
    );
    return response.data;
  }

  /**
   * Claim a revert / restore (202 new, 200 existing in-flight op; 409
   * `{"detail": {"code", "message"}}` — parse with `parseAutofixError`).
   * `ship='immediate'` ships ALL changes merged into master (plan §2.6).
   */
  async requestAutofixRevert(
    runId: string,
    body: { kind: 'revert' | 'restore'; preview_token: string; ship: AutofixShipMode },
  ): Promise<AutofixRevertClaim> {
    const response = await this.client.post<AutofixRevertClaim>(
      `/api/admin/autofix/runs/${encodeURIComponent(runId)}/revert`, body,
    );
    return response.data;
  }

  /** Pipeline switch state, active policy, recent history. Backend: GET /api/admin/autofix/status */
  async getAutofixStatus(): Promise<AutofixStatus> {
    const response = await this.client.get<AutofixStatus>('/api/admin/autofix/status');
    return response.data;
  }

  /** Every policy version, newest first. Backend: GET /api/admin/autofix/policies */
  async getAutofixPolicies(): Promise<{ items: AutofixPolicy[] }> {
    const response = await this.client.get<{ items: AutofixPolicy[] }>('/api/admin/autofix/policies');
    return response.data;
  }

  /** Issue a new policy version (always INSERT). Backend: POST /api/admin/autofix/policies */
  async createAutofixPolicy(body: AutofixPolicyCreateBody): Promise<AutofixPolicy> {
    const response = await this.client.post<AutofixPolicy>('/api/admin/autofix/policies', body);
    return response.data;
  }

  /** Revoke a policy (409 if already revoked). Backend: POST .../policies/{id}/revoke */
  async revokeAutofixPolicy(policyId: string): Promise<AutofixPolicy> {
    const response = await this.client.post<AutofixPolicy>(
      `/api/admin/autofix/policies/${encodeURIComponent(policyId)}/revoke`,
    );
    return response.data;
  }

  /**
   * Admin confirms the formal risk tier (the tier is ALWAYS explicit — the
   * advisory suggestion is never promoted implicitly).
   * Backend: POST /api/admin/agent-issues/{id}/confirm-risk-tier
   */
  async confirmAgentIssueRiskTier(id: string, riskTier: number, note?: string): Promise<AgentIssueReport> {
    const body: { risk_tier: number; note?: string } = { risk_tier: riskTier };
    if (note && note.trim()) body.note = note.trim().slice(0, 500);
    const response = await this.client.post<AgentIssueReport>(
      `/api/admin/agent-issues/${encodeURIComponent(id)}/confirm-risk-tier`, body,
    );
    return response.data;
  }

  /** Bulk confirm (1..100 rows). Backend: POST /api/admin/agent-issues/confirm-risk-tier/bulk */
  async bulkConfirmAgentIssueRiskTier(
    items: { id: string; risk_tier: number }[],
  ): Promise<RiskTierBulkConfirmResult> {
    const response = await this.client.post<RiskTierBulkConfirmResult>(
      '/api/admin/agent-issues/confirm-risk-tier/bulk', { items },
    );
    return response.data;
  }

  /** Nightly report (latest batch when no date). Backend: GET /api/admin/autofix/nightly-report */
  async getNightlyReport(batchDate?: string): Promise<NightlyReport> {
    const response = await this.client.get<NightlyReport>(
      '/api/admin/autofix/nightly-report', { params: batchDate ? { batch_date: batchDate } : {} },
    );
    return response.data;
  }

  /** Approve the candidate; 409 {detail:{code,message}} on a stale SHA / state. */
  async approveAutofixBatch(batchId: string, candidateSha: string): Promise<NightlyApproveResult> {
    const response = await this.client.post<NightlyApproveResult>(
      `/api/admin/autofix/batches/${encodeURIComponent(batchId)}/approve`,
      { candidate_sha: candidateSha },
    );
    return response.data;
  }

  /**
   * Exclude one run from the pending candidate (Stage 2): 200 `{status:
   * retesting|superseded}`; 409 wrong_state / sha_mismatch / busy /
   * candidate_invalidated — parse with `parseAutofixError`.
   * Backend: POST /api/admin/autofix/batches/{id}/runs/{run_id}/exclude
   */
  async excludeAutofixRun(
    batchId: string, runId: string, candidateSha: string, note?: string,
  ): Promise<{ status: string }> {
    const body: { candidate_sha: string; note?: string } = { candidate_sha: candidateSha };
    if (note && note.trim()) body.note = note.trim().slice(0, 500);
    const response = await this.client.post<{ status: string }>(
      `/api/admin/autofix/batches/${encodeURIComponent(batchId)}/runs/${encodeURIComponent(runId)}/exclude`,
      body,
    );
    return response.data;
  }

  /** Read-only batch revert preview. Backend: GET .../batches/{id}/revert-batch-preview?run_ids= */
  async getAutofixBatchRevertPreview(
    batchId: string, runIds: string[],
  ): Promise<AutofixBatchRevertPreview> {
    const response = await this.client.get<AutofixBatchRevertPreview>(
      `/api/admin/autofix/batches/${encodeURIComponent(batchId)}/revert-batch-preview`,
      { params: { run_ids: runIds.join(',') } },
    );
    return response.data;
  }

  /**
   * Claim an all-or-nothing batch revert (202 new / 200 same group in flight);
   * 409 stale_preview / busy / member_busy / already_reverted / wrong_batch /
   * migration_refused / policy_revoked. Poll `getAutofixRevert(leader_op_id)`.
   * Backend: POST /api/admin/autofix/batches/{id}/revert-batch
   */
  async requestAutofixBatchRevert(
    batchId: string,
    body: { run_ids: string[]; preview_token: string; ship: AutofixShipMode },
  ): Promise<AutofixBatchRevertClaim> {
    const response = await this.client.post<AutofixBatchRevertClaim>(
      `/api/admin/autofix/batches/${encodeURIComponent(batchId)}/revert-batch`, body,
    );
    return response.data;
  }

  /** Rebuild + redeploy a candidate whose test-env deploy failed; never merges. Backend: POST /api/admin/autofix/batches/{id}/retry-candidate */
  async retryAutofixCandidate(batchId: string, candidateSha: string): Promise<NightlyApproveResult> {
    const response = await this.client.post<NightlyApproveResult>(
      `/api/admin/autofix/batches/${encodeURIComponent(batchId)}/retry-candidate`,
      { candidate_sha: candidateSha },
    );
    return response.data;
  }

  /** Read-only run diff; secret-like files are path-only. Backend: GET /api/admin/autofix/runs/{id}/diff */
  async getAutofixRunDiff(runId: string): Promise<NightlyRunDiff> {
    const response = await this.client.get<NightlyRunDiff>(
      `/api/admin/autofix/runs/${encodeURIComponent(runId)}/diff`,
    );
    return response.data;
  }

  /** Test log as an authenticated blob URL; caller owns URL.revokeObjectURL. */
  async fetchAutofixTestLogUrl(resultId: string): Promise<string> {
    const response = await this.client.get<Blob>(
      `/api/admin/autofix/test-results/${encodeURIComponent(resultId)}/log`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data);
  }

  /** One revert op with its release records. Backend: GET /api/admin/autofix/reverts/{op_id} */
  async getAutofixRevert(opId: string): Promise<AutofixRevertOp> {
    const response = await this.client.get<AutofixRevertOp>(
      `/api/admin/autofix/reverts/${encodeURIComponent(opId)}`,
    );
    return response.data;
  }

  /**
   * Retrieves global system settings (admin only).
   * Backend returns: SystemSettingsResponse
   */
  async getSystemSettings(): Promise<SystemSettingsResponse> {
    const response = await this.client.get<SystemSettingsResponse>('/api/admin/system-settings');
    return response.data;
  }

  /**
   * Updates global system settings (admin only).
   * Backend returns: SystemSettingsResponse
   */
  async updateSystemSettings(data: {
    monthly_budget_cap_usd?: number | null;
    fallback_model?: string | null;
    daily_review_auto_dispatch?: boolean;
    daily_review_max_dispatches?: number;
    template_harvest_enabled?: boolean;
    template_harvest_day?: number;
    template_harvest_max_projects?: number;
    default_architecture_mode?: 'claude_cli' | 'conductor_native' | 'codex_cli' | null;
    default_backend_kind?: 'claude' | 'native_loop' | null;
    default_backend_model?: string | null;
    /** P-COND-1027 Phase 2 — toggles legacy 3-field override card. */
    advanced_overrides_enabled?: boolean;
    /** D2 hemostasis — global kill switch for self-serve subscription flow. */
    self_serve_subscription_enabled?: boolean;
    /** P-COND-7E403C2061B6 Sub-Task 4 — conversation-compaction config. */
    compact_enabled?: boolean;
    compact_provider?: string | null;
    compact_model?: string | null;
    compact_max_chars?: number;
    compact_inject_max_chars?: number;
    context_denoise_enabled?: boolean;
    context_denoise_llm_min_chars?: number;
    /** Third switch — claude_cli stateless rebuild (drop --resume). */
    claude_cli_stateless_rebuild_enabled?: boolean;
    /** P-COND-ACAD85BAB62D req#4 — new-org / new-member creation defaults. */
    new_org_default_allowed_providers?: string[] | null;
    new_org_default_provider_defaults?: Record<string, string> | null;
    new_member_default_can_select_model?: boolean | null;
    /** P-COND-64770C8A4E98 — 'A' | 'B'; null clears (follow .env); omit = unchanged. */
    phase1_review_mode?: Phase1ReviewMode | null;
  }): Promise<SystemSettingsResponse> {
    const response = await this.client.put<SystemSettingsResponse>('/api/admin/system-settings', data);
    return response.data;
  }

  // ==================== Aux OAuth Profiles (P-COND-1027 M1-05) ====================

  /**
   * Lists OAuth profiles registered for aux backends (admin only).
   * Optional ``provider`` filter narrows results to one provider.
   * Backend route: GET /api/aux/oauth/profiles[?provider=...]
   */
  async listOAuthProfiles(provider?: string): Promise<OAuthProfileSummary[]> {
    const qs = provider ? `?provider=${encodeURIComponent(provider)}` : '';
    // Backend returns an envelope `{ profiles: [...] }` — see
    // backend/app/api/aux.py (M1-05). Older callers expected a bare
    // array; defensive `?? []` keeps things sane if the envelope is
    // missing on a stale build.
    const response = await this.client.get<{ profiles: OAuthProfileSummary[] }>(`/api/aux/oauth/profiles${qs}`);
    return response.data.profiles ?? [];
  }

  /**
   * Creates a new OAuth profile (admin only). The ``api_key`` is sent in
   * cleartext over HTTPS once and encrypted server-side; the client MUST
   * NOT log or display it after submission.
   * Backend route: POST /api/aux/oauth/profiles → 201
   */
  async createOAuthProfile(payload: OAuthProfileCreatePayload): Promise<OAuthProfileSummary> {
    const response = await this.client.post<OAuthProfileSummary>('/api/aux/oauth/profiles', payload);
    return response.data;
  }

  /**
   * Retrieves a single OAuth profile by id (admin only). Encrypted
   * ``api_key`` is never returned — only metadata.
   * Backend route: GET /api/aux/oauth/profiles/{id}
   */
  async getOAuthProfile(id: string): Promise<OAuthProfileSummary> {
    const response = await this.client.get<OAuthProfileSummary>(
      `/api/aux/oauth/profiles/${encodeURIComponent(id)}`
    );
    return response.data;
  }

  /**
   * Deletes an OAuth profile (admin only). Returns 204 on success.
   * Backend route: DELETE /api/aux/oauth/profiles/{id}
   */
  async deleteOAuthProfile(id: string): Promise<void> {
    await this.client.delete(`/api/aux/oauth/profiles/${encodeURIComponent(id)}`);
  }

  // ==================== Aux Model Catalog (P-COND-1027 Phase 2) ====================

  /**
   * Fetches the global model catalog used by the model + thinking-effort
   * pickers. If the request fails, callers MUST fall back to the hardcoded
   * `FALLBACK_CATALOG` exported from `components/ModelSelector.tsx` so the
   * admin/project pages keep rendering.
   *
   * Backend route: GET /api/aux/catalog -- returns the PROVIDER-GROUPED
   * shape `{providers: [{provider, backend_kind, models: [...]}]}` (see
   * `backend/app/api/aux.py::get_catalog`), NOT a flat array. Callers
   * (`AdminPage.tsx`, `ProjectSettings.tsx`) want a flat list, so the
   * flattening happens HERE -- one place, not re-spelled per caller.
   * The `Array.isArray` branch anchors a wire shape that has already
   * drifted once (bug-patterns #533).
   */
  async getAuxCatalog(): Promise<AuxCatalogModel[]> {
    const response = await this.client.get<unknown>('/api/aux/catalog');
    const data = response.data as
      | AuxCatalogModel[]
      | { providers?: Array<{ provider?: string; models?: AuxCatalogModel[] }> }
      | null;
    if (Array.isArray(data)) return data;
    return (data?.providers ?? []).flatMap((group) =>
      (group.models ?? []).map((m) => ({ ...m, provider: m.provider ?? group.provider ?? '' }))
    );
  }

  /**
   * Fetches the unified model catalog used by the InputArea per-message
   * picker. Returns a flat list (NOT provider-grouped) plus the system
   * `allow_per_message_model_override` flag so the caller can decide
   * whether to render the picker at all in one round-trip.
   *
   * Auth: standard authenticated user (NOT admin-only). The endpoint is
   * read-only and exposes structural catalog metadata, not secrets.
   *
   * Backend route:
   *   `GET /api/catalog/models`
   *   (Multimodel Unified Backend UX Plan -- Sub-Task D)
   *
   * Frontend consumer: `frontend/src/components/InputArea.tsx`.
   */
  async getCatalogModels(): Promise<CatalogModelsResponse> {
    const response = await this.client.get<CatalogModelsResponse>('/api/catalog/models');
    return response.data;
  }

  // ==================== Project / Conversation Multi-Model (Phase 2) ====================

  /**
   * Updates the project-level default model + allowed-model whitelist, and
   * (Per-Project Backend Plan Sub-Task 2) the per-project multimodel
   * routing override fields.
   *
   * - `default_model`: a model name from the catalog, or null to fall back
   *   to the system default.
   * - `allowed_models`: optional whitelist; when null the project accepts
   *   any catalog model. An empty array `[]` means "lock all users out of
   *   model selection" (admin-only escape hatch — UI must confirm).
   * - `architecture_mode` / `backend_kind` / `backend_model`: per-project
   *   override of the multimodel router. Omit a field to leave it
   *   untouched (backend uses `model_fields_set` semantics); pass `null`
   *   to explicitly clear back to "inherit system default". Enum values
   *   are validated server-side — bad values return 422.
   *
   * Backend route: PATCH /api/admin/projects/{id}
   */
  async updateProjectModelSettings(
    projectId: string,
    data: {
      default_model?: string | null;
      allowed_models?: string[] | null;
      architecture_mode?: 'claude_cli' | 'conductor_native' | 'codex_cli' | null;
      backend_kind?: 'claude' | 'native_loop' | 'aux_only' | null;
      backend_model?: string | null;
    },
  ): Promise<ProjectResponse> {
    const response = await this.client.patch<ProjectResponse>(
      `/api/admin/projects/${encodeURIComponent(projectId)}`,
      data,
    );
    return response.data;
  }

  /**
   * Updates per-conversation model + thinking effort overrides.
   *
   * - `model`: catalog model name; routing (architecture_mode/backend_kind)
   *   is resolved server-side via `resolve_model_routing`.
   * - `thinking_effort`: one of `low|medium|high|extra|max`; ignored by the
   *   server when the chosen model has `supports_thinking=false`. The on/off
   *   dimension is carried by the separate `thinking_enabled` toggle.
   *
   * Backend route (pending): PATCH /api/conversations/{id}
   * Worker A's lane; same try/catch + "endpoint pending" toast pattern as
   * `updateProjectModelSettings` above.
   */
  async updateConversationModel(
    conversationId: string,
    data: {
      model?: string;
      thinking_effort?: 'low' | 'medium' | 'high' | 'extra' | 'max';
    },
  ): Promise<ConversationDetailResponse> {
    const response = await this.client.patch<ConversationDetailResponse>(
      `/api/conversations/${encodeURIComponent(conversationId)}`,
      data,
    );
    return response.data;
  }

  /**
   * Switch the provider profile for an existing conversation (P-COND-928A17B8C296).
   * Atomically writes provider_profile + architecture_mode + backend_kind +
   * clears backend_model + thinking_effort + thinking_length so the next
   * dispatch never hits the validate_combination gate with stale routing axes.
   * Pass null to revert to the project default (anthropic sentinel → NULL).
   */
  async patchConversationProviderProfile(
    conversationId: string,
    providerProfile: string | null,
  ): Promise<{
    id: string;
    provider_profile: string | null;
    architecture_mode: string;
    backend_kind: string;
    updated_at: string;
  }> {
    const response = await this.client.patch<{
      id: string;
      provider_profile: string | null;
      architecture_mode: string;
      backend_kind: string;
      updated_at: string;
    }>(
      `/api/conversations/${encodeURIComponent(conversationId)}/provider-profile`,
      { provider_profile: providerProfile },
    );
    return response.data;
  }

  /** Per-conversation Phase 1 review setting (P-COND-8CE6F1D21E1D). */
  async getConversationReviewSetting(conversationId: string): Promise<ConversationReviewSetting> {
    const response = await this.client.get<ConversationReviewSetting>(
      `/api/conversations/${encodeURIComponent(conversationId)}/review-setting`,
    );
    return response.data;
  }

  /** Save the per-conversation review setting; returns the full GET shape.
   *  403 = not owner / tier; 422 = illegal mode, model not offered, not viewable. */
  async updateConversationReviewSetting(
    conversationId: string,
    body: ConversationReviewSettingUpdate,
  ): Promise<ConversationReviewSetting> {
    const response = await this.client.patch<ConversationReviewSetting>(
      `/api/conversations/${encodeURIComponent(conversationId)}/review-setting`,
      body,
    );
    return response.data;
  }

  /**
   * Update preflight settings (branch_base / dev_submode) before the first
   * dispatch of a git-enabled conversation (P-COND-928A17B8C296).
   * Returns 409 once branch_name is set (conversation locked after first send).
   */
  async patchConversationPreflight(
    conversationId: string,
    data: { base_branch?: string; dev_submode?: string },
  ): Promise<{
    id: string;
    branch_base: string | null;
    dev_submode: string | null;
    updated_at: string;
  }> {
    const response = await this.client.patch<{
      id: string;
      branch_base: string | null;
      dev_submode: string | null;
      updated_at: string;
    }>(
      `/api/conversations/${encodeURIComponent(conversationId)}/preflight`,
      data,
    );
    return response.data;
  }

  // ==================== User Management (Admin) ====================

  /**
   * Retrieves all users (admin only).
   */
  async getUsers(): Promise<UserResponse[]> {
    const response = await this.client.get<UserResponse[]>('/api/admin/users');
    return response.data;
  }

  /**
   * Creates a new user (admin only).
   */
  async createUser(data: UserCreateRequest): Promise<UserResponse> {
    const response = await this.client.post<UserResponse>('/api/admin/users', data);
    return response.data;
  }

  /**
   * Updates an existing user (admin only).
   */
  async updateUser(userId: string, data: UserUpdateRequest): Promise<UserResponse> {
    const response = await this.client.put<UserResponse>(`/api/admin/users/${userId}`, data);
    return response.data;
  }

  /**
   * Soft-deletes a user (admin only).
   */
  async deactivateUser(userId: string): Promise<UserResponse> {
    const response = await this.client.delete<UserResponse>(`/api/admin/users/${userId}`);
    return response.data;
  }

  /**
   * Reactivates a deactivated user (admin only).
   */
  async reactivateUser(userId: string): Promise<UserResponse> {
    const response = await this.client.post<UserResponse>(`/api/admin/users/${userId}/reactivate`);
    return response.data;
  }

  /**
   * Retrieves token usage statistics for a given month (admin only).
   * @param period - Optional YYYY-MM string; defaults to current month if omitted.
   * @param provider - Optional provider profile filter. None/empty/"all" returns
   *   SUM-aggregate across providers; a specific name (e.g. "anthropic",
   *   "deepseek") filters to one provider's rows.
   */
  async getTokenUsage(period?: string, provider?: string, includeRetired = false): Promise<TokenUsageSummary> {
    const qp = new URLSearchParams();
    if (period) qp.set('period', period);
    if (provider && provider !== 'all') qp.set('provider', provider);
    if (includeRetired) qp.set('include_retired', 'true');
    const query = qp.toString() ? `?${qp.toString()}` : '';
    const response = await this.client.get<TokenUsageSummary>(`/api/admin/token-usage${query}`);
    return response.data;
  }

  /**
   * Retrieves per-user token usage for a single org (org 用量 page, 2026-05-30).
   * Backend (FROZEN contract): GET /api/v1/orgs/{org_id}/token-usage. Authz
   * mirrors listOrgMembers — caller must be an active member of the org OR a
   * platform admin; non-member callers get 404 (G10 existence-leak parity).
   * @param period - Optional YYYY-MM string; defaults to current month if omitted.
   * @param provider - Optional provider filter. None/empty/"all" aggregates.
   */
  async getOrgTokenUsage(orgId: string, period?: string, provider?: string): Promise<OrgTokenUsageSummary> {
    const qp = new URLSearchParams();
    if (period) qp.set('period', period);
    if (provider && provider !== 'all') qp.set('provider', provider);
    const query = qp.toString() ? `?${qp.toString()}` : '';
    const response = await this.client.get<OrgTokenUsageSummary>(`/api/v1/orgs/${encodeURIComponent(orgId)}/token-usage${query}`);
    return response.data;
  }

  // ==================== Method A Multi-Model (Admin) ====================

  /**
   * List provider profiles. Token values are NEVER returned by the backend —
   * only env var name + boolean is_set flag.
   */
  async listProviderProfiles(): Promise<ProviderProfilesResponse> {
    const response = await this.client.get<ProviderProfilesResponse>('/api/admin/providers');
    return response.data;
  }

  /**
   * Ask the backend to re-check this conversation's unsettled Codex run lock
   * (403 no access, 409 busy, 429 cooldown). Default client timeout.
   */
  async recheckConversationCodexRuns(conversationId: string): Promise<CodexConversationRecheckResult> {
    const response = await this.client.post<CodexConversationRecheckResult>(
      `/api/conversations/${encodeURIComponent(conversationId)}/codex-recheck`
    );
    return response.data;
  }

  /**
   * List provider profiles selectable in the new-conversation dialog.
   * Non-admin endpoint (any authenticated user) — returns a sanitized
   * subset so non-platform-admins are not 403'd into an empty picker.
   * Token values are NEVER returned.
   */
  async listConversationProviderProfiles(scope?: {
    project_id?: string; conversation_id?: string;
  }): Promise<SelectableProviderProfile[]> {
    const response = await this.client.get<SelectableProviderProfile[]>(
      '/api/conversations/provider-profiles', { params: scope }
    );
    return response.data;
  }

  /**
   * Clear the orchestrator's in-process provider profile cache and reload
   * from config/provider_profiles.yaml. No DB mutation.
   */
  async reloadProviderProfiles(): Promise<{ reloaded: boolean; count: number }> {
    const response = await this.client.post<{ reloaded: boolean; count: number }>(
      '/api/admin/providers/reload'
    );
    return response.data;
  }

  /**
   * Upsert a provider API token into the encrypted `provider_tokens` table.
   * The value is Fernet-encrypted server-side using the master key
   * `CONDUCTOR_TOKEN_ENCRYPTION_KEY`. The plaintext value is NEVER echoed
   * back — the response only confirms `is_set`, `source` (always "db" on
   * success), and the audit metadata `updated_at` / `updated_by`.
   *
   * Backend: PUT /api/admin/providers/tokens/{env_var_name}.
   * Caller must hold admin permission; non-admins receive 403.
   *
   * `accountLabel` (P-COND-5062A6F6431A Sub-Task 3) selects WHICH account
   * under this env var is written; the server defaults it to `'default'`
   * when omitted. Callers that pass a non-default label MUST first confirm
   * the backend is multi-account aware (i.e. `listProviderProfiles()`
   * returned an `accounts` array) — an older backend ignores the unknown
   * body field and would silently overwrite the DEFAULT account's key.
   */
  async setProviderToken(
    envVarName: string,
    value: string,
    accountLabel?: string,
  ): Promise<ProviderProfileEntry> {
    const body: { value: string; account_label?: string } = { value };
    if (accountLabel) body.account_label = accountLabel;
    const response = await this.client.put<ProviderProfileEntry>(
      `/api/admin/providers/tokens/${encodeURIComponent(envVarName)}`,
      body,
    );
    return response.data;
  }

  /**
   * Remove a provider API token from the encrypted `provider_tokens` table.
   * Idempotent — deleting a row that does not exist still returns 200.
   * After deletion the resolver falls back to the OS env var (if set);
   * otherwise the profile becomes `source = "missing"`.
   *
   * Backend: DELETE /api/admin/providers/tokens/{env_var_name}.
   *
   * `accountLabel` (P-COND-5062A6F6431A Sub-Task 3) selects WHICH account is
   * removed. Omitting it targets `'default'` — the legacy single-account row —
   * which is exactly what the per-profile 「清除」 button has always done.
   *
   * The label rides the QUERY STRING only. The backend declares it as
   * `Query(...)`, so a DELETE request body was never parsed; carrying one
   * "just in case" is dead code that reads like a live fallback.
   */
  async clearProviderToken(
    envVarName: string,
    accountLabel?: string,
  ): Promise<ProviderProfileEntry> {
    const query = accountLabel
      ? `?account_label=${encodeURIComponent(accountLabel)}`
      : '';
    const response = await this.client.delete<ProviderProfileEntry>(
      `/api/admin/providers/tokens/${encodeURIComponent(envVarName)}${query}`,
    );
    return response.data;
  }

  /**
   * Probe provider account(s) with a tiny paid call; the backend auto-clears
   * the cooldown AND the invalid latch when the call succeeds.
   *
   * Backend: POST /api/admin/providers/tokens/{env_var_name}/probe
   *   body: {account_label?, model?}
   *   400 -> env var not declared by any profile, no aux adapter can reach it,
   *          or `model` is not in that provider's declared model list
   *   503 -> Redis unavailable (refused BEFORE spending, so the money is not
   *          burned on a probe whose success could not be recorded)
   *
   * `accountLabel` targets ONE account; omitting it sweeps every label under
   * the env var, hard-capped server-side at 3 per run (a probe spends real
   * provider money, billed to the platform bucket).
   *
   * `model` is validated server-side against the profile's `models`
   * whitelist — an arbitrary string is refused rather than silently billed at
   * a flagship model's rate. Omit it to let the backend pick the cheapest.
   */
  async probeProviderAccount(
    envVarName: string,
    accountLabel?: string,
    model?: string,
  ): Promise<ProviderAccountProbeResponse> {
    const body: { account_label?: string; model?: string } = {};
    if (accountLabel) body.account_label = accountLabel;
    if (model) body.model = model;
    const response = await this.client.post<ProviderAccountProbeResponse>(
      `/api/admin/providers/tokens/${encodeURIComponent(envVarName)}/probe`,
      body,
    );
    return response.data;
  }

  /**
   * Clear the native account ring's **invalid latch** for an env var.
   *
   * Backend: POST /api/admin/providers/tokens/{env_var_name}/reset-invalid
   *   503 -> Redis unavailable (the latch could not be written)
   *
   * The latch carries NO TTL by design — a revoked key must not re-enter the
   * rotation ring just because a timer lapsed — so this is the ONLY
   * human-reachable recovery route besides a successful probe or a key
   * rewrite (bug-patterns #614: a TTL-less latch must ship its clear path).
   *
   * SCOPE: **env-var-wide**, matching
   * `AccountPairManager.clear_provider_account_invalid`. It clears the latch
   * for EVERY account label under the env var, not just one. A caller's
   * confirmation copy must say so — describing it as per-account would
   * understate the blast radius. It does NOT clear rate-limit cooldowns;
   * those carry a TTL and self-heal.
   */
  async resetProviderInvalidLatch(
    envVarName: string,
  ): Promise<{ env_var_name: string; cleared: number }> {
    const response = await this.client.post<{
      env_var_name: string;
      cleared: number;
    }>(
      `/api/admin/providers/tokens/${encodeURIComponent(envVarName)}/reset-invalid`,
    );
    return response.data;
  }

  /**
   * Upsert the admin-selected thinking mode tier for a provider profile.
   * Writes a row in `provider_thinking_settings` keyed by profile name.
   * The tier_name must match one of the profile's `thinking_modes[].name`.
   *
   * Backend: PUT /api/admin/providers/{profile_name}/thinking_mode
   *   body: {tier_name: "<name>"}
   *   200 -> {profile_name, active_thinking_mode, source: "db"}
   *   404 -> unknown profile_name
   *   422 -> tier_name not in profile.thinking_modes
   *   400 -> profile has no thinking_modes (e.g. Kimi)
   * Admin-only; non-admins receive 403.
   */
  async putProviderThinkingMode(
    profileName: string,
    tierName: string,
  ): Promise<{ profile_name: string; active_thinking_mode: string; source: 'db' }> {
    const response = await this.client.put<{
      profile_name: string;
      active_thinking_mode: string;
      source: 'db';
    }>(
      `/api/admin/providers/${encodeURIComponent(profileName)}/thinking_mode`,
      { tier_name: tierName },
    );
    return response.data;
  }

  /**
   * Clear the admin thinking mode override. Falls back to the YAML
   * `default_thinking_mode` if declared, otherwise leaves the env var
   * unset (preserves CLI 4.6+ adaptive default).
   *
   * Backend: DELETE /api/admin/providers/{profile_name}/thinking_mode
   *   200 -> {profile_name, active_thinking_mode: <yaml default or null>,
   *           source: "yaml" | "none"}
   * Idempotent: deleting a row that does not exist still returns 200.
   */
  async deleteProviderThinkingMode(
    profileName: string,
  ): Promise<{
    profile_name: string;
    active_thinking_mode: string | null;
    source: 'yaml' | 'none';
  }> {
    const response = await this.client.delete<{
      profile_name: string;
      active_thinking_mode: string | null;
      source: 'yaml' | 'none';
    }>(`/api/admin/providers/${encodeURIComponent(profileName)}/thinking_mode`);
    return response.data;
  }

  /**
   * Get the current dispatch backend selector. Master stage: read-only.
   */
  async getBackendSelector(): Promise<BackendSelectorResponse> {
    const response = await this.client.get<BackendSelectorResponse>('/api/admin/backend-selector');
    return response.data;
  }

  // ==================== Project Update (Admin) ====================

  /**
   * Updates project fields (admin only).
   */
  async updateProject(projectId: string, data: Record<string, unknown>): Promise<ProjectResponse> {
    const response = await this.client.patch<ProjectResponse>(`/api/admin/projects/${projectId}`, data);
    return response.data;
  }

  // ==================== Project Owner Transfer (Phase 2 P-COND-P2-T2B) ====================

  /**
   * Transfer project ownership to another user in the same org.
   *
   * Authorization is enforced backend-side: the caller MUST be the
   * current project owner OR an org admin of the project's org.
   * The target user MUST belong to the same org (cross-org → 400).
   * See planning/user-org-rollout/phase-2-execution.md §2.2 and
   * security-review.md §D A4.
   *
   * Backend: POST /api/v1/projects/{project_id}/transfer
   *   body: {target_user_id: string}
   *   200: TransferProjectResponse
   *   403: caller is not owner / org admin
   *   400: target user not in project org (or project has no org)
   *   404: project not found
   */
  async transferProject(
    projectId: string,
    targetUserId: string,
  ): Promise<TransferProjectResponse> {
    const response = await this.client.post<TransferProjectResponse>(
      `/api/v1/projects/${projectId}/transfer`,
      { target_user_id: targetUserId },
    );
    return response.data;
  }

  // ==================== Test Environment (Admin) ====================

  async startTestEnv(projectId: string, slot?: number): Promise<TestEnvResponse> {
    const body = slot !== undefined ? { slot } : {};
    const response = await this.client.post<TestEnvResponse>(
      `/api/projects/${projectId}/test-env/start`, body,
    );
    return response.data;
  }

  async stopTestEnv(projectId: string): Promise<TestEnvResponse> {
    const response = await this.client.post<TestEnvResponse>(
      `/api/projects/${projectId}/test-env/stop`,
    );
    return response.data;
  }

  async getTestEnvStatus(projectId: string): Promise<TestEnvResponse> {
    const response = await this.client.get<TestEnvResponse>(
      `/api/projects/${projectId}/test-env/status`,
    );
    return response.data;
  }

  async disableTestEnv(projectId: string): Promise<TestEnvResponse> {
    const response = await this.client.delete<TestEnvResponse>(
      `/api/projects/${projectId}/test-env`,
    );
    return response.data;
  }

  /**
   * Lists all projects on the shared test server with status and resource
   * usage (admin only). Powers the Test Server management section.
   */
  async getTestServerApps(): Promise<TestServerAppsResponse> {
    const response = await this.client.get<TestServerAppsResponse>(
      '/api/admin/test-server/apps',
    );
    return response.data;
  }

  /**
   * Performs a control action (start/stop) on a project's test-server unit
   * (admin only).
   */
  async controlTestServerApp(
    projectName: string,
    action: 'start' | 'stop',
  ): Promise<TestEnvResponse> {
    const response = await this.client.post<TestEnvResponse>(
      `/api/admin/test-server/apps/${encodeURIComponent(projectName)}/${action}`,
    );
    return response.data;
  }

  /**
   * Lists Conductor's OWN multi-slot test environments on this host
   * (registry: scripts/test-env/slots.conf) with live systemd status.
   * Admin only. Powers the Conductor Slots management section.
   */
  async getConductorSlots(): Promise<ConductorSlotsResponse> {
    const response = await this.client.get<ConductorSlotsResponse>(
      '/api/admin/conductor-slots',
    );
    return response.data;
  }

  /**
   * Starts/stops/restarts a registered Conductor test slot (admin only).
   */
  async controlConductorSlot(
    slotId: string,
    action: 'start' | 'stop' | 'restart',
  ): Promise<ConductorSlotActionResponse> {
    const response = await this.client.post<ConductorSlotActionResponse>(
      `/api/admin/conductor-slots/${encodeURIComponent(slotId)}/${action}`,
    );
    return response.data;
  }

  /**
   * Sync the legacy test slot to this host's LOCAL master, rebuild, restart
   * (waits up to 10 min for running agents). Background op
   * `legacy_deploy_master` — poll with getOpStatus.
   */
  async deployLegacyMaster(refreshDb: boolean): Promise<ConductorSlotOpStartResponse> {
    const response = await this.client.post<ConductorSlotOpStartResponse>(
      '/api/admin/conductor-slots/legacy/deploy-master',
      { refresh_db: refreshDb },
    );
    return response.data;
  }

  /**
   * Turn legacy takeover mode on/off (legacy_takeover.sh --on|--off).
   * Background op `legacy_takeover` — poll with getOpStatus.
   */
  async setLegacyTakeover(enable: boolean): Promise<ConductorSlotOpStartResponse> {
    const response = await this.client.post<ConductorSlotOpStartResponse>(
      '/api/admin/conductor-slots/legacy/takeover',
      { enable },
    );
    return response.data;
  }

  /**
   * Permanently remove a non-legacy test slot (slot_teardown.sh --remove).
   * `confirm` must equal slotId. Background op `slot_remove_<slotId>`.
   */
  async removeConductorSlot(
    slotId: string,
    confirm: string,
  ): Promise<ConductorSlotOpStartResponse> {
    const response = await this.client.post<ConductorSlotOpStartResponse>(
      `/api/admin/conductor-slots/${encodeURIComponent(slotId)}/teardown`,
      { confirm_slot_id: confirm },
    );
    return response.data;
  }

  // ==================== Deployment Slots (Admin) ====================

  /**
   * Returns current blue-green port pool state.
   * Powers the Deployment Slots section (P-COND-35C384AB4499).
   */
  async getDeploymentSlots(): Promise<DeploymentSlotsResponse> {
    const response = await this.client.get<DeploymentSlotsResponse>(
      '/api/admin/deployment-slots',
    );
    return response.data;
  }

  /**
   * Starts staging phase: picks idle slot, runs alembic, adds /conductor-preview/ route.
   * Platform admin only.
   */
  async triggerGracefulStage(): Promise<DeploymentSlotActionResponse> {
    const response = await this.client.post<DeploymentSlotActionResponse>(
      '/api/admin/graceful-stage',
    );
    return response.data;
  }

  /**
   * Promotes a staging slot to active: cuts nginx upstream, old slot → draining.
   * Platform admin only.
   */
  async promoteDeploymentSlot(port: number): Promise<DeploymentSlotActionResponse> {
    const response = await this.client.post<DeploymentSlotActionResponse>(
      `/api/admin/deployment-slots/${port}/promote`,
    );
    return response.data;
  }

  /**
   * Retires a draining slot immediately: stops its systemd unit, slot → idle.
   * The manual bypass for drain_monitor.sh's 2-hour drain wait — the UI only
   * offers it once the slot reports 0 active tasks. Platform admin only.
   * 409 if the slot is not draining, or is the live nginx upstream.
   */
  async retireDeploymentSlot(port: number): Promise<DeploymentSlotActionResponse> {
    const response = await this.client.post<DeploymentSlotActionResponse>(
      `/api/admin/deployment-slots/${port}/retire`,
    );
    return response.data;
  }

  /**
   * Cancels a staging slot instead of promoting it: removes the
   * /conductor-preview/ nginx route, stops the slot's systemd unit, slot → idle.
   * The undo half of graceful-stage. Platform admin only.
   * 409 if the slot is not staging, or is the live nginx upstream.
   */
  async cancelStagingSlot(port: number): Promise<DeploymentSlotActionResponse> {
    const response = await this.client.post<DeploymentSlotActionResponse>(
      `/api/admin/deployment-slots/${port}/cancel-staging`,
    );
    return response.data;
  }

  // ==================== Permissions (Admin) ====================

  /**
   * Retrieves all permissions (admin only).
   */
  async getPermissions(): Promise<PermissionResponse[]> {
    const response = await this.client.get<PermissionResponse[]>('/api/admin/permissions');
    return response.data;
  }

  /**
   * Grants a permission to a user for a project (admin only).
   */
  async createPermission(data: PermissionCreateRequest): Promise<PermissionResponse> {
    const response = await this.client.post<PermissionResponse>('/api/admin/permissions', data);
    return response.data;
  }

  /**
   * Updates a permission's tier (admin only).
   */
  async updatePermission(permissionId: string, data: { permission_tier: string; can_select_model?: boolean; can_access_planning?: boolean; can_access_documents?: boolean }): Promise<PermissionResponse> {
    const response = await this.client.put<PermissionResponse>(`/api/admin/permissions/${permissionId}`, data);
    return response.data;
  }

  /**
   * Deletes a permission (admin only).
   */
  async deletePermission(permissionId: string): Promise<void> {
    await this.client.delete(`/api/admin/permissions/${permissionId}`);
  }

  // ==================== Template Proposals (Admin) ====================

  /**
   * List template proposals with optional filters (admin only).
   */
  async getTemplateProposals(params?: {
    status?: string;
    source_project?: string;
    content_type?: string;
  }): Promise<TemplateProposalResponse[]> {
    const response = await this.client.get<TemplateProposalResponse[]>(
      '/api/admin/template-proposals',
      { params },
    );
    return response.data;
  }

  /**
   * Get full detail for a single template proposal (admin only).
   */
  async getTemplateProposal(id: string): Promise<TemplateProposalDetail> {
    const response = await this.client.get<TemplateProposalDetail>(
      `/api/admin/template-proposals/${id}`,
    );
    return response.data;
  }

  /**
   * Approve or reject a template proposal (admin only).
   */
  async updateTemplateProposal(
    id: string,
    data: { status: string; admin_note?: string },
  ): Promise<TemplateProposalResponse> {
    const response = await this.client.patch<TemplateProposalResponse>(
      `/api/admin/template-proposals/${id}`,
      data,
    );
    return response.data;
  }

  /**
   * Get pending/approved/rejected counts for badge display (admin only).
   */
  async getTemplateProposalStats(): Promise<TemplateProposalStats> {
    const response = await this.client.get<TemplateProposalStats>(
      '/api/admin/template-proposals/stats',
    );
    return response.data;
  }

  // ==================== Dispatch Proposals ====================

  /**
   * List dispatch proposals with optional filters and pagination.
   */
  async getDispatchProposals(params?: {
    project_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
    date_from?: string;
    date_to?: string;
    submitter?: string;
    display_id_search?: string;
  }): Promise<PaginatedProposalResponse> {
    const response = await this.client.get<PaginatedProposalResponse>(
      '/api/dispatch-proposals/',
      { params },
    );
    return response.data;
  }

  /**
   * Get pending/approved/rejected counts for badge display.
   */
  async getDispatchProposalStats(): Promise<DispatchProposalStats> {
    const response = await this.client.get<DispatchProposalStats>(
      '/api/dispatch-proposals/stats',
    );
    return response.data;
  }

  /**
   * Dispatch outcome statistics for the observability center Tab1 (Q1)
   * (P-COND-3BC2D7C95FC7 · ST-C). Same endpoint as getDispatchProposalStats,
   * but typed to include the ST-B-added `failure_causes` breakdown. Kept as a
   * distinct method so the observability page consumes the richer shape without
   * disturbing existing badge-count callers.
   * Backend: GET /api/dispatch-proposals/stats (extended by ST-B).
   */
  async getDispatchStats(month?: string): Promise<DispatchStatsResponse> {
    const response = await this.client.get<DispatchStatsResponse>(
      '/api/dispatch-proposals/stats',
      month ? { params: { month } } : undefined,
    );
    return response.data;
  }

  /**
   * List dispatch proposals submitted by the current user.
   */
  async getMyDispatchProposals(params?: {
    page?: number;
    page_size?: number;
    project_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<PaginatedProposalResponse> {
    const response = await this.client.get<PaginatedProposalResponse>(
      '/api/dispatch-proposals/mine',
      { params },
    );
    return response.data;
  }

  /**
   * List distinct projects that have proposals (for filter dropdown).
   */
  async getDispatchProposalProjects(): Promise<Array<{id: string; name: string}>> {
    const response = await this.client.get<Array<{id: string; name: string}>>(
      '/api/dispatch-proposals/projects',
    );
    return response.data;
  }

  /**
   * Get full detail for a single dispatch proposal.
   */
  async getDispatchProposal(id: string): Promise<DispatchProposalDetail> {
    const response = await this.client.get<DispatchProposalDetail>(
      `/api/dispatch-proposals/${id}`,
    );
    return response.data;
  }

  /**
   * Approve or reject a dispatch proposal.
   */
  async updateDispatchProposal(
    id: string,
    data: { status: string; note?: string },
  ): Promise<DispatchProposalDetail> {
    const response = await this.client.patch<DispatchProposalDetail>(
      `/api/dispatch-proposals/${id}`,
      data,
    );
    return response.data;
  }

  /**
   * Trigger implementation of an approved dispatch proposal.
   * Optionally pass command bar settings (model override, skill, long-running).
   */
  async implementDispatchProposal(
    id: string,
    options?: { model_override?: string; skill?: string; is_long_running?: boolean },
  ): Promise<{ message: string; proposal_id: string; status: string }> {
    const response = await this.client.post<{ message: string; proposal_id: string; status: string }>(
      `/api/dispatch-proposals/${id}/implement`,
      options || undefined,
    );
    return response.data;
  }

  /**
   * Trigger the inline [CONFIRMATION] proceed/cancel button.
   * Backend dispatches the same path as if the user typed yes / cancel.
   */
  async confirmConversation(
    conversationId: string,
    action: 'proceed' | 'cancel',
  ): Promise<{ message: string; conversation_id: string; action: string }> {
    const response = await this.client.post<{ message: string; conversation_id: string; action: string }>(
      `/api/conversations/${conversationId}/confirm`,
      { action },
    );
    return response.data;
  }

  /**
   * Submit in-conversation user feedback (会话反馈问题 → 观测中心).
   *
   * Files a `capture_source='user'` issue row scoped to this conversation. The
   * backend auto-attaches the conversation's last ≤2 rounds for triage. Session
   * auth; 403 if not the conversation owner, 404 if the conversation is missing.
   * `description` is optional (blank allowed, max 8000 chars).
   */
  async submitConversationFeedback(
    conversationId: string,
    description?: string,
  ): Promise<{ display_id: string; status: string }> {
    const response = await this.client.post<{ display_id: string; status: string }>(
      `/api/conversations/${conversationId}/feedback`,
      { description },
    );
    return response.data;
  }

  /**
   * List the third-party models that can review a proposal in this conversation.
   *
   * The caps travel WITH the list on purpose — `max_models_per_review` and
   * `remaining_today` are server-authoritative, so the picker must render the
   * numbers the backend reports instead of keeping its own copy (a client-side
   * limit is not a limit). An empty `targets` array is a valid response meaning
   * "no aux-callable model is configured", not an error.
   *
   * 403 on collab-mode conversations / read_only tier; 404 if missing.
   */
  async getReviewTargets(conversationId: string): Promise<ReviewTargetsResponse> {
    const response = await this.client.get<ReviewTargetsResponse>(
      `/api/conversations/${conversationId}/review-targets`,
    );
    return response.data;
  }

  /**
   * Run a 多模型复核 of one proposal message and inject the result.
   *
   * Backend rejects: 400 (no/duplicate/over-cap/unavailable model, or the
   * message carries no proposal), 403 (access / collab / read_only), 409 (a
   * task is running in this conversation), 429 (daily cap), 402 (org budget
   * exhausted). The injected `role="system"` message also arrives over the
   * WebSocket as NEW_MESSAGE.
   *
   * Per-call timeout override (P-COND-57C9ACC5F89A): the server budgets
   * `REVIEW_TIMEOUT_S=180` per model, so the instance-level `API_TIMEOUT`
   * (30s) aborted EVERY review client-side even though the backend completed,
   * billed, and injected the result — the user only saw a bare "复核失败".
   * 200s leaves headroom above the server budget without touching the instance
   * default; same per-call pattern the upload path already uses.
   */
  async runMultiModelReview(
    conversationId: string,
    messageId: string,
    models: { provider: string; model: string; execution?: 'aux' | 'agent' }[],
  ): Promise<MultiModelReviewResponse> {
    const response = await this.client.post<MultiModelReviewResponse>(
      `/api/conversations/${conversationId}/multi-model-review`,
      { message_id: messageId, models },
      { timeout: 200000 },
    );
    return response.data;
  }

  /**
   * Cancel the still-running AGENT targets of a background review (ST10).
   * Aux targets are not cancellable. Idempotent: `cancelled` = how many active
   * children were found (0 once the review has finished).
   */
  /**
   * Stop a running agent review. `confirmed` / `unconfirmed` (optional — older
   * backends omit them) split the stopped children (P-COND-158AE9229655).
   * Relies on the client-wide API_TIMEOUT (30 s) exceeding the backend's
   * worst case (~18 s waiting for every child to confirm); raise it with a
   * per-request timeout if that backend wait ever grows.
   */
  async cancelMultiModelReview(
    conversationId: string,
    reviewId: string,
  ): Promise<{ review_id: string; cancelled: number; confirmed?: number; unconfirmed?: number }> {
    const response = await this.client.post<{
      review_id: string; cancelled: number; confirmed?: number; unconfirmed?: number;
    }>(
      `/api/conversations/${conversationId}/multi-model-review/${encodeURIComponent(reviewId)}/cancel`,
    );
    return response.data;
  }

  /**
   * The reviews the server lists as open for this conversation, plus the
   * approval hold count (ReviewResultCard.tsx::useOpenReviews is the only
   * caller). Enums / ids / timestamps only.
   */
  async getActiveReviews(conversationId: string): Promise<ActiveReviewsResponse> {
    const response = await this.client.get<ActiveReviewsResponse>(
      `/api/conversations/${conversationId}/multi-model-review/active`,
    );
    return response.data;
  }

  /**
   * Reset a stuck implementing proposal back to approved (admin only).
   */
  async resetDispatchProposal(id: string): Promise<{ message: string; proposal_id: string; status: string }> {
    const response = await this.client.post<{ message: string; proposal_id: string; status: string }>(
      `/api/dispatch-proposals/${id}/reset`,
    );
    return response.data;
  }

  /**
   * Rollback a completed dispatch proposal (admin only, requires rollback_info).
   */
  async rollbackDispatchProposal(id: string): Promise<{ message: string; proposal_id: string }> {
    const response = await this.client.post<{ message: string; proposal_id: string }>(
      `/api/dispatch-proposals/${id}/rollback`,
    );
    return response.data;
  }

  // ==================== Collab Approvals (Workspace Modes) ====================

  /**
   * List collab-mode approvals scoped to an org, optionally filtered by
   * status / kind. Mirrors `getDispatchProposals` style. The backend
   * authorizes platform admin ∨ the org's admin/owner; the frontend
   * additionally gates the page on `useOrgRole().canManage`.
   * GET /api/v1/collab-approvals?org_id=&status=&kind=
   */
  async getCollabApprovals(params?: {
    org_id?: string;
    status?: string;
    kind?: string;
    mine?: boolean;
  }): Promise<{ items: CollabApprovalResponse[]; counts?: CollabApprovalCounts }> {
    const response = await this.client.get<{
      items: CollabApprovalResponse[];
      counts?: CollabApprovalCounts;
    }>('/api/v1/collab-approvals', { params });
    return response.data;
  }

  /**
   * Get full detail for a single collab approval (includes `draft_content`
   * read from the draft `source_path`).
   * GET /api/v1/collab-approvals/{id}
   */
  async getCollabApproval(id: string): Promise<CollabApprovalDetail> {
    const response = await this.client.get<CollabApprovalDetail>(
      `/api/v1/collab-approvals/${id}`,
    );
    return response.data;
  }

  /**
   * Create a pending collab approval (submission entry).
   * POST /api/v1/collab-approvals
   */
  async createCollabApproval(body: {
    project_id: string;
    kind?: string;
    dept_slug?: string;
    title: string;
    summary?: string;
    source_path: string;
    target_path: string;
    conversation_id?: string;
  }): Promise<CollabApprovalResponse> {
    const response = await this.client.post<CollabApprovalResponse>(
      '/api/v1/collab-approvals',
      body,
    );
    return response.data;
  }

  /**
   * Approve or reject a collab approval. `reject` requires a note.
   * Approve promotes the draft into `standards/` (backend owner-identity
   * write). PATCH /api/v1/collab-approvals/{id}
   */
  async decideCollabApproval(
    id: string,
    body: { action: 'approve' | 'reject'; note?: string },
  ): Promise<CollabApprovalDetail> {
    const response = await this.client.patch<CollabApprovalDetail>(
      `/api/v1/collab-approvals/${id}`,
      body,
    );
    return response.data;
  }

  // ==================== Health Monitor (Admin) ====================

  /**
   * Retrieves the full health dashboard (admin only).
   * Backend returns: HealthDashboard
   */
  async getHealthDashboard(): Promise<HealthDashboard> {
    const response = await this.client.get<HealthDashboard>('/api/admin/health');
    return response.data;
  }

  /**
   * Retrieves server metrics only (admin only).
   */
  async getHealthServers(): Promise<{ servers: ServerMetrics[]; collected_at: string; cache_age_seconds: number }> {
    const response = await this.client.get('/api/admin/health/servers');
    return response.data;
  }

  /**
   * Retrieves Docker container info only (admin only).
   */
  async getHealthDocker(): Promise<{ docker: DockerContainer[]; collected_at: string; cache_age_seconds: number }> {
    const response = await this.client.get('/api/admin/health/docker');
    return response.data;
  }

  /**
   * Retrieves project health details only (admin only).
   */
  async getHealthProjects(): Promise<{ projects: ProjectHealthDetail[]; collected_at: string; cache_age_seconds: number }> {
    const response = await this.client.get('/api/admin/health/projects');
    return response.data;
  }

  /**
   * Retrieves channel health details only (admin only).
   */
  async getHealthChannels(): Promise<{ channels: ChannelHealth[]; collected_at: string; cache_age_seconds: number }> {
    const response = await this.client.get('/api/admin/health/channels');
    return response.data;
  }

  // ==================== Token Management (Admin) ====================

  /**
   * Retrieves token status for all configured hosts (admin only).
   * Backend returns: TokenStatusResponse
   */
  async getTokenStatus(): Promise<TokenStatusResponse> {
    const response = await this.client.get<TokenStatusResponse>('/api/admin/token-status');
    return response.data;
  }

  /**
   * Triggers token refresh across all hosts (admin only).
   * Backend returns: TokenRefreshResponse
   */
  async refreshAllTokens(account?: string): Promise<TokenRefreshResponse> {
    const body = account ? { account } : undefined;
    const response = await this.client.post<TokenRefreshResponse>('/api/admin/token-refresh', body);
    return response.data;
  }

  // ============ Account Pair Credential Rotation (Admin) ============

  /**
   * Replace an account pair's OAuth credential from an uploaded file.
   *
   * Runs the whole rotation pipeline server-side: static validation -> backup
   * -> local install (0600) -> fleet fan-out -> Redis reset -> re-verify.
   * Deliberately NOT the attachment upload route: no conversation message and
   * no public download link is created for a credential file.
   *
   * Long timeout: the fleet fan-out SSHes to every active host in series.
   */
  async uploadAccountPairCredentials(
    pairLabel: string,
    file: File,
  ): Promise<CredentialRotationResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<CredentialRotationResponse>(
      `/api/admin/account-pairs/${encodeURIComponent(pairLabel)}/credentials`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    );
    return response.data;
  }

  /**
   * Re-distribute the pair's CURRENT credential and clear its Redis markers.
   *
   * No new file, and no token refresh -- use it when a host was offline during
   * the original push. For an actual token refresh use refreshAllTokens().
   */
  async rotateAccountPairCredentials(
    pairLabel: string,
  ): Promise<CredentialRotationResponse> {
    const response = await this.client.post<CredentialRotationResponse>(
      `/api/admin/account-pairs/${encodeURIComponent(pairLabel)}/rotate`,
      undefined,
      { timeout: 180000 }
    );
    return response.data;
  }

  // ==================== Pair Weight Config (Admin) ====================

  /**
   * Get current account pair weight configuration (admin only).
   */
  async getPairConfig(): Promise<PairConfigResponse> {
    const response = await this.client.get<PairConfigResponse>('/api/admin/pair-config');
    return response.data;
  }

  /**
   * Update account pair weight configuration (admin only).
   */
  async updatePairConfig(weights: Partial<Record<PairLetter, number>>): Promise<PairConfigResponse> {
    // PUT only the letters the caller provided; omitted letters keep their
    // current backend value. The caller passes non-retired letters only.
    const body: Partial<Record<`weight_${PairLetter}`, number>> = {};
    for (const letter of PAIR_LETTERS) {
      const w = weights[letter];
      if (w !== undefined) body[`weight_${letter}`] = w;
    }
    const response = await this.client.put<PairConfigResponse>('/api/admin/pair-config', body);
    return response.data;
  }

  /**
   * Read-only impact preview for retiring an account pair (platform admin).
   * 409 `retired_state_uninitialized` when this backend process has not loaded
   * the retired set yet.
   */
  async previewPairRetire(pairLabel: string): Promise<PairRetirePreviewResponse> {
    const response = await this.client.get<PairRetirePreviewResponse>(
      '/api/admin/pair-retire/preview',
      { params: { pair_label: pairLabel } },
    );
    return response.data;
  }

  /**
   * Permanently retire an account pair (no restore in UI or API) and archive
   * its local credential files. 409 `fleet_would_be_empty` when no usable
   * account would remain. A repeat call only completes a pending archive.
   */
  async retirePair(pairLabel: string): Promise<PairRetireResponse> {
    const response = await this.client.post<PairRetireResponse>(
      '/api/admin/pair-retire',
      { pair_label: pairLabel, confirm_migration: true },
    );
    return response.data;
  }

  /**
   * Turn the temporary "conductor uses pair F" policy on/off (admin only).
   * Sends only the switch; every weight keeps its current backend value.
   */
  async setLocalPrefersEgress(enabled: boolean): Promise<PairConfigResponse> {
    const response = await this.client.put<PairConfigResponse>('/api/admin/pair-config', { local_prefers_egress: enabled });
    return response.data;
  }

  /**
   * Get transient rate-limit cooldown status for all account pairs (admin only).
   */
  async getPairCooldownStatus(): Promise<PairCooldownResponse> {
    const response = await this.client.get<PairCooldownResponse>('/api/admin/pair-cooldown');
    return response.data;
  }

  /**
   * Force-clear a specific account pair's rate-limit cooldown (admin only).
   */
  async resetPairCooldown(pairLabel: string): Promise<{ pair_label: string; cleared: boolean }> {
    const response = await this.client.post<{ pair_label: string; cleared: boolean }>(
      '/api/admin/pair-cooldown/reset', { pair_label: pairLabel },
    );
    return response.data;
  }

  // ==================== System Operations (Admin) ====================

  /**
   * Schedule a backend restart (admin only).
   */
  async restartBackend(): Promise<{ status: string; delay_seconds: number; message: string }> {
    const response = await this.client.post('/api/admin/restart-backend');
    return response.data;
  }

  /**
   * Set the soft-restart pending flag (admin only). Blocks new dispatches
   * with a friendly message and triggers an automatic restart once all
   * active agents complete.
   */
  async softRestart(): Promise<{ status: string; active_agents: number; requested_at: string }> {
    const response = await this.client.post('/api/admin/soft-restart');
    return response.data;
  }

  /**
   * Cancel a pending soft restart and resume normal dispatch (admin only).
   */
  async cancelSoftRestart(): Promise<{ status: string }> {
    const response = await this.client.post('/api/admin/cancel-soft-restart');
    return response.data;
  }

  /**
   * Get the current soft-restart state and active-agent count (admin only).
   */
  async getSoftRestartStatus(): Promise<{
    pending: boolean;
    active_agents: number;
    requested_at?: string;
    requested_by?: string;
  }> {
    const response = await this.client.get('/api/admin/soft-restart-status');
    return response.data;
  }

  /**
   * Trigger frontend rebuild (admin only).
   */
  async rebuildFrontend(): Promise<{ status: string; message: string }> {
    const response = await this.client.post('/api/admin/rebuild-frontend');
    return response.data;
  }

  /**
   * Rebuild + reship the China-entry (myconductor.cn) frontend (admin only).
   */
  async rebuildFrontendChina(): Promise<{ status: string; message: string }> {
    const response = await this.client.post('/api/admin/rebuild-frontend-china');
    return response.data;
  }

  /**
   * Trigger full deploy (admin only).
   */
  async triggerDeploy(): Promise<{ status: string; message: string }> {
    const response = await this.client.post('/api/admin/deploy');
    return response.data;
  }

  /**
   * Get available module versions (git tags) for rollback.
   */
  async getModuleVersions(): Promise<{ versions: Array<{ tag: string; date: string; message: string }>; error?: string }> {
    const response = await this.client.get('/api/admin/modules/versions');
    return response.data;
  }

  /**
   * Trigger module rollback to a specific git version (admin only).
   */
  async rollbackModules(targetRef: string, files?: string[]): Promise<{ status: string; message: string }> {
    const response = await this.client.post('/api/admin/modules/rollback', {
      target_ref: targetRef,
      files: files || null,
    });
    return response.data;
  }

  /**
   * Create a manual version snapshot (admin only).
   */
  async createSnapshot(message?: string): Promise<{ tag: string; message: string }> {
    const response = await this.client.post('/api/admin/modules/snapshot', {
      message: message || '',
    });
    return response.data;
  }

  /**
   * Delete old git tags, keeping the newest 50 (admin only).
   */
  async cleanupTags(): Promise<{ deleted: number; remaining: number; protected?: number }> {
    const response = await this.client.post('/api/admin/modules/tag-cleanup');
    return response.data;
  }

  /**
   * Get status of an async operation (admin only).
   */
  async getOpStatus(opName: string): Promise<{
    op_name: string;
    status: string;
    started_at: string | null;
    finished_at: string | null;
    output_tail: string | null;
  }> {
    const response = await this.client.get(`/api/admin/op-status/${opName}`);
    return response.data;
  }

  // ==================== Organizations (Phase 1 T6-B) ====================
  //
  // Org switcher + Org Settings consume these. All endpoints are exposed
  // under /api/v1/orgs/* on conductor_p1_dev; on prod (where the orgs
  // router may not be mounted) calls will 404 and callers must catch
  // gracefully (see OrgSwitcher / OrgSettings components).
  //
  // Spec: backend/app/api/orgs.py (T5-A).
  // Auth: listMyOrgs / getOrg open to any member; create platform-admin only;
  // patch / member add+remove require org-admin OR platform-admin (403 otherwise).

  /** List organizations the current user belongs to. Backend: GET /api/v1/orgs. */
  async listMyOrgs(): Promise<OrgResponse[]> {
    const response = await this.client.get<OrgResponse[]>('/api/v1/orgs');
    return response.data;
  }

  /** Fetch a single org by id. Backend: GET /api/v1/orgs/{org_id}. */
  async getOrg(orgId: string): Promise<OrgResponse> {
    const response = await this.client.get<OrgResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}`
    );
    return response.data;
  }

  /** Create a new organization. Platform admin only. Backend: POST /api/v1/orgs. */
  async createOrg(payload: {
    slug: string;
    display_name: string;
    settings?: Record<string, unknown>;
  }): Promise<OrgResponse> {
    const response = await this.client.post<OrgResponse>('/api/v1/orgs', payload);
    return response.data;
  }

  /** Update an org's display_name and/or settings. Backend: PATCH /api/v1/orgs/{org_id}. */
  async patchOrg(
    orgId: string,
    payload: { display_name?: string; settings?: Record<string, unknown> }
  ): Promise<OrgResponse> {
    const response = await this.client.patch<OrgResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}`,
      payload
    );
    return response.data;
  }

  /**
   * List members of an org. Backend: GET /api/v1/orgs/{org_id}/members
   * (wired by 2026-05-28 T6c follow-up — see CLAUDE.md "Permission Model"
   * table). Caller must be an active member of the org OR a platform admin;
   * non-member callers get 404 (G10 existence-leak parity, NOT 403).
   * Deactivated members (deactivated_at IS NOT NULL) are filtered out.
   */
  async listOrgMembers(orgId: string): Promise<OrgMemberResponse[]> {
    const response = await this.client.get<OrgMemberResponse[]>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/members`
    );
    return response.data;
  }

  /** Add a member to an org. Backend: POST /api/v1/orgs/{org_id}/members. */
  async addOrgMember(
    orgId: string,
    payload: { user_id: string; role: string }
  ): Promise<OrgMemberResponse> {
    const response = await this.client.post<OrgMemberResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/members`,
      payload
    );
    return response.data;
  }

  /**
   * List the department registry of an org (P-COND-B115A1EAFAED
   * Sub-Task 4). Backend: GET /api/v1/orgs/{org_id}/departments.
   * Returns the deduped + sorted union of org.settings["departments"]
   * and distinct active org_members.department.
   */
  async listOrgDepartments(orgId: string): Promise<string[]> {
    const response = await this.client.get<{ departments: string[] }>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments`
    );
    return response.data.departments;
  }

  /**
   * Register a NEW department name (P-COND-B115A1EAFAED Sub-Task 4).
   * Backend: POST /api/v1/orgs/{org_id}/departments.
   * 409 → department already exists; 422 → invalid name
   * (empty / > 100 chars). Returns the updated sorted union.
   */
  async addOrgDepartment(
    orgId: string,
    payload: { name: string }
  ): Promise<string[]> {
    const response = await this.client.post<{ departments: string[] }>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments`,
      payload
    );
    return response.data.departments;
  }

  /**
   * Same GET as `listOrgDepartments`, but returns the FULL payload including
   * the additive `department_states` pause flags (P-COND-05EF0D660DAA).
   *
   * `listOrgDepartments` above is deliberately left returning `string[]`:
   * its four existing consumers (EditMemberMetadataModal, AddMemberModal,
   * CreateProjectModal, OrgMembers) must not be perturbed. Pause-aware UI
   * calls THIS method instead.
   *
   * Defensive normalization: if a not-yet-upgraded backend omits (or
   * short-changes) `department_states`, we synthesize one unpaused entry per
   * name so the list still renders every department rather than collapsing
   * to empty. This only handles field ABSENCE — the authoritative
   * paused/not-paused decision is made server-side and is never re-derived
   * on the client.
   */
  async listOrgDepartmentStates(orgId: string): Promise<OrgDepartmentsResult> {
    const response = await this.client.get<OrgDepartmentsResult>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments`
    );
    return normalizeOrgDepartmentsResult(response.data);
  }

  /**
   * Suspend a department's project visibility (P-COND-05EF0D660DAA).
   * Backend: POST /api/v1/orgs/{org_id}/departments/{name}/pause.
   *
   * Takes NO request body. 200 (not 201) on success, and idempotent —
   * pausing an already-paused department returns 200 with an unchanged
   * list. 404 → org unknown, department not in the registry, OR the caller
   * is not an org admin / not a member (G10 existence-leak parity: never
   * 403). 422 → blank name.
   *
   * Reversible and zero-touch: no file is deleted and no `access.yaml` is
   * rewritten. Returns the refreshed registry — adopt it directly instead
   * of refetching.
   */
  async pauseOrgDepartment(
    orgId: string,
    name: string
  ): Promise<OrgDepartmentsResult> {
    const response = await this.client.post<OrgDepartmentsResult>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(
        name
      )}/pause`
    );
    return normalizeOrgDepartmentsResult(response.data);
  }

  /**
   * Lift a department suspension — the inverse of `pauseOrgDepartment`
   * (P-COND-05EF0D660DAA). Backend:
   * POST /api/v1/orgs/{org_id}/departments/{name}/resume.
   *
   * Same contract as pause: no body, 200, idempotent, 404 for unknown
   * org/department/non-admin. Because pausing never wrote anything to disk,
   * resuming restores the ORIGINAL authorization path with zero data loss
   * and no replay step. Returns the refreshed registry.
   */
  async resumeOrgDepartment(
    orgId: string,
    name: string
  ): Promise<OrgDepartmentsResult> {
    const response = await this.client.post<OrgDepartmentsResult>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(
        name
      )}/resume`
    );
    return normalizeOrgDepartmentsResult(response.data);
  }

  /**
   * Preview the blast radius of removing a department
   * (P-COND-3F91C4A8D2E5 ST1). Backend:
   * GET /api/v1/orgs/{org_id}/departments/{name}/impact.
   *
   * Read-only. Same authz as the DELETE it previews (org admin OR platform
   * admin; cross-org / non-admin → 404, not 403).
   */
  async getOrgDepartmentImpact(
    orgId: string,
    name: string
  ): Promise<OrgDepartmentImpact> {
    const response = await this.client.get<OrgDepartmentImpact>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(
        name
      )}/impact`
    );
    return response.data;
  }

  /**
   * Remove a department from an org (P-COND-3F91C4A8D2E5 ST1). Backend:
   * DELETE /api/v1/orgs/{org_id}/departments/{name}.
   *
   * Members are RETAINED — only their `department` assignment is cleared
   * (irreversible from the UI; an audit snapshot is written server-side).
   * Directories and files are never deleted; collab projects get a
   * tombstone entry instead. Idempotent: 200 even if already removed.
   * Returns the fresh sorted union — adopt it instead of refetching.
   */
  async removeOrgDepartment(
    orgId: string,
    name: string
  ): Promise<OrgDepartmentRemoveResult> {
    const response = await this.client.delete<OrgDepartmentRemoveResult>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(
        name
      )}`
    );
    return response.data;
  }

  /**
   * Create an org invitation (Phase 3 v1.1 T3a — extension §17.3).
   * Backend: POST /api/v1/orgs/{org_id}/invitations.
   *
   * At least one of `email` or `wecom_userid` MUST be provided
   * (router enforces 422 with friendly message; we let the caller
   * surface the error). `org_role` is "admin" or "member" (DB CHECK
   * also enforces). `max_uses` defaults to 1 server-side; we accept
   * an explicit value for the magic-link multi-use case. `expires_at`
   * defaults to NOW()+7d server-side.
   *
   * Returns the persisted InvitationResponse including the generated
   * `invite_code` (URL-safe ≤32 chars) and computed `remaining_uses`.
   *
   * Errors:
   *  - 403 → caller is not an org admin and not platform admin.
   *  - 404 → cross-org probe (P2.6 T2-B G10 — same body as non-admin).
   *  - 409 → org.max_members reached including pending invitations.
   *  - 422 → missing both email and wecom_userid, invalid org_role, etc.
   */
  async createOrgInvitation(
    orgId: string,
    payload: {
      email?: string | null;
      phone?: string | null;
      wecom_userid?: string | null;
      org_role: string;
      max_uses?: number;
      expires_at?: string | null;
      /**
       * P-COND-E03759DDFE20 Sub-Task 3 — optional department assigned at
       * invite time. When non-null the backend writes it to the invitee's
       * `OrgMember.department` on accept/redeem; null/omitted = no
       * department (backend `org_invitations.department` stays NULL).
       */
      department?: string | null;
      /**
       * 3-step AddMemberModal (P-COND-C7C2FA6D84CD): when false the backend
       * persists the invitation + returns the link but does NOT auto-send
       * the email/SMS — delivery is deferred to resendInvitation (step-3
       * "确认发送"). Omitted/true = auto-send on create (legacy default).
       */
      send_on_create?: boolean;
      /** ST2 (P-COND-LRT, Req 6): gift N months of subscription on redeem. */
      grant_subscription_months?: number | null;
    }
  ): Promise<OrgInvitationResponse> {
    const response = await this.client.post<OrgInvitationResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/invitations`,
      payload
    );
    return response.data;
  }

  /**
   * Remove a member from an org.
   * Backend: DELETE /api/v1/orgs/{org_id}/members/{user_id}.
   * Returns 204 on success. 409 if the target is the last admin — caller
   * should surface a friendly inline error using err.response.data.detail.
   */
  async removeOrgMember(orgId: string, userId: string): Promise<void> {
    await this.client.delete(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`
    );
  }

  /**
   * Change an org member's role (admin <-> member).
   *
   * Backend: `PATCH /api/v1/orgs/{org_id}/members/{user_id}/role`
   * (Phase 2.5 T4-A; surfaced to UI by Phase 2.7 T4-B).
   *
   * Authz (enforced server-side):
   *  - Caller MUST be an org admin of `orgId` OR a platform admin.
   *  - Sole-admin guard: refuses to demote the only remaining active
   *    admin (409 Conflict — plan §5 case 30).
   *
   * Errors:
   *  - 403 → caller is an active member but not an admin.
   *  - 404 → cross-org probe or member not found.
   *  - 409 → would orphan the org (last admin demotion).
   */
  async patchOrgMemberRole(
    orgId: string,
    userId: string,
    role: 'admin' | 'member'
  ): Promise<OrgMemberResponse> {
    const response = await this.client.patch<OrgMemberResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/role`,
      { role }
    );
    return response.data;
  }

  /**
   * Update an org member's `department` / `title` / `manager_user_id`.
   *
   * Backend: `PATCH /api/v1/orgs/{org_id}/members/{user_id}/metadata`
   * (Phase 2.6 T4-A G13; surfaced to UI by Phase 2.7 T4-B).
   *
   * Authz (enforced server-side):
   *  - Caller MUST be an org admin of `orgId` OR a platform admin.
   *  - Active non-admin member of `orgId` → 403 (documented exception).
   *
   * Partial-PATCH: only keys present in `patch` are modified. Explicit
   * `null` clears the field. See `OrgMemberMetadataPatch` doc.
   *
   * Errors:
   *  - 403 → caller is an active member but not an admin.
   *  - 404 → cross-org probe, member not found, or target is
   *    soft-deleted (`deactivated_at IS NOT NULL`).
   *  - 422 → `manager_user_id` references a user who is not an
   *    active member of THIS org.
   */
  async patchOrgMemberMetadata(
    orgId: string,
    userId: string,
    patch: OrgMemberMetadataPatch
  ): Promise<OrgMemberResponse> {
    const response = await this.client.patch<OrgMemberResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/metadata`,
      patch
    );
    return response.data;
  }

  /**
   * Soft-delete (deactivate) an organization.
   *
   * Backend: `DELETE /api/v1/orgs/{org_id}` (Phase 2.5 T4-A; surfaced
   * to UI by Phase 2.7 T4-A). Cascades soft-delete to all `org_members`
   * and `projects` of the org in one transaction. Soft-delete preserves
   * history (S4 invariant — rows are NOT physically removed).
   *
   * Authz (enforced server-side):
   *  - Platform admin: always allowed.
   *  - Org owner (`organizations.owner_user_id == current_user.id`):
   *    allowed.
   *  - Org admin (without owner role): denied → 403.
   *
   * Errors:
   *  - 403 → caller is neither owner nor platform admin.
   *  - 404 → org not found OR cross-org probe (P2.6 T2-B G10 hides
   *    existence behind 404).
   *  - 409 → org already deactivated.
   *
   * Response body: `OrgResponse` of the deactivated org (with
   * `deactivated_at` set on the server-side row, though that field
   * is not currently in the response schema). Plan Pack proposes
   * row counts in the body — see `DeactivateOrgResponse` doc.
   */
  async deactivateOrg(orgId: string): Promise<DeactivateOrgResponse> {
    const response = await this.client.delete<DeactivateOrgResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}`
    );
    return response.data;
  }

  /**
   * Initiate an org-ownership transfer.
   *
   * Backend: `POST /api/v1/orgs/{org_id}/transfer` (Phase 2.5 T4-A;
   * surfaced to UI by Phase 3 T6c-A `TransferOrgModal`).
   *
   * Authz (enforced server-side):
   *  - Caller MUST be an org admin of `orgId` OR a platform admin.
   *  - Caller MUST ADDITIONALLY be the current org owner OR a
   *    platform admin (ownership-specific gate in service layer).
   *
   * Side effects:
   *  - Generates a UUID4 token, stores under
   *    `p2_5:org_transfer_token:{token}` in Redis with 12h TTL.
   *  - Emits an `org_transfer_initiated` audit row.
   *
   * Returns the token + expiry; the new owner completes the transfer
   * by calling `POST /api/v1/orgs/{org_id}/transfer/confirm` with the
   * token (the confirm step is NOT this modal's responsibility — it
   * is done by the target user, typically via a WeCom IM link).
   *
   * Errors:
   *  - 403 → caller is admin but not the current owner (and not a
   *          platform admin).
   *  - 404 → org not found OR cross-org probe (P2.6 T2-B G10).
   *  - 422 → `new_owner_user_id` is not an active admin of this org.
   */
  async initiateOrgTransfer(
    orgId: string,
    payload: OrgTransferInitiateRequest
  ): Promise<OrgTransferInitiateResponse> {
    const response = await this.client.post<OrgTransferInitiateResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/transfer`,
      payload
    );
    return response.data;
  }

  /**
   * List users addable to `orgId` (not yet active members).
   *
   * Backend: `GET /api/v1/orgs/{org_id}/addable-users` (Phase 3 UI
   * Completion T6c-B lane (a) M1). Authz: org admin OR platform admin
   * (stricter than `/members` which is any-active-member). Returns
   * users where `users.deactivated_at IS NULL` AND the user is NOT an
   * active `org_member` of this org. Users with a **deactivated**
   * `org_member` row in this org DO appear (operator re-invitation
   * use case).
   *
   * Query params:
   *  - `q`      optional substring (min length 2 server-side, ILIKE
   *             on `username` OR `email`).
   *  - `limit`  default 20, server-side hard cap 50.
   *  - `offset` default 0.
   *
   * Errors:
   *  - 403 → caller is an active member but not an admin.
   *  - 404 → cross-org probe (P2.6 T2-B G10 — same body as non-admin).
   *  - 422 → `q` shorter than 2 chars.
   */
  async listAddableUsers(
    orgId: string,
    params: { q?: string; limit?: number; offset?: number } = {}
  ): Promise<AddableUser[]> {
    const search = new URLSearchParams();
    if (params.q && params.q.length > 0) search.set('q', params.q);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    const qs = search.toString();
    const response = await this.client.get<AddableUser[]>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/addable-users${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  /**
   * Update an org's quota (max_projects / max_members /
   * daily_token_budget_usd / weekly_token_budget_usd /
   * monthly_token_budget_usd).
   *
   * Backend: `PATCH /api/v1/admin/orgs/{org_id}` (platform-admin only;
   * surfaced to UI by Phase 3 UI Completion T6c-B lane (g) M3
   * `EditOrgQuotaModal`). Partial PATCH — absent keys leave server
   * values unchanged; explicit `null` clears the cap (= unlimited).
   *
   * Phase B (P-COND-D38665CEC4B4): adds the daily/weekly token-budget tiers
   * (USD) alongside the existing monthly tier. All three accept `null` to
   * clear (= unlimited).
   *
   * Authz (enforced server-side):
   *  - Platform admin only. Org admins see a disabled button with
   *    "联系平台管理员" tooltip in `OrgSettings.tsx`.
   *
   * Errors:
   *  - 403 → caller is NOT a platform admin.
   *  - 404 → org not found.
   *  - 422 → negative values, or a *_token_budget_usd is non-finite.
   */
  async patchOrgQuota(
    orgId: string,
    patch: Partial<{
      max_projects: number | null;
      max_members: number | null;
      daily_token_budget_usd: number | null;
      weekly_token_budget_usd: number | null;
      monthly_token_budget_usd: number | null;
    }>
  ): Promise<OrgResponse> {
    const response = await this.client.patch<OrgResponse>(
      `/api/v1/admin/orgs/${encodeURIComponent(orgId)}`,
      patch
    );
    return response.data;
  }

  /**
   * Revoke (delete) a pending org invitation.
   *
   * Backend: `DELETE /api/v1/orgs/{org_id}/invitations/{invitation_id}`
   * (Phase 3 v1.1 T3a — extension §17.3 E10; mirrored by
   * `services/invitations.ts::revokeInvitation` which takes
   * `(orgId, invitationId)`). Returns 204 on success. The audit row
   * preserves history per extension §17.3.
   *
   * Authz (enforced server-side):
   *  - Caller MUST be an org admin of `orgId` OR a platform admin.
   *
   * Errors:
   *  - 403 → non-admin caller.
   *  - 404 → invitation not found / already revoked.
   */
  async revokeInvitation(orgId: string, invitationId: string): Promise<void> {
    await this.client.delete(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}`
    );
  }

  /**
   * Resend a pending org invitation (re-trigger the notification
   * channel — email or WeCom — without rotating the invite code).
   *
   * Backend route (NOT YET WIRED — Phase 3 UI Completion T6c-B Contract
   * Delta): `POST /api/v1/orgs/{org_id}/invitations/{invitation_id}/resend`.
   *
   * The plan §6 M2 File 6 line 418 surfaces this function so the
   * OrgMembers "重发邀请" button has something to call. The backend
   * route will land in a later tier (M3 lane (g) cleanup or a P3 v1.2
   * follow-up). Until then this call surfaces a friendly toast via
   * the 404/501 response so the UI degrades gracefully. Callers
   * SHOULD branch on `err.response.status === 404 || 501` and show
   * "重发功能暂未上线" rather than a generic error.
   */
  async resendInvitation(orgId: string, invitationId: string): Promise<void> {
    await this.client.post(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}/resend`
    );
  }

  // ============ Self-serve subscription (P-COND-LRT ST4) ============

  /**
   * Open a self-serve subscribe intent (org admin or platform admin).
   *
   * Backend: `POST /api/v1/orgs/{org_id}/subscribe-intent`. Creates a PENDING
   * payment order + a STUB checkout session. Does NOT change the org's
   * subscription — that happens only on a SUCCESS sandbox callback. The
   * "payment" is a STUB/sandbox; no real charge occurs.
   *
   * Authz (server-side, A2): caller MUST be an org admin of `orgId` OR a
   * platform admin (a non-admin member is denied 403/404).
   */
  async createSubscribeIntent(
    orgId: string,
    body: SubscribeIntentRequest
  ): Promise<SubscribeIntentResponse> {
    const resp = await this.client.post<SubscribeIntentResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/subscribe-intent`,
      body
    );
    return resp.data;
  }

  /**
   * Resolve a pending order via the STUB sandbox callback.
   *
   * Backend: `POST /api/v1/orgs/{org_id}/subscribe-intent/{order_id}
   * /sandbox-callback`. `status: 'success'` runs the shared subscription math
   * (extends the org) and flips the order to `paid`; `status: 'failed'` flips
   * the order to `failed` and leaves the org UNCHANGED. Idempotent: a duplicate
   * success on an already-paid order is a no-op (`applied=false`).
   *
   * A real gateway delivers this as a signed webhook; in the STUB flow the UI
   * drives it to simulate completing the (sandbox) checkout.
   */
  async sandboxSubscribeCallback(
    orgId: string,
    orderId: string,
    body: SandboxCallbackRequest
  ): Promise<SandboxCallbackResponse> {
    const resp = await this.client.post<SandboxCallbackResponse>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/subscribe-intent/` +
        `${encodeURIComponent(orderId)}/sandbox-callback`,
      body
    );
    return resp.data;
  }

  // ==================== Platform Skill Management ====================

  async getPlatformSkillCatalog(projectId: string, mode?: string): Promise<PlatformSkillCatalogResponse> {
    const qs = mode ? `?mode=${encodeURIComponent(mode)}` : '';
    const resp = await this.client.get<PlatformSkillCatalogResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/platform-skills/catalog${qs}`
    );
    return resp.data;
  }

  async activatePlatformSkill(projectId: string, slug: string): Promise<{ ok: boolean; slug: string; status: string }> {
    const resp = await this.client.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/platform-skills/${encodeURIComponent(slug)}/activate`
    );
    return resp.data;
  }

  async deactivatePlatformSkill(projectId: string, slug: string): Promise<{ ok: boolean; slug: string; status: string }> {
    const resp = await this.client.delete(
      `/api/v1/projects/${encodeURIComponent(projectId)}/platform-skills/${encodeURIComponent(slug)}/activate`
    );
    return resp.data;
  }

  async createCustomSkill(
    projectId: string,
    payload: {
      slug: string;
      description: string;
      content: string;
      /** SKC-T7: omitted = `mandatory` (legacy callers); the skill center sends `default`. */
      assignment_mode?: 'mandatory' | 'default' | 'available';
    }
  ): Promise<{ ok: boolean; slug: string; status: string }> {
    const resp = await this.client.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/platform-skills/custom`,
      payload
    );
    return resp.data;
  }

  async deleteCustomSkill(projectId: string, slug: string): Promise<{ ok: boolean; slug: string }> {
    const resp = await this.client.delete(
      `/api/v1/projects/${encodeURIComponent(projectId)}/platform-skills/custom/${encodeURIComponent(slug)}`
    );
    return resp.data;
  }

  async getMandatorySkills(projectId: string): Promise<MandatorySkillsResponse> {
    const resp = await this.client.get<MandatorySkillsResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/platform-skills/mandatory`
    );
    return resp.data;
  }

  async getUserSkillPrefs(): Promise<UserSkillPrefs> {
    const resp = await this.client.get<UserSkillPrefs>('/api/aux/users/me/skill-prefs');
    return resp.data;
  }

  async updateUserSkillPrefs(prefs: UserSkillPrefsUpdate): Promise<UserSkillPrefs> {
    const resp = await this.client.patch<UserSkillPrefs>('/api/aux/users/me/skill-prefs', prefs);
    return resp.data;
  }

  // ==================== Conductor Desktop Downloads ====================

  /**
   * List the desktop installers and their on-disk state.
   *
   * Always 200 for a logged-in caller; entries whose file has not been built
   * yet carry `available: false`. An empty release directory is the NORMAL
   * state right after deploy (the installers are built out-of-band on a
   * Windows / macOS box), so the page must render "not published yet" rather
   * than treat it as an error.
   */
  async getDesktopDownloadManifest(): Promise<DesktopDownloadsManifest> {
    const resp = await this.client.get<DesktopDownloadsManifest>(
      '/api/v1/desktop/downloads/manifest'
    );
    return resp.data;
  }

  /**
   * Download one installer and hand it to the browser's save dialog.
   *
   * Goes through the axios client (not a bare `<a href>`) because the endpoint
   * is behind `get_current_user`: a plain anchor navigation carries no
   * Authorization header and would 401. The tradeoff is that the file is
   * buffered in memory as a Blob before saving — acceptable for a
   * dozens-of-MB installer, and the only option that keeps bearer auth.
   *
   * `filename` MUST be the manifest's `filename` verbatim: the backend matches
   * it against a closed whitelist by strict string equality, so anything else
   * is a 404 that never touches the filesystem.
   */
  async downloadDesktopRelease(
    filename: string,
    opts: DesktopDownloadOptions = {}
  ): Promise<void> {
    const { onProgress, signal, saveAs } = opts;
    const response = await this.client.get(
      `/api/v1/desktop/downloads/${encodeURIComponent(filename)}`,
      {
        responseType: 'blob',
        timeout: DESKTOP_DOWNLOAD_TIMEOUT,
        signal,
        onDownloadProgress: onProgress
          ? (evt) => onProgress({ loaded: evt.loaded || 0, total: evt.total || 0 })
          : undefined,
      }
    );
    // jsdom (and any exotic embedded webview) may lack the download API —
    // never throw inside a click handler over a missing browser capability.
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      return;
    }
    const blobUrl = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = saveAs || filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 100);
  }

  // ==================== Internal Helpers ====================

  setAuth(token: string): void {
    this.authToken = token;
    setAuthItem('conductor_auth_token', token);
  }

  private clearAuth(): void {
    this.authToken = null;
    clearAuthStorage();
  }

  async getCodexAccounts(org_id?: string, scope: CodexPoolScope = 'organization'): Promise<CodexSnapshot> {
    return (await this.client.get('/api/admin/codex/accounts', { params: { org_id, scope } })).data;
  }
  async saveCodexAllocations(org_id: string | null, expected_version: number, weights: Record<string, number>, scope: CodexPoolScope = 'organization'): Promise<{ version: number }> {
    return (await this.client.put('/api/admin/codex/allocations', { scope, org_id, expected_version, weights })).data;
  }
  async setCodexAccountEnabled(id: string, org_id: string | null, expected_version: number, enabled: boolean, scope: CodexPoolScope = 'organization'): Promise<void> {
    await this.client.post(`/api/admin/codex/accounts/${id}/enabled`, { scope, org_id, expected_version, enabled });
  }
  async clearCodexCooldown(id: string, org_id: string | null, expected_version: number, scope: CodexPoolScope = 'organization'): Promise<void> {
    await this.client.post(`/api/admin/codex/accounts/${id}/clear-cooldown`, { scope, org_id, expected_version });
  }
  async maintainCodexRunner(id: string, org_id: string | null, action: 'check' | 'refresh-auth', scope: CodexPoolScope = 'organization'): Promise<CodexOperation> {
    const response = await this.client.post(`/api/admin/codex/runners/${id}/${action}`, null, { params: { org_id, scope } });
    return (await this.client.get(`/api/admin/codex/operations/${response.data.operation_id}`, { params: { org_id, scope } })).data;
  }
  async getCodexProjectOverview(org_id?: string, scope: CodexPoolScope = 'organization'): Promise<CodexProjectsOverview> {
    return (await this.client.get('/api/admin/codex/projects/execution-overview', { params: { org_id, scope } })).data;
  }
  async getCodexRunRecords(query: CodexRunRecordsQuery): Promise<CodexRunRecordsPage> {
    return (await this.client.get('/api/admin/codex/runs/records', { params: query })).data;
  }
  /** E-6: platform-admin in-process reconcile of one managed run (404 unknown, 409 refusal). */
  async reconcileCodexRun(runId: string): Promise<CodexRunReconcileResult> {
    return (await this.client.post(`/api/admin/codex/runs/${encodeURIComponent(runId)}/reconcile`,
      null, { timeout: CODEX_RECONCILE_TIMEOUT_MS })).data;
  }
  async getCodexProjectPolicy(project_id: string): Promise<CodexProjectPolicy> {
    return (await this.client.get(`/api/admin/codex/projects/${project_id}/execution-policy`)).data;
  }
  async putCodexProjectPolicy(project_id: string, body: CodexProjectPolicyUpdate): Promise<CodexProjectPolicyWriteResult> {
    return (await this.client.put(`/api/admin/codex/projects/${project_id}/execution-policy`, body)).data;
  }

  // Claude account allocation (platform admin only — require_platform_admin).
  // Routed through this.client so the Bearer interceptor, API_BASE_URL and
  // 401 handling apply (P-COND-505EF69EAD2E: bare fetch dropped Authorization).
  async getClaudeAccountAllocations(): Promise<AllocationListResponse> {
    return (await this.client.get('/api/claude-accounts/allocations')).data;
  }
  async getClaudeAccountDefaults(): Promise<AllocationDefaults> {
    return (await this.client.get('/api/claude-accounts/defaults')).data;
  }
  async putClaudeAccountOrgAllocation(
    orgId: string, accounts: string[], expectedVersion?: string,
  ): Promise<AllocationWriteResponse> {
    const body: { accounts: string[]; expected_version?: string } = { accounts };
    if (expectedVersion) body.expected_version = expectedVersion;
    return (await this.client.put(`/api/claude-accounts/allocations/orgs/${orgId}`, body)).data;
  }
  async putClaudeAccountProjectAllocation(
    projectId: string,
    body: { mode: AllocationMode; accounts?: string[]; expected_version?: string },
  ): Promise<AllocationWriteResponse> {
    return (await this.client.put(`/api/claude-accounts/allocations/projects/${projectId}`, body)).data;
  }
  /** Read-only impact preview; returns per-org ``versions`` for the follow-up write. */
  async previewClaudeAccountAllocation(body: AllocationBatchRequest): Promise<AllocationPreviewResponse> {
    return (await this.client.post('/api/claude-accounts/allocations/preview', body)).data;
  }
  /** Batch write; stale ``expected_versions`` -> 409 ``allocation_conflict`` (re-preview). */
  async batchClaudeAccountAllocation(body: AllocationBatchRequest): Promise<AllocationWriteResponse> {
    return (await this.client.post('/api/claude-accounts/allocations/batch', { ...body, dry_run: false })).data;
  }
  async putClaudeAccountDefaults(body: AllocationDefaultsUpdate): Promise<AllocationWriteResponse> {
    return (await this.client.put('/api/claude-accounts/defaults', body)).data;
  }
  async getClaudeAccountAllocationStatus(): Promise<AllocationStatus> {
    return (await this.client.get('/api/claude-accounts/status')).data;
  }
  async retryClaudeAccountRevocation(changeId: string): Promise<RevocationRetryResponse> {
    return (await this.client.post('/api/claude-accounts/allocations/revocations/retry', { change_id: changeId })).data;
  }

  private loadSavedToken(): void {
    const savedToken = getAuthItem('conductor_auth_token');
    if (savedToken) {
      this.authToken = savedToken;
    }
  }
}

/** Singleton API client instance */
const api = new ApiClient();
export default api;

// ---------------------------------------------------------------------------
// Conversation branch / execution status / supervision — TYPES ONLY
// (P-COND-82309B809347, Contract Pack v1 §3.4–§3.6,
// planning/conversation-supervision/architecture.md). Client methods arrive
// with the tiers that add the routes. Timestamps are `+08:00` ISO strings
// (to_cst_iso); USD amounts are Decimal strings (Numeric(12,6), never floats).
// ---------------------------------------------------------------------------

/** `GET /api/conversations/{id}/branch-context` (§3.4). */
export interface BranchContext {
  seed_text: string;
  cutoff_at: string | null;
  entry_kind: BranchEntryKind;
  summary_range_end_at: string | null;
  summary_omitted_reason: BranchSummaryOmittedReason | null;
  source_conversation_id: string | null;
  consumed_at: string | null;
}

/** Supervision digest embedded in {@link ExecutionStatus} (§3.5). */
export interface SupervisionSummary {
  id: string;
  grant_level: SupervisionGrantLevel;
  status: SupervisionStatus;
  used_interventions: number;
  max_interventions: number;
  spent_usd: string;
  budget_usd: string;
}

/** `GET /api/conversations/{id}/execution-status` (§3.5). */
export interface ExecutionProgressSummary {
  current: { state: ExecutionState; source: 'execution_status'; task?: string | null; at: string | null };
  reported: { message_id: string; excerpt: string; at: string | null; accepted: false } | null;
  next: 'answer_question' | 'review_approval' | 'not_reported';
  model_invoked: false;
}

export interface ExecutionStatus {
  conversation_id: string;
  run_ref: string | null;
  logical_run_ref: string | null;
  supplement_run_id: string | null;
  arm: 'claude_cli' | 'codex_cli' | 'conductor_native';
  state: ExecutionState;
  started_at: string | null;
  /** TaskTracker heartbeat — liveness, NOT progress. */
  heartbeat_at: string | null;
  /** Latest trace entry — real progress. */
  last_progress_at: string | null;
  data_fresh_at: string;
  branch_state: string | null;
  git_status: 'clean' | 'pending' | 'committed' | null;
  supplement_capability: SupplementCapability;
  supplement_reason: string | null;
  pending_questions: number;
  recent_milestones: Array<{ at: string; text: string; step_ref?: string;
    status?: 'running' | 'done' | 'error'; completion_reason?: string | null;
    exit_code?: number | null; detail_availability?: string }>;
  /** More records exist beyond `recent_milestones` (absent on older servers → false). */
  milestones_has_more?: boolean;
  progress_summary?: ExecutionProgressSummary;

  supervision: SupervisionSummary | null;
}

/** One `supervision_actions` row (§3.2). `handoff` = §3.6 package. */
export interface SupervisionAction {
  id: string;
  supervision_id: string;
  action_kind: SupervisionActionKind;
  target_conversation_id: string;
  target_dispatch_id: string | null;
  target_logical_dispatch_id: string | null;
  idempotency_key: string;
  state: SupervisionActionState;
  cancel_outcome: string | null;
  reason: string;
  evidence: Record<string, unknown> | null;
  handoff: Record<string, unknown> | null;
  continuation_conversation_id: string | null;
  /** null = cost unknown (never 0). */
  cost_usd: string | null;
  created_at: string;
  updated_at: string;
}

/** One `conversation_supervisions` row + recent actions (`GET /api/v1/supervisions`). */
export interface Supervision {
  id: string;
  supervisor_conversation_id: string;
  target_conversation_id: string;
  root_task_conversation_id: string;
  owner_user_id: string;
  org_id: string;
  project_id: string;
  grant_level: SupervisionGrantLevel;
  allowed_actions: SupervisionAllowedAction[];
  auto_continue_without_gate: boolean;
  max_interventions: number;
  used_interventions: number;
  expires_at: string;
  budget_usd: string;
  spent_usd: string;
  wake_policy: SupervisionWakePolicy;
  status: SupervisionStatus;
  version: number;
  revoked_by: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  recent_actions?: SupervisionAction[];
  latest_check?: { round_id: string; state: 'running' | 'completed' | 'failed' | 'cancelled' | 'unknown';
    started_at: string; completed_at: string | null; conclusion_available: boolean } | null;
}

/** `POST /api/v1/supervisions` body (§3.4). Omitted fields use server defaults. */
export interface SupervisionCreateRequest {
  target_conversation_id: string;
  /** null/omitted = server creates the supervisor conversation B. */
  supervisor_conversation_id?: string | null;
  grant_level: SupervisionGrantLevel;
  allowed_actions?: SupervisionAllowedAction[];
  auto_continue_without_gate?: boolean;
  max_interventions?: number;
  expires_at?: string;
  /** USD Decimal string. */
  budget_usd?: string;
  wake_policy?: SupervisionWakePolicy;
}

// Claude Account Allocation types (ST4 / P-COND-64A6273CEB07; FX5 / P-COND-5858EE53FD09)
// Mirror backend/app/api/claude_account_allocations.py — components import these,
// never re-declare them (Contract Pack "Cross-Layer Contracts").
export type AllocationMode = 'inherit' | 'allow' | 'deny_all';
export type AllocationBatchOp = 'add' | 'remove' | 'set' | 'inherit' | 'deny_all';
export type AccountOwnerState = 'backend' | 'importing' | 'vault' | 'exporting' | 'frozen_unknown';

export interface OrgAllocation {
  org_id: string;
  /** Org slug (legacy field). Display ``display_name``. */
  name: string;
  display_name?: string;
  accounts: string[];
  projects: ProjectAllocationRow[];
}

export interface ProjectAllocationRow {
  project_id: string;
  name: string;
  mode: AllocationMode;
  accounts: string[];
  effective: string[];
}

export interface AllocationListResponse {
  orgs: OrgAllocation[];
  active_letters: string[];
  vault_held: string[];
}

export interface AllocationDefaults {
  new_project: {
    platform: string[];
    org_overrides: Record<string, string[]>;
  };
  new_org: string[];
}

export interface AllocationDefaultsUpdate {
  new_project?: { platform?: string[]; org_overrides?: Record<string, string[]> };
  new_org?: string[];
}

export interface AllocationBatchRequest {
  targets: { kind: 'org' | 'project'; id: string }[];
  op: AllocationBatchOp;
  accounts?: string[];
  /** org_id -> version from the preview response. */
  expected_versions?: Record<string, string>;
}

/** One ``affected`` entry: a target's effective before/after, or an org's removed letters. */
export interface AllocationPreviewTarget {
  org_id: string;
  project_id: string | null;
  before: string[];
  after: string[];
}
export interface AllocationPreviewRemoval {
  org_id: string;
  removed_accounts: string[];
}

export interface AllocationPreviewResponse {
  status: string;
  versions: Record<string, string>;
  affected: (AllocationPreviewTarget | AllocationPreviewRemoval)[];
}

/** Write response ``{change_id|null, revoked, partial, pending_job}`` (+ change_ids for multi-org). */
export interface AllocationWriteResponse {
  status?: string;
  change_id: string | null;
  change_ids?: string[];
  revoked: number;
  partial: boolean;
  pending_job: boolean;
}

export interface RevocationJob {
  change_id: string;
  org_id: string;
  project_id: string | null;
  removed_accounts: string[];
  attempts: number;
  last_error: string | null;
  /** +08:00 ISO (to_cst_iso). */
  created_at: string | null;
  expires_at: string | null;
}

export interface AllocationStatus {
  enforced: boolean;
  vault_held: string[];
  owner_states: Record<string, AccountOwnerState | string>;
  pending_jobs: RevocationJob[];
}

export type RevocationRetryResult = 'done' | 'still_pending' | 'expired' | 'already_done' | 'in_progress';

export interface RevocationRetryResponse {
  change_id: string;
  result: RevocationRetryResult;
  revoked: number;
  skipped_reallowed: number;
}
